import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { App as CapApp } from "@capacitor/app";
import { Share } from "@capacitor/share";
import { Filesystem, Directory } from "@capacitor/filesystem";
import {
  fbSignIn, fbRegister, onFbAuthChange,
  syncDoc, deleteDocFs,
  syncUser, deleteUserFs, loadUsers,
  syncStore, deleteStoreFs, loadStores,
  loadAllStoreData, bulkSync, subscribeToStore,
} from "./firebaseSync";

// ─── THEMES ───────────────────────────────────────────────────────────────────
const THEME = {
  appBg:"#f9f8f5", sidebarBg:"#fefefe", sidebarBorder:"#eae7de",
  topbarBg:"#fefefe", topbarBorder:"#eae7de", cardBg:"#fefefe", cardBorder:"#eae7de",
  inputBg:"#fefefe", inputBorder:"#eae7de", inputText:"#111111",
  itemCardBg:"#fefefe", itemCardBorder:"#eae7de", tableThead:"#f9f8f5", tableHover:"#f0eee8",
  text:"#111111", textSub:"#555555", textHeading:"#000000",
  navActive:"rgba(58,61,128,0.09)", navText:"#444444",
  scrollTrack:"#f9f8f5", scrollThumb:"#d8d4c8",
  ghostBg:"#fefefe", ghostBorder:"#eae7de", ghostText:"#444444",
  modalBg:"#fefefe", accent:"#3a3d80", innerCard:"#f9f8f5",
};
const THEMES = { dark: THEME, light: THEME };

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────
const T = {
  en: {
    appName:"Nethran Crackers", sub:"SHOP MANAGER",
    dashboard:"Dashboard", inventory:"Inventory", billing:"Billing / POS",
    reports:"Reports", categories:"Categories", expenses:"Expenses",
    todaySales:"Today's Sales", totalItems:"Total Items", lowStock:"Low Stock", todayProfit:"Today's Profit",
    recentTx:"Recent Transactions", stockAlerts:"Stock Alerts",
    addItem:"Add Item", editItem:"Edit", deleteItem:"Delete",
    searchPlaceholder:"Search items...",
    itemName:"Item Name", itemCode:"Item Code", brand:"Brand", category:"Category", subCategory:"Sub Category", unit:"Unit",
    salePrice:"Sale Price", purchasePrice:"Purchase Price",
    stock:"Stock", gstRate:"GST Rate", hsn:"HSN Code",
    save:"Save", cancel:"Cancel", addNew:"Add New",
    billing_title:"New Bill", customer:"Customer Name", phone:"Phone",
    gstInvoice:"GST Invoice", nonGst:"Non-GST Bill",
    subtotal:"Subtotal", gst:"GST", discount:"Discount (₹)",
    grandTotal:"Grand Total", printA4:"Print A4", printThermal:"Print 80mm",
    clearBill:"Clear Bill", saveBill:"Save & Print",
    qty:"Qty", price:"Price", amount:"Amount",
    inStock:"In Stock", outStock:"Out of Stock", lowStockBadge:"Low Stock",
    allCat:"All", allSubCat:"All Sub-Categories",
    monthlySales:"Monthly Sales", monthlyProfit:"Monthly Profit", gstCollected:"GST Collected",
    salesByCat:"Sales by Category",
    language:"தமிழ்", adminRole:"Admin",
    confirmDelete:"Delete this item?",
    confirmDelCat:"Delete this category? Items will become 'Uncategorized'.",
    confirmDelSubCat:"Delete this sub-category? Items will lose their sub-category.",
    stockUpdated:"Saved!", billSaved:"Bill saved!", catSaved:"Category saved!",
    catDeleted:"Category deleted!", subCatSaved:"Sub-category saved!", subCatDeleted:"Sub-category deleted!",
    expenseSaved:"Expense saved!", expenseDeleted:"Expense deleted!",
    invoiceNo:"Invoice No", date:"Date", time:"Time",
    shopAddress:"Srivilliputhur, Tamil Nadu - 626125",
    shopPhone:"Ph: +91 98765 43210", shopGST:"GSTIN: 33XXXXX1234X1ZX",
    billTo:"Bill To", cashier:"Cashier", thankYou:"Thank you for your purchase!",
    manageCat:"Manage Categories", addCat:"Add Category", catName:"Category Name",
    catColor:"Pick Color", catIcon:"Pick Icon", editCat:"Edit Category",
    manageSubCat:"Sub-Categories", addSubCat:"Add Sub-Category", subCatName:"Sub-Category Name",
    itemsInCat:"items", viewItems:"View Items",
    cash:"Cash", upi:"UPI", card:"Card", credit:"Credit",
    // Purchases
    purchases:"Purchases", addPurchase:"Add Purchase", purchaseTitle:"Item Name",
    supplier:"Supplier", purchaseBrand:"Brand", purchaseQty:"Quantity",
    purchaseUnit:"Unit", purchaseCost:"Cost per Unit", purchaseTotal:"Total Cost",
    purchaseDate:"Date", purchaseNote:"Note", purchaseCategory:"Category",
    allPurchases:"All Purchases", noPurchases:"No purchases found.",
    purchaseSaved:"Purchase saved!", purchaseDeleted:"Purchase deleted!",
    // Expenses
    addExpense:"Add Expense", expenseTitle:"Expense Title", expenseAmt:"Amount (₹)",
    expenseCat:"Expense Category", expenseNote:"Note (optional)", expenseDate:"Date",
    totalExpenses:"Total Expenses", dailyExp:"Today", weeklyExp:"This Week",
    monthlyExp:"This Month", quarterExp:"Quarter", annualExp:"This Year",
    expFilter:"Filter Period", allExpenses:"All Expenses",
    noExpenses:"No expenses found for this period.",
    expCats:["Rent","Salary","Electricity","Transport","Purchase","Maintenance","Marketing","Miscellaneous"],
  },
  ta: {
    appName:"நேத்ரன் பட்டாசு", sub:"கடை மேலாளர்",
    dashboard:"டாஷ்போர்டு", inventory:"சரக்கு பட்டியல்", billing:"பில்லிங்",
    reports:"அறிக்கைகள்", categories:"வகைகள்", expenses:"செலவுகள்",
    todaySales:"இன்றைய விற்பனை", totalItems:"மொத்த பொருட்கள்", lowStock:"குறைந்த சரக்கு", todayProfit:"இன்றைய லாபம்",
    recentTx:"சமீபத்திய பரிவர்த்தனைகள்", stockAlerts:"சரக்கு எச்சரிக்கை",
    addItem:"பொருள் சேர்", editItem:"திருத்து", deleteItem:"நீக்கு",
    searchPlaceholder:"பொருட்களை தேடு...",
    itemName:"பொருளின் பெயர்", itemCode:"பொருள் குறியீடு", brand:"பிராண்ட்", category:"வகை", subCategory:"உட்பிரிவு", unit:"அலகு",
    salePrice:"விற்பனை விலை", purchasePrice:"கொள்முதல் விலை",
    stock:"சரக்கு", gstRate:"GST விகிதம்", hsn:"HSN குறியீடு",
    save:"சேமி", cancel:"ரத்து செய்", addNew:"புதிது சேர்",
    billing_title:"புதிய பில்", customer:"வாடிக்கையாளர் பெயர்", phone:"தொலைபேசி",
    gstInvoice:"GST இன்வாய்ஸ்", nonGst:"சாதாரண பில்",
    subtotal:"கூட்டுத்தொகை", gst:"GST", discount:"தள்ளுபடி (₹)",
    grandTotal:"மொத்த தொகை", printA4:"A4 அச்சிடு", printThermal:"80mm அச்சிடு",
    clearBill:"பில் அழி", saveBill:"சேமி & அச்சிடு",
    qty:"அளவு", price:"விலை", amount:"தொகை",
    inStock:"சரக்கு உள்ளது", outStock:"சரக்கு இல்லை", lowStockBadge:"குறைந்த சரக்கு",
    allCat:"அனைத்தும்", allSubCat:"அனைத்து உட்பிரிவுகள்",
    monthlySales:"மாத விற்பனை", monthlyProfit:"மாத லாபம்", gstCollected:"GST வசூல்",
    salesByCat:"வகை வாரியான விற்பனை",
    language:"English", adminRole:"நிர்வாகி",
    confirmDelete:"இந்த பொருளை நீக்கவா?",
    confirmDelCat:"இந்த வகையை நீக்கவா?",
    confirmDelSubCat:"இந்த உட்பிரிவை நீக்கவா?",
    stockUpdated:"சேமிக்கப்பட்டது!", billSaved:"பில் சேமிக்கப்பட்டது!", catSaved:"வகை சேமிக்கப்பட்டது!",
    catDeleted:"வகை நீக்கப்பட்டது!", subCatSaved:"உட்பிரிவு சேமிக்கப்பட்டது!", subCatDeleted:"உட்பிரிவு நீக்கப்பட்டது!",
    expenseSaved:"செலவு சேமிக்கப்பட்டது!", expenseDeleted:"செலவு நீக்கப்பட்டது!",
    invoiceNo:"இன்வாய்ஸ் எண்", date:"தேதி", time:"நேரம்",
    shopAddress:"சிவகாசி, தமிழ்நாடு - 626125",
    shopPhone:"தொ: +91 98765 43210", shopGST:"GSTIN: 33XXXXX1234X1ZX",
    billTo:"பெறுநர்", cashier:"காசாளர்", thankYou:"வாங்கியதற்கு நன்றி!",
    manageCat:"வகைகளை நிர்வகி", addCat:"வகை சேர்", catName:"வகையின் பெயர்",
    catColor:"நிறம் தேர்வு", catIcon:"ஐகான் தேர்வு", editCat:"வகை திருத்து",
    manageSubCat:"உட்பிரிவுகள்", addSubCat:"உட்பிரிவு சேர்", subCatName:"உட்பிரிவு பெயர்",
    itemsInCat:"பொருட்கள்", viewItems:"பொருட்கள் பார்க்க",
    cash:"பணம்", upi:"UPI", card:"கார்டு", credit:"கடன்",
    addExpense:"செலவு சேர்", expenseTitle:"செலவு தலைப்பு", expenseAmt:"தொகை (₹)",
    expenseCat:"செலவு வகை", expenseNote:"குறிப்பு", expenseDate:"தேதி",
    totalExpenses:"மொத்த செலவு", dailyExp:"இன்று", weeklyExp:"இந்த வாரம்",
    monthlyExp:"இந்த மாதம்", quarterExp:"காலாண்டு", annualExp:"இந்த ஆண்டு",
    expFilter:"காலம் தேர்வு", allExpenses:"அனைத்து செலவுகள்",
    noExpenses:"இந்த காலகட்டத்தில் செலவுகள் இல்லை.",
    expCats:["வாடகை","சம்பளம்","மின்சாரம்","போக்குவரத்து","கொள்முதல்","பராமரிப்பு","விளம்பரம்","பிற"],
  }
};

// ─── SEED DATA ────────────────────────────────────────────────────────────────
const INIT_CATS = [
  { id:"cat1", name:"Crackers", color:"#e74c3c", icon:"🎆",
    subCategories:[{id:"sc1",name:"Ground Items"},{id:"sc2",name:"Sky Items"},{id:"sc3",name:"Sparklers"}] },
  { id:"cat2", name:"Hardware", color:"#2980b9", icon:"🔧",
    subCategories:[{id:"sc4",name:"Nails & Bolts"},{id:"sc5",name:"Pipes & Fittings"},{id:"sc6",name:"Cement & Putty"}] },
  { id:"cat3", name:"General",  color:"#27ae60", icon:"🛒",
    subCategories:[{id:"sc7",name:"Rice & Grains"},{id:"sc8",name:"Oil & Ghee"},{id:"sc9",name:"Sugar & Salt"}] },
];
const INIT_INVENTORY = [
  { id:1,  name:"Sparklers Box",    nameTa:"மெழுகுவர்த்தி பெட்டி", category:"Crackers", subCategory:"Sparklers",      unit:"Box",    salePrice:120,  purchasePrice:80,   stock:45,  gst:18, hsn:"3604", itemCode:"CRK001", brand:"Standard" },
  { id:2,  name:"Flower Pot 10pcs", nameTa:"பூந்தொட்டி 10pcs",     category:"Crackers", subCategory:"Ground Items",    unit:"Pack",   salePrice:250,  purchasePrice:160,  stock:8,   gst:18, hsn:"3604", itemCode:"CRK002", brand:"Standard" },
  { id:3,  name:"Ground Chakkar",   nameTa:"தரை சக்கரம்",          category:"Crackers", subCategory:"Ground Items",    unit:"Piece",  salePrice:60,   purchasePrice:35,   stock:3,   gst:18, hsn:"3604", itemCode:"CRK003", brand:"Standard" },
  { id:4,  name:"Bijli String 100", nameTa:"பிஜிலி மாலை 100",      category:"Crackers", subCategory:"Sky Items",       unit:"String", salePrice:180,  purchasePrice:110,  stock:25,  gst:18, hsn:"3604", itemCode:"CRK004", brand:"Standard" },
  { id:5,  name:"Iron Nails 1kg",   nameTa:"இரும்பு ஆணி 1kg",      category:"Hardware", subCategory:"Nails & Bolts",  unit:"Kg",     salePrice:85,   purchasePrice:60,   stock:120, gst:12, hsn:"7317", itemCode:"HW001", brand:"GoodGrip" },
  { id:6,  name:"Wall Putty 5kg",   nameTa:"சுவர் பட்டி 5kg",      category:"Hardware", subCategory:"Cement & Putty", unit:"Bag",    salePrice:320,  purchasePrice:220,  stock:30,  gst:12, hsn:"3214", itemCode:"HW002", brand:"Birla" },
  { id:7,  name:"PVC Pipe 3m",      nameTa:"PVC குழாய் 3m",         category:"Hardware", subCategory:"Pipes & Fittings",unit:"Piece", salePrice:210,  purchasePrice:140,  stock:22,  gst:18, hsn:"3917", itemCode:"HW003", brand:"Finolex" },
  { id:8,  name:"Cement 50kg",      nameTa:"சிமெண்ட் 50kg",         category:"Hardware", subCategory:"Cement & Putty", unit:"Bag",    salePrice:480,  purchasePrice:370,  stock:40,  gst:28, hsn:"2523", itemCode:"HW004", brand:"Ultratech" },
  { id:9,  name:"Rice 25kg",        nameTa:"அரிசி 25kg",            category:"General",  subCategory:"Rice & Grains",  unit:"Bag",    salePrice:1450, purchasePrice:1200, stock:6,   gst:0,  hsn:"1006", itemCode:"GEN001", brand:"Ponni" },
  { id:10, name:"Coconut Oil 1L",   nameTa:"தேங்காய் எண்ணெய் 1L", category:"General",  subCategory:"Oil & Ghee",     unit:"Bottle", salePrice:180,  purchasePrice:140,  stock:55,  gst:5,  hsn:"1513", itemCode:"GEN002", brand:"Parachute" },
  { id:11, name:"Sugar 1kg",        nameTa:"சர்க்கரை 1kg",          category:"General",  subCategory:"Sugar & Salt",   unit:"Kg",     salePrice:45,   purchasePrice:38,   stock:80,  gst:5,  hsn:"1701", itemCode:"GEN003", brand:"" },
  { id:12, name:"Toor Dal 1kg",     nameTa:"துவரம் பருப்பு 1kg",    category:"General",  subCategory:"Rice & Grains",  unit:"Kg",     salePrice:135,  purchasePrice:110,  stock:4,   gst:0,  hsn:"0713", itemCode:"GEN004", brand:"" },
];
const INIT_SALES = [
  { id:"INV-0001", customer:"Ravi Kumar", phone:"9876543210", items:[{name:"Sparklers Box",qty:2,price:120,gst:18}],   subtotal:240,  gstAmt:43.2, discount:0,  total:283.2, type:"GST",     payment:"Cash",   date:"2026-03-11", time:"10:30 AM", cashier:"Admin" },
  { id:"INV-0002", customer:"Meena Devi", phone:"9876500001", items:[{name:"Coconut Oil 1L",qty:3,price:180,gst:5}],   subtotal:540,  gstAmt:27,   discount:10, total:557,   type:"Non-GST", payment:"UPI",    date:"2026-03-11", time:"11:15 AM", cashier:"Admin" },
  { id:"INV-0003", customer:"Selvan M",   phone:"9876500002", items:[{name:"Cement 50kg",qty:5,price:480,gst:28}],     subtotal:2400, gstAmt:672,  discount:0,  total:3072,  type:"GST",     payment:"Credit", date:"2026-03-11", time:"12:00 PM", cashier:"Admin" },
  { id:"INV-0004", customer:"Priya S",    phone:"9876500003", items:[{name:"Rice 25kg",qty:2,price:1450,gst:0}],       subtotal:2900, gstAmt:0,    discount:50, total:2850,  type:"Non-GST", payment:"Cash",   date:"2026-03-11", time:"01:45 PM", cashier:"Admin" },
];

// Seed expenses spanning different periods
const now = new Date();
const dStr = (d) => d.toLocaleDateString("en-IN");
const daysAgo = (n) => { const d=new Date(now); d.setDate(d.getDate()-n); return dStr(d); };
const INIT_EXPENSES = [
  { id:"EXP001", title:"Shop Rent",          category:"Rent",          amount:8000,  date:daysAgo(0),  note:"March rent paid" },
  { id:"EXP002", title:"Electricity Bill",   category:"Electricity",   amount:1200,  date:daysAgo(1),  note:"EB bill" },
  { id:"EXP003", title:"Staff Salary",       category:"Salary",        amount:12000, date:daysAgo(2),  note:"2 staff" },
  { id:"EXP004", title:"Crackers Purchase",  category:"Purchase",      amount:45000, date:daysAgo(3),  note:"Festival stock" },
  { id:"EXP005", title:"Auto Transport",     category:"Transport",     amount:850,   date:daysAgo(5),  note:"Delivery charge" },
  { id:"EXP006", title:"Generator Repair",   category:"Maintenance",   amount:2200,  date:daysAgo(8),  note:"Service" },
  { id:"EXP007", title:"Pamphlet Printing",  category:"Marketing",     amount:600,   date:daysAgo(10), note:"Offers" },
  { id:"EXP008", title:"Hardware Purchase",  category:"Purchase",      amount:18000, date:daysAgo(12), note:"PVC & Cement" },
  { id:"EXP009", title:"Water Can",          category:"Miscellaneous", amount:120,   date:daysAgo(15), note:"" },
  { id:"EXP010", title:"Shop Rent (Feb)",    category:"Rent",          amount:8000,  date:daysAgo(38), note:"February rent" },
  { id:"EXP011", title:"Salary (Feb)",       category:"Salary",        amount:12000, date:daysAgo(40), note:"" },
  { id:"EXP012", title:"EB Bill (Feb)",      category:"Electricity",   amount:1100,  date:daysAgo(42), note:"" },
  { id:"EXP013", title:"Annual Insurance",   category:"Miscellaneous", amount:5500,  date:daysAgo(60), note:"Shop insurance" },
  { id:"EXP014", title:"Shop Rent (Jan)",    category:"Rent",          amount:8000,  date:daysAgo(70), note:"" },
];

const INIT_PURCHASES = [
  { id:"PUR001", title:"Sparklers Box",    brand:"Standard",  supplier:"ABC Traders",   category:"Crackers", qty:100, unit:"Box",    costPerUnit:80,  total:8000,  date:daysAgo(3),  note:"Festival stock" },
  { id:"PUR002", title:"Iron Nails 1kg",   brand:"GoodGrip",  supplier:"Metro Hardware",category:"Hardware", qty:50,  unit:"Kg",     costPerUnit:60,  total:3000,  date:daysAgo(5),  note:"" },
  { id:"PUR003", title:"Cement 50kg",      brand:"Ultratech", supplier:"Shree Cement",  category:"Hardware", qty:20,  unit:"Bag",    costPerUnit:370, total:7400,  date:daysAgo(7),  note:"Bulk order" },
  { id:"PUR004", title:"Rice 25kg",        brand:"Ponni",     supplier:"Rajan Traders", category:"General",  qty:10,  unit:"Bag",    costPerUnit:1200,total:12000, date:daysAgo(10), note:"" },
  { id:"PUR005", title:"Bijli String 100", brand:"Classic",   supplier:"Velu Crackers", category:"Crackers", qty:50,  unit:"String", costPerUnit:110, total:5500,  date:daysAgo(12), note:"Sky items" },
  { id:"PUR006", title:"Coconut Oil 1L",   brand:"Parachute", supplier:"Raj Wholesale", category:"General",  qty:30,  unit:"Bottle", costPerUnit:140, total:4200,  date:daysAgo(15), note:"" },
];
let purCtr = 7;
const newPurId = () => "PUR" + String(purCtr++).padStart(3,"0");

const COLOR_PALETTE = ["#e74c3c","#e67e22","#f1c40f","#27ae60","#2980b9","#8e44ad","#e91e8c","#1abc9c","#ff5722","#607d8b","#ff6b35","#00bcd4"];
const ICON_OPTIONS  = ["🎆","🔧","🛒","🏪","🧨","🪔","🛠","🧲","🌾","🥤","📦","🎁","💊","🚗","🖥","👗","🍎","🧴","🏗","🎯"];
const INIT_EXP_CATS = [
  { id:"ec1", name:"Rent",          icon:"🏠", color:"#8e44ad" },
  { id:"ec2", name:"Salary",        icon:"👷", color:"#2980b9" },
  { id:"ec3", name:"Electricity",   icon:"⚡", color:"#f1c40f" },
  { id:"ec4", name:"Transport",     icon:"🚚", color:"#e67e22" },
  { id:"ec5", name:"Purchase",      icon:"🛍", color:"#e74c3c" },
  { id:"ec6", name:"Maintenance",   icon:"🔨", color:"#607d8b" },
  { id:"ec7", name:"Marketing",     icon:"📣", color:"#e91e8c" },
  { id:"ec8", name:"Miscellaneous", icon:"📌", color:"#7f8c8d" },
];
const GST_RATES = [0,5,12,18,28];
const UNITS     = ["Piece","Box","Pack","Kg","Bag","Bottle","String","Dozen","Litre","Meter"];

let invCtr = 5, expCtr = 15;
const newInvId = () => "INV-" + String(invCtr++).padStart(4,"0");
const newExpId = () => "EXP" + String(expCtr++).padStart(3,"0");
const fmt      = n => "₹" + Number(n).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const today    = () => new Date().toLocaleDateString("en-IN");
const nowTime  = () => new Date().toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"});
const toDate   = str => { const [d,m,y]=str.split("/"); return new Date(y,m-1,d); };

// ─── PRINT ────────────────────────────────────────────────────────────────────
function doPrintA4(bill, t, cfg={}) {
  const invLabel   = bill.invoiceType || cfg.printInvoiceType || (bill.type==="GST"?"TAX INVOICE":"BILL");
  const invNoLabel = {"TAX INVOICE":"Invoice No","BILL / RECEIPT":"Bill No","ESTIMATE":"Estimate No","DELIVERY NOTE":"DN No","PROFORMA INVOICE":"Proforma No"}[invLabel]||"Invoice No";
  const footer     = cfg.printFooterText  || t.thankYou;
  const hFSize     = cfg.printHeaderFontSize||"11";
  const header     = cfg.printHeaderText  ? `<div style="text-align:center;font-size:${hFSize}px;color:#555;margin-bottom:6px;white-space:pre-line">${cfg.printHeaderText}</div>` : "";
  const isA5       = cfg.printPaperSize==="A5";
  const isLandscape= cfg.printOrientation==="landscape";
  const themeColor = {red:"#e74c3c",blue:"#2980b9",green:"#27ae60",dark:"#2c3e50",purple:"#8e44ad"}[cfg.printColorTheme]||"#e74c3c";
  const fSize      = {small:"11",medium:"12",large:"15"}[cfg.printFontSize]||cfg.printFontSize||"12";
  const grey       = cfg.printGreyscale;
  const logoHtml   = cfg.printLogoUrl ? `<img class="logo" src="${cfg.printLogoUrl}" style="height:${cfg.printLogoSize||60}px;width:auto;object-fit:contain;"/>` : `<div style="font-size:22px;font-weight:900;color:${grey?"#111":themeColor}">🎆 Nethran Crackers</div>`;
  // Greyscale: replace ink-heavy filled blocks with bordered lines; logo stays full colour
  const thStyle    = grey
    ? `background:none;color:#111;padding:9px 10px;font-size:11px;text-transform:uppercase;letter-spacing:.8px;border-top:2px solid #333;border-bottom:2px solid #333;`
    : `background:${themeColor};color:#fff;padding:9px 10px;font-size:11px;text-transform:uppercase;letter-spacing:.8px`;
  const gtStyle    = grey
    ? `background:none;color:#111;font-size:15px;font-weight:800;border-top:2.5px solid #333;border-bottom:2.5px solid #333;`
    : `background:${themeColor};color:#fff;font-size:15px;font-weight:800`;
  const address    = cfg.storeAddressLine || t.shopAddress || "";
  const phone      = cfg.storePhone      || t.shopPhone   || "";
  const gstin      = cfg.storeGSTIN      || t.shopGST     || "";
  const email      = cfg.storeEmail      ? `<br>${cfg.storeEmail}` : "";
  const extraLines = (cfg.storeExtraLines||[]).filter(Boolean).map(l=>`<br>${l}`).join("");
  const showCashier  = cfg.printShowCashier !== false;
  const showPayment  = cfg.printShowPayment !== false;

  const gR = bill.type==="GST" ? `<tr><td colspan="5" align="right" style="padding:3px 10px;color:#555">GST (${bill.gstAmt > 0 ? ((bill.gstAmt/bill.subtotal)*100).toFixed(0) : 0}%)</td><td align="right" style="padding:3px 10px;font-weight:600">₹${(bill.gstAmt||0).toFixed(2)}</td></tr>` : "";
  const dR = bill.discount>0 ? `<tr><td colspan="5" align="right" style="padding:3px 10px;color:${grey?"#111":"#c0392b"}">Discount</td><td align="right" style="padding:3px 10px;color:${grey?"#111":"#c0392b"}">-₹${(bill.discount||0).toFixed(2)}</td></tr>` : "";
  return `<!DOCTYPE html><html><head><title>${bill.id}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Segoe UI',sans-serif;font-size:${fSize}px;padding:12px 16px;color:#222;${isA5?"width:148mm;max-width:148mm;":""}${grey?"filter:grayscale(100%);":""}}
img.logo{filter:none!important}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${grey?"#333":themeColor};padding-bottom:8px;margin-bottom:6px}
.sn{font-weight:900;color:${grey?"#111":themeColor}}.ss{font-size:10px;color:#888;letter-spacing:1px;margin-top:1px}
.si{font-size:${cfg.printDetailFontSize||"11"}px;color:#555;margin-top:2px;line-height:1.3}.it{font-size:${isA5?"14":"18"}px;font-weight:800;text-align:right;color:${grey?"#111":themeColor}}
.im{font-size:${cfg.printDetailFontSize||"11"}px;color:#555;margin-top:2px;text-align:right;line-height:1.3}
.bt{background:#f9f9f9;border-radius:6px;padding:4px 10px;margin-bottom:6px;font-size:${cfg.printDetailFontSize||"11"}px;color:#444;line-height:1.4}
table{width:100%;border-collapse:collapse}th{${thStyle}}
td{padding:5px 8px;border-bottom:1px solid #eee}tfoot tr:last-child td{${gtStyle}}
.ft{margin-top:12px;text-align:center;font-size:10px;color:#999;border-top:1px dashed #ccc;padding-top:7px;white-space:pre-line}
.gst-box{margin-top:6px;border:1px solid #ddd;border-radius:5px;padding:5px 10px;font-size:10px;color:#555;background:#fafafa}
@media print{.no-print{display:none!important}body{padding:10px;${isA5?"width:148mm;max-width:148mm;":""}}}
@page{size:${isA5?"A5":"A4"} ${isLandscape?"landscape":"portrait"};margin:10mm;}
</style></head><body>
${header}
<div class="hdr">
  <div>${logoHtml}
  <div class="si">${address}<br>${phone}${email}${bill.type==="GST"?`<br><b>GSTIN: ${gstin}</b>`:""}${extraLines}</div></div>
  <div style="padding-top:20px"><div class="it">${invLabel}</div>
  <div class="im"><b>${invNoLabel}:</b> ${bill.id}<br><b>Date:</b> ${bill.date}<br><b>Time:</b> ${bill.time}${showPayment?`<br><b>Payment:</b> ${bill.payment}`:""}</div></div>
</div>
<div class="bt"><b>Bill To:</b> ${bill.customer||"Walk-in Customer"} ${bill.phone?`&nbsp;| 📞 ${bill.phone}`:""}</div>
<table><thead><tr><th>#</th><th>Item</th><th align="center">Qty</th><th align="right">Rate</th>${bill.type==="GST"?`<th align="center">GST%</th>`:"<th></th>"}<th align="right">Amount</th></tr></thead>
<tbody>${bill.items.map((it,i)=>`<tr><td>${i+1}</td><td>${it.name}</td><td align="center">${it.qty}</td><td align="right">₹${(it.price||0).toFixed(2)}</td><td align="center">${bill.type==="GST"?((it.gst||0)+"%"):""}</td><td align="right">₹${((it.price||0)*(it.qty||0)).toFixed(2)}</td></tr>`).join("")}</tbody>
<tfoot>
  <tr><td colspan="5" align="right" style="padding:4px 10px;color:#555">Subtotal</td><td align="right" style="padding:4px 10px;font-weight:600">₹${(bill.subtotal||0).toFixed(2)}</td></tr>
  ${gR}${dR}
  <tr><td colspan="5" align="right" style="padding:6px 10px">GRAND TOTAL</td><td align="right" style="padding:6px 10px">₹${(bill.total||0).toFixed(2)}</td></tr>
</tfoot></table>
<div class="ft">${footer}${showCashier?`\nCashier: ${bill.cashier||"Admin"}`:""}</div>
</body></html>`;
}
function doPrintThermal(bill, t, cfg={}) {
  const footer     = cfg.printFooterText || t.thankYou;
  const hFSize     = cfg.printHeaderFontSize||"9";
  const header     = cfg.printHeaderText ? `<div class="c" style="font-size:${hFSize}px;white-space:pre-line;margin-bottom:3px">${cfg.printHeaderText}</div>` : "";
  const greyStyle  = cfg.printGreyscale ? "filter:grayscale(100%)" : "";
  const logoHtml   = `<div class="c b xl">Nethran Crackers</div>`;
  const address    = cfg.storeAddressLine || t.shopAddress || "Srivilliputhur, TN";
  const phone      = cfg.storePhone      || t.shopPhone   || "+91 98765 43210";
  const gstin      = cfg.storeGSTIN      || "";
  const extraLinesThermal = (cfg.storeExtraLines||[]).filter(Boolean);
  const showCashier  = cfg.printShowCashier !== false;
  const showPayment  = cfg.printShowPayment !== false;
  const qrHtml     = cfg.printQrImage ? `<div class="c" style="margin:6px 0"><img src="${cfg.printQrImage}" style="width:${cfg.printQrSize||60}px;height:${cfg.printQrSize||60}px;object-fit:contain;${cfg.printGreyscale?"filter:grayscale(100%)":""}"/><div class="c" style="font-size:8px;margin-top:2px">${cfg.printQrLabel||"Scan to find us"}</div></div>` : "";
  const qrTop      = cfg.printQrImage && cfg.printQrPosition==="top" ? qrHtml : "";
  const qrBottom   = cfg.printQrImage && cfg.printQrPosition!=="top" ? qrHtml : "";

  return `<!DOCTYPE html><html><head><title>Receipt</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;font-size:11px;padding:8px;width:72mm;max-width:72mm;margin:0 auto;color:#000;${greyStyle}}
.c{text-align:center}.b{font-weight:700}.xl{font-size:15px}
.ln{border-top:1px dashed #000;margin:5px 0}.rw{display:flex;justify-content:space-between;margin:2px 0}
.tot{display:flex;justify-content:space-between;font-weight:700;font-size:13px;border-top:2px solid #000;padding-top:4px;margin-top:4px}
@media print{body{padding:2px;width:72mm;max-width:72mm;margin:0 auto;}}
@page{size:80mm auto;margin:4mm;}</style></head><body>
${logoHtml}
<div class="c" style="font-size:9px">${address} | ${phone}</div>
${bill.type==="GST"&&gstin?`<div class="c" style="font-size:9px">GSTIN: ${gstin}</div>`:""}
${extraLinesThermal.map(l=>`<div class="c" style="font-size:9px">${l}</div>`).join("")}
${header}
${qrTop}
<div class="ln"></div>
<div class="c b" style="font-size:10px">${cfg.printInvoiceType||"RECEIPT"}</div>
<div class="ln"></div>
<div class="rw"><span>${{"TAX INVOICE":"Invoice No","BILL / RECEIPT":"Bill No","ESTIMATE":"Estimate No","DELIVERY NOTE":"DN No","PROFORMA INVOICE":"Proforma No"}[cfg.printInvoiceType]||"Invoice No"}: ${bill.id}</span></div>
<div class="rw"><span>${bill.date} ${bill.time}</span></div>
<div class="rw"><span>Customer: ${bill.customer||"Walk-in"}</span></div>
<div class="ln"></div>
<div class="rw b"><span>Item</span><span>Amt</span></div>
<div class="ln"></div>
${bill.items.map(i=>`<div style="margin:3px 0"><div>${i.name}</div><div class="rw"><span>${i.qty} x ₹${i.price}</span><span>₹${((i.qty||0)*(i.price||0)).toFixed(2)}</span></div>${bill.type==="GST"&&i.gst>0?`<div style="font-size:9px;color:#555">GST ${i.gst}%: ₹${((i.qty||0)*(i.price||0)*(i.gst||0)/100).toFixed(2)}</div>`:""}</div>`).join("")}
<div class="ln"></div>
<div class="rw"><span>Subtotal</span><span>₹${(bill.subtotal||0).toFixed(2)}</span></div>
${bill.type==="GST"?`<div class="rw b"><span>GST</span><span>₹${(bill.gstAmt||0).toFixed(2)}</span></div>`:""}
${bill.discount>0?`<div class="rw"><span>Discount</span><span>-₹${(bill.discount||0).toFixed(2)}</span></div>`:""}
<div class="tot"><span>TOTAL</span><span>₹${(bill.total||0).toFixed(2)}</span></div>
<div class="ln"></div>
${showPayment?`<div class="rw"><span>Payment: ${bill.payment}</span></div>`:""}
${bill.type==="GST"?`<div class="ln"></div><div class="c" style="font-size:8px">GSTIN: ${gstin} | Tax Inv.</div>`:""}
<div class="ln"></div>
<div class="c" style="margin-top:6px;font-size:10px;white-space:pre-line">${footer}</div>
${showCashier?`<div class="c" style="font-size:9px">Cashier: ${bill.cashier||"Admin"}</div>`:""}
${qrBottom}
</body></html>`;
}

// ─── PRINT OVERLAY (works on Android — shows bill in-app with Print + Back) ───
function PrintOverlay({ html, onClose }) {
  const iframeRef = useRef(null);
  const handlePrint = () => {
    if (iframeRef.current) iframeRef.current.contentWindow.print();
    else window.print();
  };
  return (
    <div style={{position:"fixed",inset:0,zIndex:9000,background:"#fff",display:"flex",flexDirection:"column"}}>
      {/* Toolbar */}
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 16px",background:"#3a3d80",flexShrink:0,paddingTop:"calc(10px + env(safe-area-inset-top))"}}>
        <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",color:"#fff",borderRadius:8,padding:"8px 14px",fontSize:14,fontWeight:700,cursor:"pointer"}}>← Back</button>
        <div style={{flex:1,color:"#fff",fontSize:13,fontWeight:600}}>Bill Preview</div>
        <button onClick={handlePrint} style={{background:"#fff",border:"none",color:"#3a3d80",borderRadius:8,padding:"8px 16px",fontSize:14,fontWeight:800,cursor:"pointer"}}>🖨️ Print</button>
      </div>
      {/* Bill content */}
      <iframe ref={iframeRef} srcDoc={html} style={{flex:1,border:"none",width:"100%"}} title="Bill Preview"/>
    </div>
  );
}

// ─── UI HELPERS ───────────────────────────────────────────────────────────────
function Toast({msg}){
  return <div style={{position:"fixed",bottom:28,right:28,zIndex:9999,background:"linear-gradient(135deg,#27ae60,#1a7a3c)",color:"#fff",padding:"11px 22px",borderRadius:12,fontSize:13,fontWeight:600,boxShadow:"0 8px 28px #0004",animation:"fadeUp .3s ease"}}>{msg}</div>;
}
function Modal({title,children,onClose,width=480,th}){
  return (
    <div style={{position:"fixed",inset:0,background:"#0007",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:th.modalBg,borderRadius:20,border:`1px solid ${th.cardBorder}`,padding:26,width,maxWidth:"95vw",maxHeight:"90vh",overflowY:"auto",boxShadow:"0 24px 80px #0003"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:15,fontWeight:700,color:th.textHeading}}>{title}</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:th.textSub,cursor:"pointer",fontSize:20,lineHeight:1}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({label,children,th}){
  return (
    <div style={{marginBottom:13}}>
      <label style={{display:"block",fontSize:10,color:th.textSub,marginBottom:5,textTransform:"uppercase",letterSpacing:.8}}>{label}</label>
      {children}
    </div>
  );
}
function StatCard({label,value,icon,color,sub,th}){
  return (
    <div style={{background:th.cardBg,borderRadius:16,border:`1px solid ${color}33`,padding:"20px 22px",boxShadow:`0 2px 12px ${color}12`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:1,marginBottom:10}}>{label}</div>
          <div style={{fontSize:30,fontWeight:900,color,letterSpacing:"-0.5px"}}>{value}</div>
          {sub&&<div style={{fontSize:13,fontWeight:600,color:th.textSub,marginTop:6}}>{sub}</div>}
        </div>
        <div style={{fontSize:32}}>{icon}</div>
      </div>
    </div>
  );
}

// ─── EXPENSE PERIOD FILTER ────────────────────────────────────────────────────
function filterExpenses(expenses, period) {
  const now = new Date();
  return expenses.filter(e => {
    const d = toDate(e.date);
    if (isNaN(d)) return false;
    switch(period) {
      case "daily":   return dStr(d)===dStr(now);
      case "weekly":  { const w=new Date(now); w.setDate(w.getDate()-6); return d>=w; }
      case "monthly": return d.getMonth()===now.getMonth() && d.getFullYear()===now.getFullYear();
      case "quarter": { const q=new Date(now); q.setMonth(q.getMonth()-3); return d>=q; }
      case "annual":  return d.getFullYear()===now.getFullYear();
      default:        return true;
    }
  });
}

// ─── EXCEL UTILITIES ─────────────────────────────────────────────────────────
async function exportToCSV(rows, headers, filename) {
  const escape = v => { const s = String(v==null?"":""+v); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g,'""')}"` : s; };
  const csv = [headers.join(","), ...rows.map(r=>r.map(escape).join(","))].join("\n");
  const isAndroid = /android/i.test(navigator.userAgent);
  if (isAndroid) {
    try {
      // Write to filesystem then share
      const b64 = btoa(unescape(encodeURIComponent(csv)));
      const result = await Filesystem.writeFile({ path: filename, data: b64, directory: Directory.Cache });
      await Share.share({ title: filename, url: result.uri, dialogTitle: "Save or open report" });
    } catch(e) {
      // Fallback: share as text
      try { await Share.share({ title: filename, text: csv, dialogTitle: "Share report" }); } catch(_) {}
    }
    return;
  }
  // Web / Desktop
  const blob = new Blob([csv], {type:"text/csv;charset=utf-8;"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function downloadInventoryTemplate() {
  const headers = ["Item Name (EN)","Item Name (Tamil)","Item Code","Brand","Category","Sub Category","Unit","Sale Price","Purchase Price","Stock","GST %","HSN Code"];
  const sample  = [["Sparklers Box","மெழுகுவர்த்தி பெட்டி","CRK001","Standard","Crackers","Sparklers","Box","120","80","45","18","3604"]];
  exportToCSV(sample, headers, "inventory_template.csv");
}

function exportInventoryCSV(inventory) {
  const headers = ["Item Name (EN)","Item Name (Tamil)","Item Code","Brand","Category","Sub Category","Unit","Sale Price","Purchase Price","Stock","GST %","HSN Code"];
  const rows    = inventory.map(i=>[i.name,i.nameTa||"",i.itemCode||"",i.brand||"",i.category,i.subCategory||"",i.unit,i.salePrice,i.purchasePrice,i.stock,i.gst,i.hsn||""]);
  exportToCSV(rows, headers, "inventory_export.csv");
}

function exportExpensesCSV(expenses) {
  const headers = ["ID","Title","Category","Amount","Date","Note"];
  const rows    = expenses.map(e=>[e.id,e.title,e.category,e.amount,e.date,e.note||""]);
  exportToCSV(rows, headers, "expenses_export.csv");
}

function parseInventoryCSV(text, cats) {
  const lines = text.trim().split("\n").filter(Boolean);
  if(lines.length<2) return [];
  // skip header row
  return lines.slice(1).map((line,idx)=>{
    const cols = line.match(/("([^"]|"")*"|[^,]*)(,|$)/g)?.map(c=>{
      const v=c.replace(/,$/, "").trim();
      return v.startsWith('"')&&v.endsWith('"') ? v.slice(1,-1).replace(/""/g,'"') : v;
    }) || [];
    return {
      id: Date.now()+idx,
      name:         cols[0]||"",
      nameTa:       cols[1]||"",
      itemCode:     cols[2]||"",
      brand:        cols[3]||"",
      category:     cols[4]||"Uncategorized",
      subCategory:  cols[5]||"",
      unit:         cols[6]||"Piece",
      salePrice:    +cols[7]||0,
      purchasePrice:+cols[8]||0,
      stock:        +cols[9]||0,
      gst:          +cols[10]||0,
      hsn:          cols[11]||"",
    };
  }).filter(i=>i.name.trim());
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
function NethranApp({ storeId, storeName: storeNameProp, currentUser, onLogout, onSwitchStore, onManageUsers, allStores:_allStores, fbUser, onConnectCloud }) {
  const PenIcon = ()=><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline-block",verticalAlign:"middle",marginRight:6}}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
  const [lang,        setLang]       = useState(()=>{ try{ return JSON.parse(localStorage.getItem("nethran_settings_" + (storeId||"default"))||"{}").lang||"en"; }catch(e){return "en";} });
  // eslint-disable-next-line no-unused-vars
  const themeName = "light";
  const [showSettings, setShowSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Load defaults from localStorage on first render
  const _ls = ()=>{ try{ return JSON.parse(localStorage.getItem("nethran_settings_" + (storeId||"default"))||"{}"); }catch(e){return {};} };
  const _updateStoreField = (field, val) => {
    try {
      const stores = JSON.parse(localStorage.getItem("nethran_stores")||"[]");
      const updated = stores.map(s => s.id===storeId ? {...s,[field]:val} : s);
      localStorage.setItem("nethran_stores", JSON.stringify(updated));
    } catch(e) {}
  };
  const [storeName,     setStoreNameLocal]     = useState(()=>{ try{ const s=JSON.parse(localStorage.getItem("nethran_stores")||"[]").find(s=>s.id===storeId); return s?.name||storeNameProp||""; }catch(e){return storeNameProp||"";} });
  const [storeLocation, setStoreLocationLocal] = useState(()=>{ try{ const s=JSON.parse(localStorage.getItem("nethran_stores")||"[]").find(s=>s.id===storeId); return s?.location||""; }catch(e){return "";} });
  const [defInvType,   setDefInvType]  = useState(()=>_ls().invType||"GST");
  const [defBillInvoiceType, setDefBillInvoiceType] = useState(()=>_ls().defBillInvoiceType||"TAX INVOICE");
  // eslint-disable-next-line no-unused-vars
  const [defPayment,   setDefPayment]  = useState("Cash");
  const [defPrintMode, setDefPrintMode]= useState(()=>_ls().printMode||"A4");
  const [printPaperSize,   setPrintPaperSize]   = useState(()=>_ls().printPaperSize||"A4");
  const [showPrintSetup,    setShowPrintSetup]    = useState(false);
  const [printLogoUrl,      setPrintLogoUrl]      = useState(()=>_ls().printLogoUrl||"");
  const [printLogoSize,     setPrintLogoSize]     = useState(()=>_ls().printLogoSize||60);
  const [storeAddressLine,  setStoreAddressLine]  = useState(()=>_ls().storeAddressLine||"Srivilliputhur, Tamil Nadu");
  const [storePhone,        setStorePhone]        = useState(()=>_ls().storePhone||"+91 98765 43210");
  const [storeGSTIN,        setStoreGSTIN]        = useState(()=>_ls().storeGSTIN||"");
  const [storeEmail,        setStoreEmail]        = useState(()=>_ls().storeEmail||"");
  // eslint-disable-next-line no-unused-vars
  const [storeTagline,      setStoreTagline]      = useState(()=>_ls().storeTagline||"Crackers · Hardware · General Supplies");
  const [storeExtraLines,   setStoreExtraLines]   = useState(()=>{ try{ return JSON.parse(_ls().storeExtraLines||"[]"); }catch(e){ return []; } });
  const [printSizeConfigs,  setPrintSizeConfigs]  = useState(()=>{
    const saved = _ls().printSizeConfigs;
    return saved || {
      A4:   {invoiceType:"TAX INVOICE",  headerText:"", footerText:"Thank you for shopping with us!", orientation:"portrait", colorTheme:"red",  fontSize:"12", headerFontSize:"11", detailFontSize:"11", showCashier:true, showPayment:true, greyscale:false},
      A5:   {invoiceType:"BILL / RECEIPT",headerText:"", footerText:"Thank you!",                     orientation:"portrait", colorTheme:"blue", fontSize:"11", headerFontSize:"10", detailFontSize:"10", showCashier:true, showPayment:true, greyscale:false},
      "80mm":{invoiceType:"RECEIPT",     headerText:"", footerText:"Thank you!",                     orientation:"portrait", colorTheme:"red",  fontSize:"10", headerFontSize:"9",  detailFontSize:"9",  showCashier:true, showPayment:true, greyscale:false, qrImage:"", qrSize:60, qrLabel:"Scan to find us", qrPosition:"bottom"}
    };
  });
  const [printSetupTab,     setPrintSetupTab]     = useState("A4");
  const [defLang,      setDefLang]     = useState(()=>_ls().lang||"en");
  const [tab,         setTab]        = useState("dashboard");
  // eslint-disable-next-line no-unused-vars
  const [sidebarOpen, setSidebarOpen]= useState(true);
  const [cats,        setCats]       = useState(()=>{ try{ const d=localStorage.getItem("nethran_cats_"+(storeId||"default")); return d?JSON.parse(d):INIT_CATS; }catch(e){return INIT_CATS;} });
  const [inventory,   setInventory]  = useState(()=>{ try{ const d=localStorage.getItem("nethran_inventory_"+(storeId||"default")); return d?JSON.parse(d):INIT_INVENTORY; }catch(e){return INIT_INVENTORY;} });
  const [sales,       setSales]      = useState(()=>{ try{ const d=localStorage.getItem("nethran_sales_"+(storeId||"default")); return d?JSON.parse(d):INIT_SALES; }catch(e){return INIT_SALES;} });
  const [expenses,    setExpenses]   = useState(()=>{ try{ const d=localStorage.getItem("nethran_expenses_"+(storeId||"default")); return d?JSON.parse(d):INIT_EXPENSES; }catch(e){return INIT_EXPENSES;} });
  const [purchases,   setPurchases]  = useState(()=>{ try{ const d=localStorage.getItem("nethran_purchases_"+(storeId||"default")); return d?JSON.parse(d):INIT_PURCHASES; }catch(e){return INIT_PURCHASES;} });
  const [customers,   setCustomers]  = useState(()=>{ try{ const d=localStorage.getItem("nethran_customers_"+(storeId||"default")); return d?JSON.parse(d):[]; }catch(e){return [];} });
  const [showCustModal, setShowCustModal] = useState(false);
  const [editingCust,   setEditingCust]   = useState(null);
  const [custForm,      setCustForm]      = useState({name:"",phone:"",email:"",address:"",pan:"",gstin:"",aadhar:"",notes:""});
  const [custSearch,    setCustSearch]    = useState("");
  const [toast,       setToast]      = useState(null);
  const [cloudSynced, setCloudSynced] = useState(false);
  const [printHtml,   setPrintHtml]  = useState(null);
  const showPrint = useCallback((html) => setPrintHtml(html), []);

  // Real-time cloud sync — fires immediately on mount (startup pull) + on every change
  useEffect(() => {
    if (!fbUser || !storeId) return;
    setCloudSynced(false);
    const unsub = subscribeToStore(storeId, {
      sales:       items => { setSales(items);     localStorage.setItem("nethran_sales_"      + storeId, JSON.stringify(items)); },
      inventory:   items => { setInventory(items); localStorage.setItem("nethran_inventory_"  + storeId, JSON.stringify(items)); },
      categories:  items => { setCats(items);      localStorage.setItem("nethran_cats_"       + storeId, JSON.stringify(items)); },
      expenses:    items => { setExpenses(items);  localStorage.setItem("nethran_expenses_"   + storeId, JSON.stringify(items)); },
      purchases:   items => { setPurchases(items); localStorage.setItem("nethran_purchases_"  + storeId, JSON.stringify(items)); },
      customers:   items => { setCustomers(items); localStorage.setItem("nethran_customers_"  + storeId, JSON.stringify(items)); },
    });
    setCloudSynced(true);
    return unsub; // unsubscribe on logout / store switch
  }, [fbUser, storeId]);

  // Inventory state
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem,   setEditingItem]   = useState(null);
  const [itemForm,      setItemForm]      = useState({});
  const [confirmDel,    setConfirmDel]    = useState(null);
  const [invSearch,     setInvSearch]     = useState("");
  const [invCat,        setInvCat]        = useState("all");
  const [invSubCat,     setInvSubCat]     = useState("all");
  const [importError,   setImportError]   = useState(null);

  // Category state
  const [showCatModal,     setShowCatModal]     = useState(false);
  const [editingCat,       setEditingCat]       = useState(null);
  const [catForm,          setCatForm]          = useState({});
  const [confirmDelCat,    setConfirmDelCat]    = useState(null);
  const [showSubCatModal,  setShowSubCatModal]  = useState(false);
  const [editingSubCat,    setEditingSubCat]    = useState(null);
  const [subCatForm,       setSubCatForm]       = useState({});
  const [subCatParentId,   setSubCatParentId]   = useState(null);
  const [confirmDelSubCat, setConfirmDelSubCat] = useState(null);
  const [expandedCat,      setExpandedCat]      = useState(null);

  // Billing state
  const [cart,         setCart]         = useState([]);
  const [billCustomer, setBillCustomer] = useState("");
  const [billPhone,    setBillPhone]    = useState("");
  const [billType,     setBillType]     = useState(()=>{ try{ return JSON.parse(localStorage.getItem("nethran_settings_" + (storeId||"default"))||"{}").invType||"GST"; }catch(e){return "GST";} });
  const [billDiscount, setBillDiscount] = useState(0);
  const [billPayment,  setBillPayment]  = useState("Cash");
  const [billInvoiceType, setBillInvoiceType] = useState(()=>_ls().defBillInvoiceType||"TAX INVOICE");
  const [billSearch,   setBillSearch]   = useState("");
  const [posHighlight, setPosHighlight] = useState(0);   // arrow-key highlighted index in dropdown
  const [posQtyMode,   setPosQtyMode]   = useState(null);// item waiting for qty input in POS mode
  const [posQtyVal,    setPosQtyVal]    = useState("1"); // qty string while typing
  const cartScrollRef = useRef(null);
  const posSearchEnterRef = useRef(0);
  useEffect(()=>{ if(cartScrollRef.current) cartScrollRef.current.scrollTop = cartScrollRef.current.scrollHeight; },[cart]);
  useEffect(()=>{ if(tab==="billing"){ const t=setTimeout(()=>document.getElementById("pos-search")?.focus(),80); return()=>clearTimeout(t); } },[tab]);
  // eslint-disable-next-line no-unused-vars
  const [billCat,      setBillCat]      = useState("all");
  // eslint-disable-next-line no-unused-vars
  const [billSubCat,   setBillSubCat]   = useState("all");
  // Touch billing state
  const [touchStep,    setTouchStep]    = useState("category");
  const [expandedPur,  setExpandedPur]  = useState(null); // purchase row id
  // eslint-disable-next-line no-unused-vars
  const [expandedExp,  setExpandedExp]  = useState(null); // expense row id
  const [showInvHistory,   setShowInvHistory]   = useState(false);
  const [invPrintPick,     setInvPrintPick]     = useState(null); // sale object awaiting print type
  const [billSuccess,      setBillSuccess]      = useState(null); // saved bill object
  // eslint-disable-next-line no-unused-vars
  const [showSalesReport,  setShowSalesReport]  = useState(false);
  const [showInvEdit,      setShowInvEdit]      = useState(false); // invoice edit modal
  const [showOwnerAnalytics, setShowOwnerAnalytics] = useState(false); // owner analytics panel
  const [analyticsPeriod,  setAnalyticsPeriod]  = useState("monthly"); // today|weekly|monthly|custom
  const [analyticsFrom,    setAnalyticsFrom]    = useState(""); // custom from date (YYYY-MM-DD)
  const [analyticsTo,      setAnalyticsTo]      = useState(""); // custom to date (YYYY-MM-DD)
  const [editingInv,       setEditingInv]       = useState(null);  // invoice being edited
  const [invEditForm,      setInvEditForm]      = useState(null);  // edit form state
  const [srPeriod,         setSrPeriod]         = useState("daily");
  const [srCat,            setSrCat]            = useState("all");
  const [invHisSearch,  setInvHisSearch]  = useState("");
  const [invHisDate,    setInvHisDate]    = useState("");
  const [invHisType,    setInvHisType]    = useState("all");  // all | GST | Non-GST
  const [invHisPay,     setInvHisPay]     = useState("all");  // all | Cash | UPI | Card | Credit
  const [purBrandOpen, setPurBrandOpen] = useState(false); // By Brand panel
  const [purCatOpen,   setPurCatOpen]   = useState(false); // By Category panel (purchases)
  const [expCatOpen,   setExpCatOpen]   = useState(false); // By Category panel (expenses) // category | subcategory | items
  const [touchCat,     setTouchCat]     = useState(null);
  const [touchSubCat,  setTouchSubCat]  = useState(null);
  const [qtyModal,     setQtyModal]     = useState(null); // item waiting for qty input
  const [qtyInput,     setQtyInput]     = useState("1");
  // eslint-disable-next-line no-unused-vars
  const isMobile = typeof window !== "undefined" && (('ontouchstart' in window || navigator.maxTouchPoints > 0) || window.innerWidth < 768);

  // Sales tab state
  const [salesSearch,     setSalesSearch]     = useState("");
  const [salesPeriod,     setSalesPeriod]     = useState("monthly");
  const [salesPayFilter,  setSalesPayFilter]  = useState("all");
  const [salesTypeFilter, setSalesTypeFilter] = useState("all");
  const [salesDateFrom,   setSalesDateFrom]   = useState("");
  const [salesDateTo,     setSalesDateTo]     = useState("");
  const [showRefundModal,   setShowRefundModal]   = useState(false);
  const [refundSale,        setRefundSale]        = useState(null);
  const [refundReason,      setRefundReason]      = useState("");
  const [analyticsDateFrom, setAnalyticsDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [analyticsDateTo,   setAnalyticsDateTo]   = useState(`${new Date().getFullYear()}-12-31`);

  // Purchase state
  const [showPurModal,  setShowPurModal]  = useState(false);
  const [editingPur,    setEditingPur]    = useState(null);
  const [purForm,       setPurForm]       = useState({});
  const [confirmDelPur, setConfirmDelPur] = useState(null);
  const [purSearch,     setPurSearch]     = useState("");
  const [purCatFilter,  setPurCatFilter]  = useState("all");
  const [purPeriod,     setPurPeriod]     = useState("monthly");

  // Expense state
  const [showExpModal,     setShowExpModal]     = useState(false);
  const [editingExp,       setEditingExp]       = useState(null);
  const [expForm,          setExpForm]          = useState({});
  const [confirmDelExp,    setConfirmDelExp]    = useState(null);
  const [expPeriod,        setExpPeriod]        = useState("monthly");
  const [expCatFilter,     setExpCatFilter]     = useState("all");
  // Expense category management
  const [expCats,          setExpCats]          = useState(()=>{ try{ const d=localStorage.getItem("nethran_expCats_"+(storeId||"default")); return d?JSON.parse(d):INIT_EXP_CATS; }catch(e){return INIT_EXP_CATS;} });
  const [showExpCatModal,  setShowExpCatModal]  = useState(false);
  const [editingExpCat,    setEditingExpCat]    = useState(null);
  const [expCatForm,       setExpCatForm]       = useState({});
  const [confirmDelExpCat, setConfirmDelExpCat] = useState(null);

  const t  = T[lang];
  const th = THEMES[themeName];
  // eslint-disable-next-line no-unused-vars
  const isDark = false;
  const expCatColor = name => expCats.find(c=>c.name===name)?.color || "#888";
  const expCatIcon  = name => expCats.find(c=>c.name===name)?.icon  || "📌";

  const isTouch = typeof window!=="undefined" && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  const [isMobileView, setIsMobileView] = useState(()=> isTouch || (typeof window!=="undefined" && window.innerWidth < 768));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuTimer = useState(null);
  useEffect(()=>{
    // Only update on resize if NOT a touch device (touch devices keep mobile view regardless of orientation)
    if(isTouch) return;
    const fn = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return ()=>window.removeEventListener("resize", fn);
  },[isTouch]);



  // ── localStorage: save settings (debounced 600ms to avoid writes on every keystroke/slider) ──
  useEffect(()=>{
    const tid=setTimeout(()=>{
      try {
        localStorage.setItem("nethran_settings_" + (storeId||"default"), JSON.stringify({
          invType: defInvType, defBillInvoiceType, lang: defLang, printMode: defPrintMode,
          printPaperSize, printLogoUrl, printLogoSize, storeAddressLine, storePhone, storeGSTIN, storeEmail,
          storeTagline, printSizeConfigs, storeExtraLines: JSON.stringify(storeExtraLines)
        }));
      } catch(e) {}
    },600);
    return()=>clearTimeout(tid);
  },[defInvType, defBillInvoiceType, defLang, defPrintMode, printPaperSize, printLogoUrl, printLogoSize, storeAddressLine, storePhone, storeGSTIN, storeEmail, storeTagline, printSizeConfigs, storeExtraLines, storeId]);

  // ── Persist store data to localStorage ────────────────────────────────
  useEffect(()=>{ try{ localStorage.setItem("nethran_cats_"+(storeId||"default"), JSON.stringify(cats)); }catch(e){} },[cats, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_inventory_"+(storeId||"default"), JSON.stringify(inventory)); }catch(e){} },[inventory, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_sales_"+(storeId||"default"), JSON.stringify(sales)); }catch(e){} },[sales, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_expenses_"+(storeId||"default"), JSON.stringify(expenses)); }catch(e){} },[expenses, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_purchases_"+(storeId||"default"), JSON.stringify(purchases)); }catch(e){} },[purchases, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_expCats_"+(storeId||"default"), JSON.stringify(expCats)); }catch(e){} },[expCats, storeId]);
  useEffect(()=>{ try{ localStorage.setItem("nethran_customers_"+(storeId||"default"), JSON.stringify(customers)); }catch(e){} },[customers, storeId]);

  // ── Load data from Firestore on mount (if cloud connected) ─────────────
  useEffect(() => {
    if (!fbUser || !storeId) return;
    loadAllStoreData(storeId).then(data => {
      if (data.sales     && data.sales.length)     setSales(data.sales);
      if (data.inventory && data.inventory.length) setInventory(data.inventory);
      if (data.categories&& data.categories.length)setCats(data.categories);
      if (data.expenses  && data.expenses.length)  setExpenses(data.expenses);
      if (data.purchases && data.purchases.length) setPurchases(data.purchases);
      if (data.customers && data.customers.length) setCustomers(data.customers);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbUser, storeId]);



  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(null),2500); };
  const catColor  = name => cats.find(c=>c.name===name)?.color||"#888";
  const catIcon   = name => cats.find(c=>c.name===name)?.icon ||"📦";
  const catSubCats= name => cats.find(c=>c.name===name)?.subCategories||[];

  // ── Style helpers ──────────────────────────────────────────────────────────
  const IS  = {width:"100%",padding:"9px 13px",borderRadius:9,background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.inputText,fontSize:13,outline:"none",boxSizing:"border-box"};
  const card= {background:th.cardBg,borderRadius:16,border:`1.5px solid ${th.cardBorder}`,boxShadow:'0 2px 10px rgba(58,61,128,0.06)'};
  const btn = (c="#3a3d80")=>({padding:"9px 18px",borderRadius:9,border:"none",cursor:"pointer",background:`linear-gradient(135deg,${c},${c}cc)`,color:"#fff",fontSize:13,fontWeight:600});
  const ghost={padding:"9px 18px",borderRadius:9,border:`1px solid ${th.ghostBorder}`,background:th.ghostBg,color:th.ghostText,fontSize:13,cursor:"pointer"};
  const tag  = c=>({fontSize:10,padding:"2px 8px",borderRadius:20,fontWeight:700,background:`${c}20`,color:c,display:"inline-block"});

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const openAddCat  = () => { setEditingCat(null); setCatForm({name:"",color:COLOR_PALETTE[cats.length%COLOR_PALETTE.length],icon:ICON_OPTIONS[0],image:"",subCategories:[]}); setShowCatModal(true); };
  const openEditCat = cat => { setEditingCat(cat.id); setCatForm({...cat,subCategories:[...(cat.subCategories||[])]}); setShowCatModal(true); };
  const saveCat = () => {
    if (!catForm.name?.trim()) return;
    if (editingCat) {
      const old=cats.find(c=>c.id===editingCat)?.name;
      setCats(cats.map(c=>c.id===editingCat?{...c,...catForm}:c));
      if(old&&old!==catForm.name) setInventory(inventory.map(i=>i.category===old?{...i,category:catForm.name}:i));
    } else { setCats([...cats,{id:"cat"+Date.now(),subCategories:[],...catForm}]); }
    setShowCatModal(false); showToast(t.catSaved);
  };
  const deleteCat = id => {
    const name=cats.find(c=>c.id===id)?.name;
    setCats(cats.filter(c=>c.id!==id));
    if(name) setInventory(inventory.map(i=>i.category===name?{...i,category:"Uncategorized",subCategory:""}:i));
    setConfirmDelCat(null); showToast(t.catDeleted);
  };

  // ── SubCategory CRUD ───────────────────────────────────────────────────────
  const openAddSubCat  = (catId) => { setSubCatParentId(catId); setEditingSubCat(null); setSubCatForm({name:""}); setShowSubCatModal(true); };
  const openEditSubCat = (catId, sc) => { setSubCatParentId(catId); setEditingSubCat(sc.id); setSubCatForm({...sc}); setShowSubCatModal(true); };
  const saveSubCat = () => {
    if (!subCatForm.name?.trim()) return;
    setCats(cats.map(c => {
      if (c.id !== subCatParentId) return c;
      if (editingSubCat) {
        const oldName = c.subCategories.find(sc=>sc.id===editingSubCat)?.name;
        if(oldName && oldName!==subCatForm.name) setInventory(prev=>prev.map(i=>i.subCategory===oldName&&i.category===c.name?{...i,subCategory:subCatForm.name}:i));
        return {...c,subCategories:c.subCategories.map(sc=>sc.id===editingSubCat?{...sc,...subCatForm}:sc)};
      } else {
        return {...c,subCategories:[...(c.subCategories||[]),{id:"sc"+Date.now(),...subCatForm}]};
      }
    }));
    setShowSubCatModal(false); showToast(t.subCatSaved);
  };
  const deleteSubCat = (catId, scId) => {
    const cat = cats.find(c=>c.id===catId);
    const scName = cat?.subCategories.find(sc=>sc.id===scId)?.name;
    setCats(cats.map(c=>c.id===catId?{...c,subCategories:c.subCategories.filter(sc=>sc.id!==scId)}:c));
    if(scName) setInventory(inventory.map(i=>i.subCategory===scName?{...i,subCategory:""}:i));
    setConfirmDelSubCat(null); showToast(t.subCatDeleted);
  };

  // ── Inventory CRUD ─────────────────────────────────────────────────────────
  const openAddItem  = () => { setEditingItem(null); setItemForm({name:"",nameTa:"",itemCode:"",brand:"",category:cats[0]?.name||"",subCategory:"",unit:"Piece",salePrice:"",purchasePrice:"",stock:"",gst:18,hsn:"",image:""}); setShowItemModal(true); };
  const openEditItem = item => { setEditingItem(item.id); setItemForm({...item}); setShowItemModal(true); };
  const saveItem = () => {
    if(!itemForm.name||!itemForm.salePrice||itemForm.stock==="") return;
    const p={...itemForm,salePrice:+itemForm.salePrice,purchasePrice:+itemForm.purchasePrice,stock:+itemForm.stock,gst:+itemForm.gst};
    if(editingItem) {
      const updated = {...inventory.find(i=>i.id===editingItem)||{},...p};
      setInventory(inventory.map(i=>i.id===editingItem?updated:i));
      if(fbUser) syncDoc(storeId, "inventory", updated);
    } else {
      const newItem = {...p, id:Date.now()};
      setInventory([...inventory, newItem]);
      if(fbUser) syncDoc(storeId, "inventory", newItem);
    }
    setShowItemModal(false); showToast(t.stockUpdated);
  };
  const deleteItem = id => {
    setInventory(inventory.filter(i=>i.id!==id));
    setConfirmDel(null);
  };

  // ── POS search results — memoized to avoid re-filtering on every cart update ──
  const posResults = useMemo(()=>
    billSearch.trim().length>0
      ? inventory.filter(i=>i.stock>0&&(
          i.name.toLowerCase().includes(billSearch.toLowerCase())||
          (i.nameTa&&i.nameTa.includes(billSearch))||
          (i.itemCode&&i.itemCode.toLowerCase().includes(billSearch.toLowerCase()))||
          (i.brand&&i.brand.toLowerCase().includes(billSearch.toLowerCase()))||
          i.category.toLowerCase().includes(billSearch.toLowerCase())||
          String(i.salePrice).includes(billSearch.trim())
        )).slice(0,12)
      : []
  ,[billSearch, inventory]);

  // ── Print config object — shared across all print call sites ─────────────
  const pCfg = useMemo(()=>{
    const sizeKey = defPrintMode==="80mm"?"80mm":printPaperSize==="A5"?"A5":"A4";
    const sc = (printSizeConfigs&&printSizeConfigs[sizeKey])||{};
    return {
      printPaperSize: sizeKey,
      printInvoiceType: sc.invoiceType||"TAX INVOICE",
      printHeaderText:  sc.headerText||"",
      printFooterText:  sc.footerText||"Thank you for shopping with us!",
      printOrientation: sc.orientation||"portrait",
      printColorTheme:  sc.colorTheme||"red",
      printFontSize:       sc.fontSize||"12",
      printHeaderFontSize: sc.headerFontSize||"11",
      printDetailFontSize: sc.detailFontSize||"11",
      printShowCashier: sc.showCashier!==false,
      printShowPayment: sc.showPayment!==false,
      printGreyscale:   sc.greyscale||false,
      printQrImage:     sc.qrImage||"",
      printQrSize:      sc.qrSize||60,
      printQrLabel:     sc.qrLabel||"Scan to find us",
      printQrPosition:  sc.qrPosition||"bottom",
      printLogoUrl, printLogoSize,
      storeAddressLine, storePhone, storeGSTIN, storeEmail, storeTagline, storeExtraLines,
    };
  },[defPrintMode, printPaperSize, printSizeConfigs, printLogoUrl, printLogoSize, storeAddressLine, storePhone, storeGSTIN, storeEmail, storeTagline, storeExtraLines]);

  const filteredInv = useMemo(()=>inventory.filter(i=>
    (invCat==="all"||i.category===invCat)&&
    (invSubCat==="all"||i.subCategory===invSubCat)&&
    ((lang==="en"?i.name:i.nameTa).toLowerCase().includes(invSearch.toLowerCase()))
  ),[inventory,invCat,invSubCat,invSearch,lang]);

  // ── Billing ────────────────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const addToCart = item => {
    if(item.stock===0)return;
    const ex=cart.find(c=>c.id===item.id);
    if(ex){if(ex.qty>=item.stock)return;setCart(cart.map(c=>c.id===item.id?{...c,qty:c.qty+1}:c));}
    else setCart([...cart,{...item,qty:1}]);
  };
  const updateQty = (id,qty) => {
    if(qty<1){setCart(cart.filter(c=>c.id!==id));return;}
    const it=inventory.find(i=>i.id===id);if(qty>it.stock)return;
    setCart(cart.map(c=>c.id===id?{...c,qty}:c));
  };
  const cartSubtotal = cart.reduce((s,i)=>s+i.salePrice*i.qty,0);
  const cartGST      = billType==="GST"?cart.reduce((s,i)=>s+(i.salePrice*i.qty*i.gst)/100,0):0;
  const cartTotal    = cartSubtotal+cartGST-Number(billDiscount||0);
  const saveBill = printType => {
    if(!cart.length)return;
    const bill={id:newInvId(),customer:billCustomer||"Walk-in Customer",phone:billPhone,
      items:cart.map(c=>({name:lang==="en"?c.name:c.nameTa,qty:c.qty,price:c.salePrice,gst:c.gst})),
      subtotal:cartSubtotal,gstAmt:cartGST,discount:Number(billDiscount||0),total:cartTotal,
      type:billType,payment:billPayment,invoiceType:billInvoiceType,date:today(),time:nowTime(),cashier:"Admin"};
    const newInventory = inventory.map(inv=>{const ci=cart.find(c=>c.id===inv.id);return ci?{...inv,stock:inv.stock-ci.qty}:inv;});
    setInventory(newInventory);
    setSales([bill,...sales]);
    // Sync to Firestore if owner is logged in
    if(fbUser) {
      syncDoc(storeId, "sales", bill);
      cart.forEach(c => {
        const updated = newInventory.find(i=>i.id===c.id);
        if(updated) syncDoc(storeId, "inventory", updated);
      });
    }
    if(printType==="A4")showPrint(doPrintA4(bill,t,pCfg));
    else if(printType==="thermal")showPrint(doPrintThermal(bill,t,pCfg));
    setCart([]);setBillCustomer("");setBillPhone("");setBillDiscount(0);setBillPayment("Cash");
    setTouchStep("category");
    setBillSuccess(bill); // show success dialog
  };

  // ── Invoice Edit / Cancel ─────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const openEditInv = inv => {
    setEditingInv(inv);
    setInvEditForm({
      customer: inv.customer,
      phone: inv.phone||"",
      type: inv.type,
      payment: inv.payment,
      discount: inv.discount||0,
      items: inv.items.map(it=>({...it})),
      status: inv.status||"active",
      cancelReason: ""
    });
    setShowInvEdit(true);
  };
  const saveInvEdit = () => {
    if(!invEditForm) return;
    // Recalculate totals
    const subtotal = invEditForm.items.reduce((s,it)=>s+it.price*it.qty,0);
    const gstAmt   = invEditForm.type==="GST" ? invEditForm.items.reduce((s,it)=>s+(it.price*it.qty*(it.gst||0)/100),0) : 0;
    const total    = subtotal + gstAmt - Number(invEditForm.discount||0);
    const updated  = {...editingInv, ...invEditForm, subtotal, gstAmt, total, editedAt: nowTime()+" "+today()};
    // Restore old stock, deduct new stock
    let inv2 = [...inventory];
    editingInv.items.forEach(oi=>{
      const found = inv2.find(i=>i.name===oi.name||i.nameTa===oi.name);
      if(found) inv2 = inv2.map(i=>i.id===found.id?{...i,stock:i.stock+oi.qty}:i);
    });
    invEditForm.items.forEach(ni=>{
      const found = inv2.find(i=>i.name===ni.name||i.nameTa===ni.name);
      if(found) inv2 = inv2.map(i=>i.id===found.id?{...i,stock:Math.max(0,i.stock-ni.qty)}:i);
    });
    setInventory(inv2);
    setSales(sales.map(s=>s.id===editingInv.id ? updated : s));
    setShowInvEdit(false); setEditingInv(null); setInvEditForm(null);
    showToast("Invoice updated!");
  };
  const cancelInvoice = () => {
    if(!invEditForm?.cancelReason?.trim()) return;
    // Restore stock
    let inv2 = [...inventory];
    editingInv.items.forEach(oi=>{
      const found = inv2.find(i=>i.name===oi.name||i.nameTa===oi.name);
      if(found) inv2 = inv2.map(i=>i.id===found.id?{...i,stock:i.stock+oi.qty}:i);
    });
    setInventory(inv2);
    const cancelled = {...editingInv, status:"cancelled", cancelReason:invEditForm.cancelReason, cancelledAt: nowTime()+" "+today()};
    setSales(sales.map(s=>s.id===editingInv.id ? cancelled : s));
    setShowInvEdit(false); setEditingInv(null); setInvEditForm(null);
    showToast("Invoice cancelled & stock restored!");
  };

  // ── Expenses CRUD ──────────────────────────────────────────────────────────
  const openAddExp  = () => { setEditingExp(null); setExpForm({title:"",category:expCats[0]?.name||"",amount:"",date:today(),note:""}); setShowExpModal(true); };
  const openEditExp = exp => { setEditingExp(exp.id); setExpForm({...exp}); setShowExpModal(true); };
  const saveExp = () => {
    if(!expForm.title||!expForm.amount) return;
    const p={...expForm,amount:+expForm.amount};
    if(editingExp) setExpenses(expenses.map(e=>e.id===editingExp?{...e,...p}:e));
    else setExpenses([{...p,id:newExpId()},...expenses]);
    setShowExpModal(false); showToast(t.expenseSaved);
  };
  const deleteExp = id => { setExpenses(expenses.filter(e=>e.id!==id)); setConfirmDelExp(null); showToast(t.expenseDeleted); };

  // ── Purchase CRUD ────────────────────────────────────────────────────────────
  const openAddPur  = () => { setEditingPur(null); setPurForm({title:"",brand:"",supplier:"",category:cats[0]?.name||"",qty:"",unit:"Piece",costPerUnit:"",date:today(),note:""}); setShowPurModal(true); };
  const openEditPur = p => { setEditingPur(p.id); setPurForm({...p}); setShowPurModal(true); };
  const savePur = () => {
    if(!purForm.title||!purForm.qty||!purForm.costPerUnit) return;
    const total = +purForm.qty * +purForm.costPerUnit;
    const p = {...purForm, qty:+purForm.qty, costPerUnit:+purForm.costPerUnit, total};
    if(editingPur) setPurchases(purchases.map(x=>x.id===editingPur?{...x,...p}:x));
    else setPurchases([{...p,id:newPurId()},...purchases]);
    setShowPurModal(false); showToast(t.purchaseSaved);
  };
  const deletePur = id => { setPurchases(purchases.filter(p=>p.id!==id)); setConfirmDelPur(null); showToast(t.purchaseDeleted); };

  const filteredPur = useMemo(()=>{
    let ps = filterExpenses(purchases, purPeriod); // reuse date filter logic
    if(purCatFilter!=="all") ps = ps.filter(p=>p.category===purCatFilter);
    if(purSearch) ps = ps.filter(p=>p.title.toLowerCase().includes(purSearch.toLowerCase())||p.brand.toLowerCase().includes(purSearch.toLowerCase())||p.supplier.toLowerCase().includes(purSearch.toLowerCase()));
    return ps.sort((a,b)=>toDate(b.date)-toDate(a.date));
  },[purchases,purPeriod,purCatFilter,purSearch]);

  const purTotal = filteredPur.reduce((s,p)=>s+p.total,0);

  const purSummary = useMemo(()=>({
    daily:   filterExpenses(purchases,"daily"  ).reduce((s,p)=>s+p.total,0),
    weekly:  filterExpenses(purchases,"weekly" ).reduce((s,p)=>s+p.total,0),
    monthly: filterExpenses(purchases,"monthly").reduce((s,p)=>s+p.total,0),
    quarter: filterExpenses(purchases,"quarter").reduce((s,p)=>s+p.total,0),
    annual:  filterExpenses(purchases,"annual" ).reduce((s,p)=>s+p.total,0),
  }),[purchases]);

  // Brand breakdown for purchases
  const purByBrand = useMemo(()=>{
    const fp = filterExpenses(purchases, purPeriod);
    const map = {};
    fp.forEach(p=>{ if(!map[p.brand]) map[p.brand]={brand:p.brand,total:0,count:0,items:[]}; map[p.brand].total+=p.total; map[p.brand].count++; map[p.brand].items.push(p.title); });
    return Object.values(map).sort((a,b)=>b.total-a.total);
  },[purchases,purPeriod]);

  // Category breakdown for purchases
  const purByCat = useMemo(()=>{
    const fp = filterExpenses(purchases, purPeriod);
    const map = {};
    fp.forEach(p=>{ if(!map[p.category]) map[p.category]={cat:p.category,total:0,count:0}; map[p.category].total+=p.total; map[p.category].count++; });
    return Object.values(map).sort((a,b)=>b.total-a.total);
  },[purchases,purPeriod]);

  // ── Expense Category CRUD ──────────────────────────────────────────────────
  const openAddExpCat  = () => { setEditingExpCat(null); setExpCatForm({name:"",icon:"📌",color:COLOR_PALETTE[expCats.length%COLOR_PALETTE.length]}); setShowExpCatModal(true); };
  const openEditExpCat = ec => { setEditingExpCat(ec.id); setExpCatForm({...ec}); setShowExpCatModal(true); };
  const saveExpCat = () => {
    if(!expCatForm.name?.trim()) return;
    if(editingExpCat) {
      const old = expCats.find(c=>c.id===editingExpCat)?.name;
      setExpCats(expCats.map(c=>c.id===editingExpCat?{...c,...expCatForm}:c));
      if(old && old!==expCatForm.name) setExpenses(expenses.map(e=>e.category===old?{...e,category:expCatForm.name}:e));
    } else {
      setExpCats([...expCats,{id:"ec"+Date.now(),...expCatForm}]);
    }
    setShowExpCatModal(false); showToast("Expense category saved!");
  };
  const deleteExpCat = id => {
    const name = expCats.find(c=>c.id===id)?.name;
    setExpCats(expCats.filter(c=>c.id!==id));
    if(name) setExpenses(expenses.map(e=>e.category===name?{...e,category:"Miscellaneous"}:e));
    setConfirmDelExpCat(null); showToast("Expense category deleted!");
  };

  const filteredExp = useMemo(()=>{
    let ex = filterExpenses(expenses, expPeriod);
    if(expCatFilter!=="all") ex = ex.filter(e=>e.category===expCatFilter);
    return ex.sort((a,b)=>toDate(b.date)-toDate(a.date));
  },[expenses,expPeriod,expCatFilter]);

  const expTotal = filteredExp.reduce((s,e)=>s+e.amount,0);

  // Expense period summary cards
  const expSummary = useMemo(()=>({
    daily:   filterExpenses(expenses,"daily"  ).reduce((s,e)=>s+e.amount,0),
    weekly:  filterExpenses(expenses,"weekly" ).reduce((s,e)=>s+e.amount,0),
    monthly: filterExpenses(expenses,"monthly").reduce((s,e)=>s+e.amount,0),
    quarter: filterExpenses(expenses,"quarter").reduce((s,e)=>s+e.amount,0),
    annual:  filterExpenses(expenses,"annual" ).reduce((s,e)=>s+e.amount,0),
  }),[expenses]);

  // Expense by category for the filtered period
  const expByCat = useMemo(()=>expCats.map(ec=>({
    cat:ec.name, color:ec.color, icon:ec.icon,
    total: filterExpenses(expenses,expPeriod).filter(e=>e.category===ec.name).reduce((s,e)=>s+e.amount,0)
  })).filter(x=>x.total>0),[expenses,expPeriod,expCats]);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const todaySalesAmt = sales.filter(s=>s.date===today()).reduce((s,i)=>s+i.total,0);
  const todayProfit   = sales.filter(s=>s.date===today()).reduce((s,b)=>{
    const cost=b.items.reduce((c,bi)=>{const inv=inventory.find(i=>i.name===bi.name||i.nameTa===bi.name);return c+(inv?inv.purchasePrice*bi.qty:0);},0);
    return s+(b.subtotal-cost);
  },0) - expSummary.daily;
  const lowStockItems = inventory.filter(i=>i.stock>0&&i.stock<10);
  const outStockItems = inventory.filter(i=>i.stock===0);

  // eslint-disable-next-line no-unused-vars
  const billItems = useMemo(()=>inventory.filter(i=>
    (billCat==="all"||i.category===billCat)&&
    (billSubCat==="all"||i.subCategory===billSubCat)&&
    ((lang==="en"?i.name:i.nameTa).toLowerCase().includes(billSearch.toLowerCase()))
  ),[inventory,billCat,billSubCat,billSearch,lang]);

  // eslint-disable-next-line no-unused-vars
  const activeBillSubCats = billCat!=="all" ? catSubCats(billCat) : [];
  const activeInvSubCats  = invCat !=="all" ? catSubCats(invCat)  : [];

  const role = currentUser?.role || "owner";
  const rolePerms = loadRolePermissions();
  const nav=[
    {key:"billing",   icon:"🧾", label:t.billing},
    {key:"inventory", icon:"📦", label:t.inventory},
    {key:"purchases", icon:"🛒", label:t.purchases},
    {key:"customers", icon:"👥", label:"Customers"},
    {key:"sales",     icon:"💰", label:"Sales"},
    {key:"categories",icon:"🏷", label:t.categories},
    {key:"expenses",  icon:"💸", label:t.expenses},
    {key:"reports",   icon:"📊", label:t.reports},
  ].filter(n=> role==="owner" || (rolePerms[role]?.[n.key] === true));


  const renderPcBilling = () => {
                const confirmPosItem = item => {
                  setPosQtyMode(item);
                  setPosQtyVal("");
                  setBillSearch("");
                  setPosHighlight(0);
                };

                const confirmPosQty = () => {
                  if(!posQtyMode) return;
                  const qty = Math.max(1, parseInt(posQtyVal)||1);
                  const existing = cart.find(c=>c.id===posQtyMode.id);
                  if(existing) setCart(cart.map(c=>c.id===posQtyMode.id?{...c,qty:c.qty+qty}:c));
                  else setCart([...cart,{...posQtyMode,qty}]);
                  setPosQtyMode(null);
                  setPosQtyVal("");
                  setTimeout(()=>document.getElementById("pos-search")?.focus(),50);
                };

                const handlePosSearchKey = e => {
                  if(e.key==="ArrowDown"){e.preventDefault();setPosHighlight(h=>Math.min(h+1,posResults.length-1));}
                  else if(e.key==="ArrowUp"){e.preventDefault();setPosHighlight(h=>Math.max(h-1,0));}
                  else if(e.key==="Enter"){
                    e.preventDefault();
                    if(posResults.length>0){
                      posSearchEnterRef.current=0;
                      confirmPosItem(posResults[posHighlight]||posResults[0]);
                    } else if(!billSearch.trim()){
                      posSearchEnterRef.current+=1;
                      if(posSearchEnterRef.current>=2){
                        posSearchEnterRef.current=0;
                        setTimeout(()=>document.getElementById("pos-customer")?.focus(),50);
                      }
                    }
                  }
                  else if(e.key==="Escape"){setBillSearch("");setPosHighlight(0);posSearchEnterRef.current=0;}
                  else{posSearchEnterRef.current=0;}
                };

                const handlePosQtyKey = e => {
                  if(e.key==="Enter"){e.preventDefault();confirmPosQty();}
                  else if(e.key==="Escape"){setPosQtyMode(null);setTimeout(()=>document.getElementById("pos-search")?.focus(),50);}
                };

                return (
                <div style={{...card,padding:0,display:"flex",flexDirection:"column",overflow:"hidden",borderRadius:16}}>
                  <div style={{padding:"6px 12px",borderBottom:`1px solid ${th.cardBorder}`,flexShrink:0,background:th.cardBg,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <img src="/logo.png" alt="logo" tabIndex={-1} style={{height:52,width:"auto",objectFit:"contain",flexShrink:0}}/>
                    <button tabIndex={-1} onClick={()=>setTab("dashboard")}
                      style={{padding:"5px 9px",borderRadius:999,border:"none",cursor:"pointer",background:"#1e1e2e",color:"#fff",fontSize:15,flexShrink:0,boxShadow:"0 2px 6px #0003",lineHeight:1}}>
                      🏠
                    </button>
                    <div style={{width:1,height:28,background:th.cardBorder,flexShrink:0}}/>
                    <div style={{fontSize:14,fontWeight:800,color:"#3a3d80",flexShrink:0}}>New Bill</div>
                    <div style={{flex:1}}/>
                    <div style={{display:"flex",gap:5,flexShrink:0,alignItems:"center"}}>
                      {["GST","Non-GST"].map(tp=>(
                        <button tabIndex={-1} key={tp} onClick={()=>setBillType(tp)} style={{padding:"8px 28px",borderRadius:7,border:billType===tp?"none":`1px solid ${th.ghostBorder}`,cursor:"pointer",fontSize:11,fontWeight:700,whiteSpace:"nowrap",background:billType===tp?"linear-gradient(135deg,#3a3d80,#e74c3c)":th.ghostBg,color:billType===tp?"#fff":th.ghostText}}>
                          {tp}
                        </button>
                      ))}
                      <div style={{width:1,height:20,background:th.cardBorder,flexShrink:0}}/>
                      <select tabIndex={-1} value={billInvoiceType} onChange={e=>setBillInvoiceType(e.target.value)}
                        style={{...IS,fontSize:11,fontWeight:600,padding:"4px 8px",cursor:"pointer"}}>
                        {["TAX INVOICE","BILL / RECEIPT","ESTIMATE","DELIVERY NOTE","PROFORMA INVOICE"].map(tp=>(
                          <option key={tp} value={tp}>{tp}</option>
                        ))}
                      </select>
                      <button tabIndex={-1} onClick={()=>{setCart([]);setBillCustomer("");setBillPhone("");setBillDiscount(0);setBillSearch("");}}
                        style={{padding:"5px 10px",borderRadius:7,border:"1px solid #e74c3c44",background:"rgba(231,76,60,.08)",color:"#e74c3c",cursor:"pointer",fontSize:11,fontWeight:700}}>
                        Clear
                      </button>
                      <button tabIndex={-1} onClick={()=>setTab("dashboard")} title="Close billing"
                        style={{width:28,height:28,borderRadius:"50%",border:"none",cursor:"pointer",background:"#e74c3c",color:"#fff",fontSize:14,fontWeight:900,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1,boxShadow:"0 1px 4px #e74c3c55"}}>
                        ✕
                      </button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"32px 2fr 110px 80px 130px 90px 36px",padding:"5px 20px",background:th.appBg,borderBottom:`2px solid ${th.cardBorder}`,position:"sticky",top:0,zIndex:2}}>
                    {["#","Item","Code","MRP","Qty","Total",""].map((h,i)=>(
                      <div key={i} style={{fontSize:11,fontWeight:800,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,textAlign:i>=5?"right":"left"}}>{h}</div>
                    ))}
                  </div>
                  {/* ── Scrollable cart items + search as next line ── */}
                  <div ref={cartScrollRef} style={{flex:1,overflowY:"auto",minHeight:0,position:"relative"}}>
                    {cart.map((item,lineIdx)=>(
                      <div key={item.id}
                        style={{display:"grid",gridTemplateColumns:"32px 2fr 110px 80px 130px 90px 36px",alignItems:"center",padding:"6px 20px",borderBottom:`1px solid ${th.cardBorder}`,transition:"background .12s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=th.tableHover}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{fontSize:11,fontWeight:800,color:"#3a3d8066",textAlign:"center"}}>{lineIdx+1}</div>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{fontSize:14,fontWeight:700,color:th.text}}>{lang==="en"?item.name:item.nameTa}</div>
                          {billType==="GST"&&item.gst>0&&<span style={{fontSize:10,color:"#2980b9",fontWeight:700,background:"rgba(41,128,185,0.1)",borderRadius:4,padding:"1px 5px"}}>+{item.gst}% GST</span>}
                        </div>
                        <div style={{fontSize:12,color:th.textSub,fontFamily:"monospace"}}>{item.itemCode||"-"}</div>
                        <div style={{fontSize:13,fontWeight:600,color:th.text}}>Rs.{item.salePrice}</div>
                        <div style={{display:"flex",alignItems:"center",gap:0}}>
                          <button onClick={()=>updateQty(item.id,item.qty-1)} style={{width:24,height:24,borderRadius:"6px 0 0 6px",border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:15,fontWeight:700,lineHeight:1,flexShrink:0}}>−</button>
                          <input type="text" value={item.qty} onChange={e=>updateQty(item.id,Math.max(1,+e.target.value||1))}
                            style={{...IS,width:34,padding:"2px 2px",textAlign:"center",fontSize:13,fontWeight:800,borderRadius:0,borderLeft:"none",borderRight:"none",MozAppearance:"textfield"}}/>
                          <button onClick={()=>updateQty(item.id,item.qty+1)} style={{width:24,height:24,borderRadius:"0 6px 6px 0",border:"1px solid #3a3d8044",background:"rgba(58,61,128,0.12)",color:"#3a3d80",cursor:"pointer",fontSize:15,fontWeight:700,lineHeight:1,flexShrink:0}}>+</button>
                        </div>
                        <div style={{fontSize:14,fontWeight:800,color:"#3a3d80",textAlign:"right"}}>Rs.{item.salePrice*item.qty}</div>
                        <button onClick={()=>setCart(cart.filter(c=>c.id!==item.id))} title="Remove"
                          style={{background:"none",border:"none",cursor:"pointer",padding:4,display:"flex",alignItems:"center",justifyContent:"center",color:"#e74c3c",opacity:.6,transition:"opacity .15s"}}
                          onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                          onMouseLeave={e=>e.currentTarget.style.opacity=".6"}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>
                    ))}

                    {/* ── Search as next numbered line ── */}
                    {posQtyMode ? (
                      <div style={{display:"grid",gridTemplateColumns:"32px 1fr",alignItems:"center",padding:"7px 20px",borderBottom:`1px solid ${th.cardBorder}`,background:"rgba(58,61,128,0.04)"}}>
                        <div style={{fontSize:11,fontWeight:800,color:"#3a3d80",textAlign:"center"}}>{cart.length+1}</div>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <span style={{fontSize:13,fontWeight:700,color:"#3a3d80"}}>{lang==="en"?posQtyMode.name:posQtyMode.nameTa}</span>
                          <span style={{fontSize:12,color:th.textSub}}>Rs.{posQtyMode.salePrice}</span>
                          <span style={{fontSize:12,color:th.textSub}}>Qty:</span>
                          <input id="pos-qty" tabIndex={6} autoFocus type="number" min="1" value={posQtyVal}
                            onChange={e=>setPosQtyVal(e.target.value)}
                            onKeyDown={handlePosQtyKey}
                            style={{...IS,width:70,padding:"4px 8px",textAlign:"center",fontSize:14,fontWeight:700}}/>
                          <button onClick={confirmPosQty} style={{...btn("#3a3d80"),fontSize:12,padding:"5px 16px"}}>Add</button>
                          <button onClick={()=>{setPosQtyMode(null);setTimeout(()=>document.getElementById("pos-search")?.focus(),50);}}
                            style={{...btn("#e74c3c"),fontSize:12,padding:"5px 12px"}}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{display:"grid",gridTemplateColumns:"32px 1fr",alignItems:"center",padding:"5px 20px 5px 20px",borderBottom:`1px dashed ${th.cardBorder}`,background:"rgba(58,61,128,0.02)",position:"relative"}}>
                        <div style={{fontSize:11,fontWeight:800,color:"#3a3d8055",textAlign:"center"}}>{cart.length+1}</div>
                        <div style={{position:"relative"}}>
                          <input id="pos-search" tabIndex={5} autoComplete="off" value={billSearch}
                            onChange={e=>{setBillSearch(e.target.value);setPosHighlight(0);posSearchEnterRef.current=0;}}
                            onKeyDown={handlePosSearchKey}
                            placeholder="Search item by name, code, brand, category, price…"
                            style={{...IS,width:"100%",padding:"6px 12px",fontSize:13,borderRadius:7,border:`1.5px solid ${billSearch?'#3a3d80':th.inputBorder}`}}/>
                          {posResults.length>0&&(
                            <div style={{position:"fixed",zIndex:9999,background:th.cardBg,border:`1.5px solid ${th.cardBorder}`,borderRadius:10,boxShadow:"0 8px 32px #0003",maxHeight:280,overflowY:"auto",minWidth:500}}
                              ref={el=>{
                                if(el){
                                  const inp=document.getElementById("pos-search");
                                  if(inp){const r=inp.getBoundingClientRect();el.style.left=r.left+"px";el.style.width=r.width+"px";el.style.top=r.bottom+2+"px";}
                                }
                              }}>
                              {posResults.map((item,idx)=>(
                                <div key={item.id} onMouseDown={()=>confirmPosItem(item)}
                                  style={{display:"grid",gridTemplateColumns:"2fr 90px 70px 60px",alignItems:"center",padding:"7px 14px",cursor:"pointer",background:idx===posHighlight?"rgba(58,61,128,0.12)":"transparent",borderBottom:idx<posResults.length-1?`1px solid ${th.cardBorder}`:"none",transition:"background .1s"}}
                                  onMouseEnter={()=>setPosHighlight(idx)}>
                                  <div style={{fontSize:13,fontWeight:600,color:th.text}}>{lang==="en"?item.name:item.nameTa}</div>
                                  <div style={{fontSize:11,color:th.textSub,fontFamily:"monospace"}}>{item.itemCode||"-"}</div>
                                  <div style={{fontSize:13,fontWeight:700,color:"#3a3d80"}}>Rs.{item.salePrice}</div>
                                  <div style={{fontSize:11,color:item.stock<5?"#e74c3c":"#27ae60",fontWeight:700,textAlign:"right"}}>Qty:{item.stock}</div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{borderTop:`2px solid ${th.cardBorder}`,flexShrink:0,background:th.cardBg}}>
                    <div style={{padding:"10px 20px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"flex-start",gap:20}}>
                      {/* Left — customer & payment */}
                      <div style={{flex:1,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                        <div style={{position:"relative",flexShrink:0}}>
                          <input id="pos-customer" tabIndex={1} value={billCustomer}
                            onChange={e=>setBillCustomer(e.target.value)}
                            onKeyDown={e=>{
                              if(e.key==="Enter"){e.preventDefault();document.getElementById("pos-phone")?.focus();}
                              else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();document.getElementById("pos-search")?.focus();}
                            }}
                            placeholder="Customer name" style={{...IS,width:220,fontSize:12,padding:"6px 10px"}}/>
                          {billCustomer.trim().length>1&&(()=>{
                            const matches = customers.filter(c=>
                              c.name.toLowerCase().includes(billCustomer.toLowerCase())||
                              (c.phone&&c.phone.includes(billCustomer))
                            ).slice(0,6);
                            if(!matches.length) return null;
                            return <div style={{position:"absolute",top:"100%",left:0,zIndex:200,background:"#fff",border:"1px solid #ddd",borderRadius:8,boxShadow:"0 8px 24px #0002",minWidth:260,marginTop:2}}>
                              {matches.map(c=>(
                                <div key={c.id} onMouseDown={()=>{setBillCustomer(c.name);setBillPhone(c.phone||"");}}
                                  style={{padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid #eee"}}
                                  onMouseEnter={e=>e.currentTarget.style.background="#f5f5f5"}
                                  onMouseLeave={e=>e.currentTarget.style.background=""}>
                                  <div style={{fontSize:13,fontWeight:700}}>{c.name}</div>
                                  <div style={{fontSize:10,color:"#888"}}>{c.phone||""}{c.address?` · ${c.address}`:""}</div>
                                </div>
                              ))}
                            </div>;
                          })()}
                        </div>
                        <input id="pos-phone" tabIndex={2} value={billPhone} onChange={e=>setBillPhone(e.target.value)}
                          onKeyDown={e=>{
                            if(e.key==="Enter"){e.preventDefault();document.getElementById("pos-payment-group")?.focus();}
                            else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();document.getElementById("pos-customer")?.focus();}
                          }}
                          placeholder="Phone" style={{...IS,width:220,flexShrink:0,fontSize:12,padding:"6px 10px"}}/>
                        <div style={{width:1,height:28,background:th.cardBorder,flexShrink:0}}/>
                        <div style={{display:"flex",flexDirection:"column",gap:4}}>
                          <span style={{fontSize:10,fontWeight:800,color:th.textSub,textTransform:"uppercase",letterSpacing:.8}}>Payment Method</span>
                          <select id="pos-payment-group" tabIndex={3} value={billPayment} onChange={e=>setBillPayment(e.target.value)}
                            onKeyDown={e=>{
                              if(e.key==="Enter"){e.preventDefault();document.getElementById("pos-discount")?.focus();}
                              else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();document.getElementById("pos-phone")?.focus();}
                            }}
                            style={{...IS,fontSize:13,fontWeight:600,padding:"6px 10px",width:140,cursor:"pointer"}}>
                            <option value="Cash">{t.cash}</option>
                            <option value="UPI">{t.upi}</option>
                            <option value="Card">{t.card}</option>
                            <option value="Credit">{t.credit}</option>
                          </select>
                        </div>
                      </div>
                      {/* Right — totals */}
                      <div style={{display:"grid",gridTemplateColumns:"auto auto",columnGap:24,rowGap:4,alignItems:"center",flexShrink:0}}>
                        <span style={{fontSize:13,color:th.textSub,textAlign:"right"}}>Subtotal</span>
                        <span style={{fontSize:13,fontWeight:700,color:th.text,textAlign:"right"}}>{fmt(cartSubtotal)}</span>
                        {billType==="GST"&&<><span style={{fontSize:13,color:th.textSub,textAlign:"right"}}>GST</span><span style={{fontSize:13,fontWeight:700,color:"#2980b9",textAlign:"right"}}>+{fmt(cartGST)}</span></>}
                        <span style={{fontSize:13,color:th.textSub,textAlign:"right"}}>Discount (Rs.)</span>
                        <input id="pos-discount" tabIndex={4} type="number" value={billDiscount} onChange={e=>setBillDiscount(e.target.value)} min="0"
                          onKeyDown={e=>{
                            if(e.key==="Enter"){e.preventDefault();document.getElementById("pos-save")?.focus();}
                            else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();document.getElementById("pos-payment-group")?.focus();}
                          }}
                          style={{...IS,width:72,padding:"3px 8px",textAlign:"right",fontSize:13,fontWeight:600}}/>
                        <div style={{gridColumn:"1/-1",borderTop:`1px dashed ${th.cardBorder}`,margin:"4px 0"}}/>
                        <span style={{fontSize:15,fontWeight:800,color:th.textHeading,textAlign:"right"}}>Grand Total</span>
                        <span style={{fontSize:26,fontWeight:900,color:"#3a3d80",textAlign:"right"}}>{fmt(cartTotal)}</span>
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 20px"}}>
                      <div style={{display:"flex",gap:7,marginLeft:"auto",flexShrink:0}}>
                        <button id="pos-save" tabIndex={7} onClick={()=>saveBill(null)} disabled={!cart.length}
                          onKeyDown={e=>{
                            if(e.key==="Enter"){e.preventDefault();if(cart.length)saveBill(null);}
                            else if(e.key==="Tab"&&e.shiftKey){e.preventDefault();document.getElementById("pos-discount")?.focus();}
                          }}
                          style={{...btn("#27ae60"),fontSize:14,fontWeight:800,padding:"9px 28px",opacity:cart.length?1:.4}}>Generate Bill</button>
                      </div>
                    </div>
                  </div>
                </div>
                );
  };

  if (printHtml) return <PrintOverlay html={printHtml} onClose={()=>setPrintHtml(null)}/>;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100vh",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)",background:th.appBg,color:th.text,fontFamily:"'Segoe UI',system-ui,sans-serif",fontSize:"14.5px",overflow:"hidden"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Playfair+Display:wght@700;800&display=swap');
        ::-webkit-scrollbar{width:6px;height:6px}
        ::-webkit-scrollbar-track{background:${th.scrollTrack}}
        ::-webkit-scrollbar-thumb{background:${th.scrollThumb};border-radius:10px}
        @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        input,select,textarea{font-size:14px!important;font-weight:500!important}
        input:focus,select:focus{border-color:#3a3d80!important;outline:none}
        button:active{transform:scale(.97)}
        "body *{letter-spacing:0.01em} td,th{font-size:13.5px!important}"
        ${isMobileView?".mob-scroll-x{overflow-x:auto;-webkit-overflow-scrolling:touch} .mob-scroll-x table{min-width:700px}":""}
      `}</style>

      {/* ── DESKTOP TOP NAV ── */}
      {!isMobileView&&tab!=="billing"&&(
      <div style={{position:"fixed",top:0,left:0,right:0,zIndex:200,background:th.topbarBg,borderBottom:`1.5px solid ${th.topbarBorder}`,display:"flex",alignItems:"center",height:"calc(74px + env(safe-area-inset-top))",paddingTop:"env(safe-area-inset-top)",padding:`env(safe-area-inset-top) 20px 0 20px`,gap:0,boxShadow:"0 2px 10px rgba(58,61,128,0.07)",flexShrink:0}}>
        {/* Logo */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginRight:16,flexShrink:0}}>
          <img src="/logo.png" alt="Nethran Crackers" style={{height:70,width:"auto",objectFit:"contain"}} />
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#111",whiteSpace:"nowrap"}}>{storeName||"Main Branch"}</div>
            <div style={{fontSize:10,color:"#3a3d80",letterSpacing:0.8,textTransform:"uppercase"}}>Shop Manager</div>
          </div>
        </div>

        {/* Home button */}
        <button onClick={()=>setTab("dashboard")}
          style={{padding:"6px 10px",borderRadius:999,border:"none",cursor:"pointer",background:tab==="dashboard"?"#3a3d80":"#1e1e2e",color:"#fff",fontSize:16,marginRight:8,flexShrink:0,boxShadow:"0 2px 8px #0003",lineHeight:1}}>
          🏠
        </button>
        {/* Nav items */}
        <div style={{display:"flex",alignItems:"center",gap:1,minWidth:0,overflow:"hidden",flex:"0 1 auto"}}>
          {nav.map(n=>(
            <button key={n.key} onClick={()=>setTab(n.key)}
              style={{padding:"5px 9px",borderRadius:999,border:"none",cursor:"pointer",fontSize:12.5,fontWeight:tab===n.key?700:500,background:tab===n.key?"#3a3d80":"transparent",color:tab===n.key?"#ffffff":"#444",transition:"all .2s",whiteSpace:"nowrap",flexShrink:0,boxShadow:tab===n.key?"0 2px 10px rgba(58,61,128,0.25)":"none"}}>
              {n.label}
            </button>
          ))}
        </div>
        {/* Invoices button */}
        <button onClick={()=>setShowInvHistory(true)}
          style={{display:"flex",alignItems:"center",gap:5,padding:"6px 16px",borderRadius:999,border:"none",cursor:"pointer",fontSize:12.5,fontWeight:500,background:"transparent",color:"#444",transition:"all .2s",whiteSpace:"nowrap",flexShrink:0}}
          onMouseEnter={e=>e.currentTarget.style.background="rgba(58,61,128,0.08)"}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          Invoices
          <span style={{fontSize:10,padding:"1px 6px",borderRadius:10,background:"rgba(58,61,128,0.10)",color:"#3a3d80",fontWeight:700,marginLeft:2}}>{sales.length}</span>
        </button>
        <div style={{flex:1}}/>

        {/* Right side */}
        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
          <button onClick={()=>setShowSettings(true)} style={{padding:"5px 8px",borderRadius:8,border:"none",background:"#2c2c3e",color:"#fff",cursor:"pointer",fontSize:13,lineHeight:1}}>⚙️</button>
          <div style={{position:"relative"}}>
            <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 8px",borderRadius:8,border:`1px solid ${th.cardBorder}`,background:th.cardBg,cursor:"pointer"}} onClick={()=>setShowUserMenu(o=>!o)}>
              <div style={{width:24,height:24,borderRadius:"50%",background:"linear-gradient(135deg,#3a3d80,#5558b0)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>{(currentUser?.name||"A")[0].toUpperCase()}</div>
              <div style={{lineHeight:1.2}}>
                <div style={{fontSize:11.5,fontWeight:600,color:"#111",whiteSpace:"nowrap"}}>{currentUser?.name||"Admin"}</div>
                <div style={{fontSize:9.5,color:"#3a3d80",textTransform:"capitalize"}}>{currentUser?.role||"owner"}</div>
              </div>
              <span style={{fontSize:10,color:"#888",marginLeft:2}}>{showUserMenu?"▲":"▼"}</span>
            </div>
            {showUserMenu&&(
              <div style={{position:"absolute",top:"calc(100% + 6px)",right:0,minWidth:160,background:th.modalBg,border:`1px solid ${th.cardBorder}`,borderRadius:12,boxShadow:"0 8px 32px #0003",zIndex:500,overflow:"hidden"}}>
                <div style={{padding:"12px 14px",borderBottom:`1px solid ${th.cardBorder}`}}>
                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>{currentUser?.name||"Admin"}</div>
                  <div style={{fontSize:11,color:"#3a3d80",textTransform:"capitalize"}}>{currentUser?.role||"owner"}</div>
                </div>
                {currentUser?.role==="owner"&&(
                  <button onClick={()=>{setShowUserMenu(false);onSwitchStore();}}
                    style={{width:"100%",padding:"12px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:"#3a3d80",textAlign:"left",borderBottom:`1px solid ${th.cardBorder}`}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(58,61,128,0.07)"}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    🏪 Switch Store
                  </button>
                )}
                {currentUser?.role==="owner"&&(
                  <button onClick={()=>{setShowUserMenu(false);onManageUsers();}}
                    style={{width:"100%",padding:"12px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:th.text,textAlign:"left",borderBottom:`1px solid ${th.cardBorder}`}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(58,61,128,0.07)"}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    👥 Manage Users
                  </button>
                )}
                {currentUser?.role==="owner"&&(
                  <button onClick={()=>{setShowUserMenu(false);setShowOwnerAnalytics(true);}}
                    style={{width:"100%",padding:"12px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:600,color:"#3a3d80",textAlign:"left",borderBottom:`1px solid ${th.cardBorder}`}}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(58,61,128,0.07)"}
                    onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    📊 Owner Analytics
                  </button>
                )}
                <button onClick={()=>{setShowUserMenu(false);onLogout();}}
                  style={{width:"100%",padding:"12px 14px",border:"none",background:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:13,fontWeight:700,color:"#c0392b",textAlign:"left"}}
                  onMouseEnter={e=>e.currentTarget.style.background="rgba(192,57,43,0.08)"}
                  onMouseLeave={e=>e.currentTarget.style.background="none"}>
                  🚪 Log Off
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* ── MOBILE TOP BAR ── */}
      {isMobileView&&(
        <div style={{position:"relative",zIndex:300,flexShrink:0}}>
          <div style={{background:th.topbarBg,borderBottom:`1px solid ${th.topbarBorder}`,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
            {/* Burger */}
            <button onClick={()=>{
              setMobileMenuOpen(o=>{
                if(!o){
                  clearTimeout(mobileMenuTimer[0]);
                  mobileMenuTimer[0]=setTimeout(()=>setMobileMenuOpen(false),10000);
                } else { clearTimeout(mobileMenuTimer[0]); }
                return !o;
              });
            }} style={{background:"none",border:"none",color:th.textHeading,cursor:"pointer",fontSize:22,lineHeight:1,padding:"2px 4px"}}>
              {mobileMenuOpen?"✕":"☰"}
            </button>
            <button onClick={()=>{setTab("dashboard");setMobileMenuOpen(false);}} style={{background:tab==="dashboard"?"#3a3d80":"none",border:"none",color:tab==="dashboard"?"#fff":th.textHeading,cursor:"pointer",fontSize:18,lineHeight:1,padding:"4px 7px",borderRadius:8,flexShrink:0}}>🏠</button>
            <img src="/logo.png" alt="Nethran" style={{height:34,width:"auto",objectFit:"contain",flexShrink:0}}/>
            <div style={{flex:1,fontSize:14,fontWeight:800,color:th.textHeading,fontFamily:"'Raleway',sans-serif"}}>{t.appName}</div>
            {fbUser && cloudSynced && <span style={{fontSize:9,color:"#27ae60",fontWeight:700,letterSpacing:.5,flexShrink:0}}>● LIVE</span>}
            <button onClick={()=>setLang(lang==="en"?"ta":"en")} style={{background:"none",border:"none",fontSize:16,cursor:"pointer",color:th.textSub}}>🌐</button>
          </div>
          {/* Dropdown menu */}
          {mobileMenuOpen&&(
            <div style={{position:"absolute",top:"100%",left:0,right:0,background:th.topbarBg,borderBottom:`2px solid #3a3d80`,boxShadow:"0 8px 24px #0003",zIndex:400}}>
              {nav.map(n=>(
                <button key={n.key} onClick={()=>{setTab(n.key);setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:tab===n.key?"rgba(247,147,26,.08)":"transparent",color:tab===n.key?"#3a3d80":th.text,cursor:"pointer",fontSize:14,fontWeight:tab===n.key?700:500,textAlign:"left"}}>
                  <span style={{fontSize:20}}>{n.icon}</span>
                  <span>{n.label}</span>
                  {tab===n.key&&<span style={{marginLeft:"auto",width:8,height:8,borderRadius:"50%",background:"#3a3d80",flexShrink:0}}/>}
                </button>
              ))}

              <button onClick={()=>{setShowInvHistory(true);setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:"transparent",color:th.text,cursor:"pointer",fontSize:14,fontWeight:500,textAlign:"left"}}>
                <span style={{fontSize:20}}>🧾</span>
                <span>Invoice History</span>
                <span style={{marginLeft:"auto",fontSize:11,padding:"3px 9px",borderRadius:10,background:"rgba(58,61,128,0.12)",color:"#3a3d80",fontWeight:700}}>{sales.length}</span>
              </button>
              {currentUser?.role==="owner"&&(
                <button onClick={()=>{setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);onSwitchStore();}}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:"transparent",color:"#3a3d80",cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}}>
                  <span style={{fontSize:20}}>🏪</span>
                  <span>Switch Store</span>
                </button>
              )}
              <button onClick={()=>{setShowSettings(true);setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:"transparent",color:th.text,cursor:"pointer",fontSize:14,fontWeight:500,textAlign:"left"}}>
                <span style={{fontSize:20}}>⚙️</span>
                <span>Settings</span>
                <span style={{marginLeft:"auto",fontSize:11,padding:"3px 9px",borderRadius:10,background:"rgba(41,128,185,.12)",color:"#2980b9",fontWeight:700}}>Defaults</span>
              </button>
              {fbUser
                ? <div style={{display:"flex",alignItems:"center",gap:14,padding:"11px 20px",borderBottom:`1px solid ${th.topbarBorder}`}}>
                    <span style={{width:8,height:8,borderRadius:"50%",background:"#27ae60",flexShrink:0}}/>
                    <span style={{fontSize:13,color:"#27ae60",fontWeight:600}}>Cloud Connected</span>
                    <span style={{marginLeft:"auto",fontSize:11,color:th.textSub,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{fbUser.email}</span>
                  </div>
                : <button onClick={()=>{setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);onConnectCloud&&onConnectCloud();}}
                    style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:"transparent",color:"#2980b9",cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}}>
                    <span style={{fontSize:20}}>☁️</span>
                    <span>Connect to Cloud</span>
                  </button>
              }
              <button onClick={()=>{setMobileMenuOpen(false);clearTimeout(mobileMenuTimer[0]);onLogout();}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:14,padding:"13px 20px",border:"none",borderBottom:`1px solid ${th.topbarBorder}`,background:"transparent",color:"#e74c3c",cursor:"pointer",fontSize:14,fontWeight:600,textAlign:"left"}}>
                <span style={{fontSize:20}}>🚪</span>
                <span>Logout</span>
              </button>
              <div style={{padding:"10px 20px",fontSize:11,color:th.textSub,textAlign:"center"}}>Auto-closes in 10s · Tap ✕ to close</div>
            </div>
          )}
        </div>
      )}

      {/* ── MAIN ── */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",minHeight:0}}>
        {!isMobileView&&tab!=="billing"&&<div style={{height:"calc(74px + env(safe-area-inset-top))",flexShrink:0}}/>}


        <div style={{flex:1,overflowY:"auto",overflowX:"hidden",padding:isMobileView?"10px 10px 16px":"3px 24px"}}>

          {/* ════ DASHBOARD ════ */}
          {tab==="dashboard"&&(
            <div>
              <div style={{marginBottom:14,paddingBottom:10,borderBottom:`1.5px solid ${th.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontSize:10,fontWeight:600,color:"#3a3d80",letterSpacing:1.2,textTransform:"uppercase"}}>Welcome back</span>
                    <span style={{fontSize:16,fontWeight:800,color:th.textHeading,fontFamily:"'Playfair Display',Georgia,serif"}}>Good day, {currentUser?.name||"Admin"} 👋</span>
                  </div>
                  <div style={{fontSize:11,color:th.textSub,marginTop:4}}>Here's your shop overview for today</div>
                </div>
                <div style={{display:"flex",gap:isMobileView?8:16,flexShrink:0,width:isMobileView?"100%":undefined}}>
                  <div onClick={()=>setTab("sales")} style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1.5px solid #3a3d8033`,cursor:"pointer",flex:isMobileView?1:undefined,minWidth:isMobileView?undefined:260,height:40}}>
                    <div style={{width:"35%",background:"#3a3d80",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 8px"}}>
                      <span style={{fontSize:10,fontWeight:800,color:"#fff",textTransform:"uppercase",letterSpacing:0.5,textAlign:"center",lineHeight:1.3}}>2026{"\n"}Sales</span>
                    </div>
                    <div style={{flex:1,background:th.cardBg,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:isMobileView?14:18,fontWeight:900,color:"#3a3d80"}}>{fmt(sales.filter(s=>s.date&&s.date.startsWith("2026")).reduce((a,s)=>a+s.total,0))}</span>
                    </div>
                  </div>
                  <div onClick={()=>setTab("purchases")} style={{display:"flex",borderRadius:10,overflow:"hidden",border:`1.5px solid #e67e2233`,cursor:"pointer",flex:isMobileView?1:undefined,minWidth:isMobileView?undefined:260,height:40}}>
                    <div style={{width:"35%",background:"#e67e22",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 8px"}}>
                      <span style={{fontSize:10,fontWeight:800,color:"#fff",textTransform:"uppercase",letterSpacing:0.5,textAlign:"center",lineHeight:1.3}}>2026{"\n"}Purchases</span>
                    </div>
                    <div style={{flex:1,background:th.cardBg,display:"flex",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:isMobileView?14:18,fontWeight:900,color:"#e67e22"}}>{fmt(purSummary.annual)}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobileView?"repeat(2,1fr)":"repeat(4,1fr)",gap:isMobileView?10:16,marginBottom:isMobileView?12:22}}>
                <StatCard label={t.todaySales}  value={fmt(todaySalesAmt)} icon="💰" color="#3a3d80" sub={`${sales.filter(s=>s.date===today()).length} transactions`} th={th}/>
                <StatCard label={t.totalItems}  value={inventory.length}   icon="📦" color="#2980b9" sub={`${cats.length} categories`} th={th}/>
                <StatCard label={t.lowStock}    value={lowStockItems.length+outStockItems.length} icon="⚠️" color="#e74c3c" sub={`${outStockItems.length} out of stock`} th={th}/>
                <StatCard label={t.todayProfit} value={fmt(Math.max(0,todayProfit))} icon="📈" color="#27ae60" sub="After today's expenses" th={th}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobileView?"1fr":"1.3fr 1fr",gap:isMobileView?10:16,marginBottom:16}}>
                <div style={{...card,padding:20}}>
                  <div style={{fontSize:14,fontWeight:700,color:th.textHeading,marginBottom:16}}>{t.recentTx}</div>
                  {sales.slice(0,6).map(tx=>(
                    <div key={tx.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px solid ${th.cardBorder}`}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:600,color:th.text}}>{tx.customer}</div>
                        <div style={{fontSize:11,color:th.textSub}}>{tx.id} · {tx.time} · <span style={{color:tx.type==="GST"?"#2980b9":th.textSub}}>{tx.type}</span></div>
                      </div>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:14,fontWeight:700,color:"#3a3d80"}}>{fmt(tx.total)}</div>
                        <span style={tag(tx.payment==="Credit"?"#e74c3c":"#27ae60")}>{tx.payment}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <div style={{...card,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:12}}>💸 Today's Expenses</div>
                    {filterExpenses(expenses,"daily").length===0
                      ? <div style={{fontSize:12,color:th.textSub,textAlign:"center",padding:12}}>No expenses today</div>
                      : filterExpenses(expenses,"daily").slice(0,3).map(e=>(
                        <div key={e.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${th.cardBorder}`,fontSize:12}}>
                          <span style={{color:th.text}}>{expCatIcon(e.category)} {e.title}</span>
                          <span style={{color:"#e74c3c",fontWeight:700}}>-{fmt(e.amount)}</span>
                        </div>
                      ))
                    }
                    <button onClick={()=>setTab("expenses")} style={{...ghost,width:"100%",marginTop:10,fontSize:11,padding:"6px 0",textAlign:"center"}}>View All Expenses →</button>
                  </div>
                  <div style={{...card,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:12}}>{t.stockAlerts} ⚠️</div>
                    <div style={{maxHeight:200,overflowY:"auto"}}>
                      {[...outStockItems,...lowStockItems].map(item=>(
                        <div key={item.id} style={{display:"flex",justifyContent:"space-between",padding:"6px 10px",borderRadius:8,marginBottom:5,background:item.stock===0?"rgba(231,76,60,.08)":"rgba(247,147,26,.07)",border:`1px solid ${item.stock===0?"rgba(231,76,60,.22)":"rgba(58,61,128,0.15)"}`}}>
                          <div style={{fontSize:12,color:th.text,fontWeight:500}}>{lang==="en"?item.name:item.nameTa}</div>
                          <div style={{fontSize:14,fontWeight:800,color:item.stock===0?"#e74c3c":"#3a3d80"}}>{item.stock}</div>
                        </div>
                      ))}
                      {!lowStockItems.length&&!outStockItems.length&&<div style={{textAlign:"center",color:"#27ae60",fontSize:12,padding:12}}>✅ All well stocked</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════ BILLING ════ */}
          {tab==="billing"&&(
            <div style={{display:"grid",gridTemplateColumns:"1fr",gap:16,height:isMobileView?"calc(100vh - 130px)":"calc(100vh - 10px)"}}>

              {/* ── MOBILE TOUCH BILLING ── */}
              {isMobileView ? (
                <div style={{...card,overflow:"hidden",display:"flex",flexDirection:"column",height:"100%"}}>
                  {/* Breadcrumb */}
                  <div style={{padding:"10px 14px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                    {touchStep!=="category"&&(
                      <button onClick={()=>{
                        if(touchStep==="items"&&touchSubCat&&touchSubCat!=="all"){setTouchStep("subcategory");setTouchSubCat(null);}
                        else if(touchStep==="items"){setTouchStep("category");setTouchCat(null);setTouchSubCat(null);}
                        else if(touchStep==="subcategory"){setTouchStep("category");setTouchCat(null);setTouchSubCat(null);}
                      }} style={{background:"none",border:"none",fontSize:18,color:th.textSub,cursor:"pointer",padding:"2px 4px",lineHeight:1,flexShrink:0}}>←</button>
                    )}
                    <button onClick={()=>{setTouchStep("category");setTouchCat(null);setTouchSubCat(null);}}
                      style={{background:"none",border:"none",color:touchStep==="category"?th.textHeading:th.textSub,cursor:"pointer",fontSize:14,fontWeight:touchStep==="category"?800:400,padding:"4px 0"}}>
                      Categories
                    </button>
                    {touchCat&&<><span style={{color:th.textSub}}>›</span>
                    <button onClick={()=>{setTouchStep("subcategory");setTouchSubCat(null);}}
                      style={{background:"none",border:"none",color:touchStep==="subcategory"?th.textHeading:th.textSub,cursor:"pointer",fontSize:14,fontWeight:touchStep==="subcategory"?800:400,padding:"4px 0"}}>
                      {touchCat}
                    </button></>}
                    {touchSubCat&&<><span style={{color:th.textSub}}>›</span>
                    <span style={{fontSize:14,fontWeight:800,color:"#3a3d80"}}>{touchSubCat}</span></>}
                    <div style={{flex:1}}/>
                    {cart.length>0&&<div style={{background:"#3a3d80",color:"#fff",borderRadius:20,padding:"3px 10px",fontSize:12,fontWeight:700}}>{cart.length} items</div>}
                  </div>

                  <div style={{flex:1,overflowY:"auto",padding:14}}>
                    {/* Step 1: Categories - full width equal cards */}
                    {touchStep==="category"&&(
                      <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
                        {cats.map(cat=>(
                          <button key={cat.id} onClick={()=>{
                            setTouchCat(cat.name);
                            const subs = cat.subCategories||[];
                            if(subs.length===0){setTouchSubCat("all");setTouchStep("items");}
                            else if(subs.length===1){setTouchSubCat(subs[0].name);setTouchStep("items");}
                            else setTouchStep("subcategory");
                          }} style={{background:th.cardBg,border:`2px solid ${cat.color}44`,borderRadius:16,padding:"16px 12px",cursor:"pointer",textAlign:"center",transition:"all .18s",display:"flex",flexDirection:"column",alignItems:"center",gap:10}}
                          onTouchStart={e=>e.currentTarget.style.transform="scale(0.96)"}
                          onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
                            <div style={{width:60,height:60,borderRadius:16,background:cat.image?`url(${cat.image}) center/cover no-repeat`:`${cat.color}18`,border:`2px solid ${cat.color}44`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden",flexShrink:0}}>
                              {!cat.image&&<span style={{fontSize:28,fontWeight:900,color:cat.color}}>{cat.icon||cat.name.charAt(0)}</span>}
                            </div>
                            <div>
                              <div style={{fontSize:15,fontWeight:800,color:th.textHeading,lineHeight:1.3}}>{cat.name}</div>
                              <div style={{fontSize:11,color:th.textSub,marginTop:2}}>{inventory.filter(i=>i.category===cat.name&&i.stock>0).length} items</div>
                            </div>
                          </button>
                        ))}
                        {/* Back to Main Menu — spans full width */}
                        <button onClick={()=>setTab("dashboard")}
                          style={{gridColumn:"1/-1",padding:"14px 0",borderRadius:14,border:`2px solid ${th.cardBorder}`,background:th.cardBg,color:th.textSub,cursor:"pointer",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                          ← Main Menu
                        </button>
                      </div>
                    )}

                    {/* Step 2: Sub-categories - 3 per row */}
                    {touchStep==="subcategory"&&touchCat&&(()=>{
                      const subs = catSubCats(touchCat);
                      const cc = catColor(touchCat);
                      return (
                        <div>
                          {/* All items button - full width */}
                          <button onClick={()=>{setTouchSubCat("all");setTouchStep("items");}}
                            style={{width:"100%",background:`${cc}10`,border:`2px solid ${cc}55`,borderRadius:14,padding:"14px 20px",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
                            <span style={{fontSize:22}}>📦</span>
                            <div>
                              <div style={{fontSize:15,fontWeight:700,color:cc}}>All {touchCat} Items</div>
                              <div style={{fontSize:12,color:th.textSub}}>{inventory.filter(i=>i.category===touchCat&&i.stock>0).length} items available</div>
                            </div>
                            <span style={{marginLeft:"auto",fontSize:18,color:`${cc}88`}}>›</span>
                          </button>
                          {/* Sub-category grid - 3 per row */}
                          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                            {subs.map(sc=>{
                              const cnt=inventory.filter(i=>i.category===touchCat&&i.subCategory===sc.name&&i.stock>0).length;
                              return (
                                <button key={sc.id} onClick={()=>{setTouchSubCat(sc.name);setTouchStep("items");}}
                                  style={{background:th.cardBg,border:`2px solid ${cc}33`,borderRadius:14,padding:"14px 10px",cursor:"pointer",textAlign:"center",opacity:cnt===0?.4:1,transition:"all .15s"}}
                                  onTouchStart={e=>e.currentTarget.style.transform="scale(0.94)"}
                                  onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
                                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading,lineHeight:1.3}}>{sc.name}</div>
                                  <div style={{fontSize:11,color:th.textSub,marginTop:4}}>{cnt} items</div>
                                </button>
                              );
                            })}
                          </div>
                          {/* Back to Categories */}
                          <button onClick={()=>{setTouchStep("category");setTouchCat(null);setTouchSubCat(null);}}
                            style={{width:"100%",marginTop:12,padding:"14px 0",borderRadius:14,border:`2px solid ${th.cardBorder}`,background:th.cardBg,color:th.textSub,cursor:"pointer",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                            ← Back to Categories
                          </button>
                        </div>
                      );
                    })()}

                    {/* Step 3: Items */}
                    {touchStep==="items"&&touchCat&&(()=>{
                      const subs3 = catSubCats(touchCat);
                      const cc3 = catColor(touchCat);
                      const items = inventory.filter(i=>
                        i.category===touchCat &&
                        (touchSubCat==="all"||i.subCategory===touchSubCat)
                      );
                      return (
                        <div style={{display:"flex",flexDirection:"column",gap:10}}>
                          {/* Sub-category horizontal scroll bar */}
                          {subs3.length>1&&(
                            <div style={{display:"flex",gap:8,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:2,flexShrink:0}}>
                              <button onClick={()=>setTouchSubCat("all")} style={{flexShrink:0,padding:"7px 16px",borderRadius:20,border:`1.5px solid ${touchSubCat==="all"?cc3:`${cc3}55`}`,background:touchSubCat==="all"?cc3:"transparent",color:touchSubCat==="all"?"#fff":th.text,cursor:"pointer",fontSize:12,fontWeight:700,whiteSpace:"nowrap"}}>All</button>
                              {subs3.map(sc=>{
                                const active=touchSubCat===sc.name;
                                return (
                                  <button key={sc.id} onClick={()=>setTouchSubCat(sc.name)} style={{flexShrink:0,padding:"7px 16px",borderRadius:20,border:`1.5px solid ${active?cc3:`${cc3}55`}`,background:active?cc3:"transparent",color:active?"#fff":th.text,cursor:"pointer",fontSize:12,fontWeight:active?700:500,whiteSpace:"nowrap"}}>
                                    {sc.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                          {items.map(item=>{
                            const inCart = cart.find(c=>c.id===item.id);
                            return (
                              <button key={item.id} onClick={()=>{
                                if(item.stock===0) return;
                                setQtyModal(item); setQtyInput("1");
                              }}
                                disabled={item.stock===0}
                                style={{background:th.cardBg,border:`2px solid ${inCart?"#3a3d80":item.stock===0?"#e74c3c33":th.cardBorder}`,borderRadius:16,padding:0,cursor:item.stock===0?"not-allowed":"pointer",textAlign:"left",color:th.text,opacity:item.stock===0?.4:1,overflow:"hidden",position:"relative",transition:"all .18s"}}
                                onTouchStart={e=>{if(item.stock>0)e.currentTarget.style.transform="scale(0.96)"}}
                                onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
                                {/* Product image */}
                                <div style={{width:"100%",height:80,background:item.image?`url(${item.image}) center/cover no-repeat`:`${catColor(item.category)}12`,display:"flex",alignItems:"center",justifyContent:"center",borderBottom:`1px solid ${th.cardBorder}`}}>
                                  {!item.image&&<span style={{fontSize:26,opacity:.4}}>{catIcon(item.category)||"📦"}</span>}
                                </div>
                                {inCart&&<div style={{position:"absolute",top:6,right:6,background:"#3a3d80",color:"#fff",borderRadius:20,padding:"2px 8px",fontSize:11,fontWeight:800}}>×{inCart.qty}</div>}
                                <div style={{padding:"8px 8px"}}>
                                  <div style={{fontSize:12,fontWeight:700,color:th.text,lineHeight:1.3,marginBottom:3}}>{lang==="en"?item.name:item.nameTa}</div>
                                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:2}}>
                                    <span style={{fontSize:14,fontWeight:900,color:"#3a3d80"}}>₹{item.salePrice}</span>
                                    <span style={{fontSize:10,color:item.stock===0?"#e74c3c":item.stock<10?"#e67e22":"#27ae60",fontWeight:600}}>{item.stock===0?"Out":item.stock<10?`${item.stock}`:`✓`}</span>
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                          {/* Back button at bottom of items */}
                          <button
                            onClick={()=>{
                              if(touchSubCat&&touchSubCat!=="all"){setTouchStep("subcategory");setTouchSubCat(null);}
                              else{setTouchStep("category");setTouchCat(null);setTouchSubCat(null);}
                            }}
                            style={{gridColumn:"1/-1",padding:"14px 0",borderRadius:14,border:`2px solid ${th.cardBorder}`,background:th.cardBg,color:th.textSub,cursor:"pointer",fontSize:14,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:4}}>
                            ← Back to {touchSubCat&&touchSubCat!=="all"?touchCat:"Categories"}
                          </button>
                        </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Step 4: Checkout */}
                  {touchStep==="checkout"&&(
                    <div style={{position:"fixed",inset:0,zIndex:400,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
                      {/* Header */}
                      <div style={{padding:"14px 16px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:12,flexShrink:0,background:th.cardBg}}>
                        <button onClick={()=>setTouchStep("items")} style={{background:"none",border:"none",fontSize:20,color:th.textSub,cursor:"pointer",padding:"2px 6px"}}>←</button>
                        <div style={{flex:1,fontSize:16,fontWeight:800,color:th.textHeading}}>Checkout</div>
                        <div style={{fontSize:15,fontWeight:900,color:"#3a3d80"}}>{fmt(cartTotal)}</div>
                      </div>
                      {/* Form */}
                      <div style={{flex:1,overflowY:"auto",padding:"16px"}}>
                        {/* Cart summary */}
                        <div style={{...card,padding:"12px 14px",marginBottom:14}}>
                          <div style={{fontSize:12,fontWeight:700,color:th.textSub,marginBottom:8,textTransform:"uppercase",letterSpacing:.6}}>{cart.length} Items</div>
                          {cart.map(item=>(
                            <div key={item.id} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"4px 0",borderBottom:`1px solid ${th.cardBorder}`}}>
                              <span style={{color:th.text}}>{lang==="en"?item.name:item.nameTa} ×{item.qty}</span>
                              <span style={{fontWeight:700,color:"#3a3d80"}}>₹{item.salePrice*item.qty}</span>
                            </div>
                          ))}
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0 2px",color:th.textSub}}>
                            <span>Subtotal</span><span>₹{cartSubtotal.toFixed(2)}</span>
                          </div>
                          {billType==="GST"&&cartGST>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"2px 0",color:"#2980b9"}}>
                            <span>GST</span><span>₹{cartGST.toFixed(2)}</span>
                          </div>}
                          {Number(billDiscount)>0&&<div style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"2px 0",color:"#e74c3c"}}>
                            <span>Discount</span><span>-₹{Number(billDiscount).toFixed(2)}</span>
                          </div>}
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:15,fontWeight:900,padding:"8px 0 0",borderTop:`2px solid #3a3d80`,marginTop:4,color:"#3a3d80"}}>
                            <span>TOTAL</span><span>{fmt(cartTotal)}</span>
                          </div>
                        </div>
                        {/* Customer */}
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:12,fontWeight:700,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Customer</div>
                          <input value={billCustomer} onChange={e=>setBillCustomer(e.target.value)} placeholder="Name (optional)"
                            style={{...IS,width:"100%",marginBottom:8,fontSize:14}}/>
                          <input value={billPhone} onChange={e=>setBillPhone(e.target.value)} placeholder="Phone (optional)"
                            style={{...IS,width:"100%",fontSize:14}}/>
                        </div>
                        {/* GST / Non-GST */}
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:12,fontWeight:700,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Bill Type</div>
                          <div style={{display:"flex",gap:8,marginBottom:8}}>
                            {["GST","Non-GST"].map(tp=>(
                              <button key={tp} onClick={()=>setBillType(tp)}
                                style={{flex:1,padding:"10px 0",borderRadius:9,border:billType===tp?"none":`1px solid ${th.ghostBorder}`,cursor:"pointer",fontSize:13,fontWeight:700,background:billType===tp?"linear-gradient(135deg,#3a3d80,#e74c3c)":"transparent",color:billType===tp?"#fff":th.ghostText}}>
                                {tp}
                              </button>
                            ))}
                          </div>
                          <select value={billInvoiceType} onChange={e=>setBillInvoiceType(e.target.value)}
                            style={{...IS,width:"100%",fontSize:13}}>
                            {["TAX INVOICE","BILL / RECEIPT","ESTIMATE","DELIVERY NOTE","PROFORMA INVOICE"].map(tp=>(
                              <option key={tp} value={tp}>{tp}</option>
                            ))}
                          </select>
                        </div>
                        {/* Payment */}
                        <div style={{marginBottom:12}}>
                          <div style={{fontSize:12,fontWeight:700,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Payment</div>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            {["Cash","UPI","Card","Credit"].map(pm=>(
                              <button key={pm} onClick={()=>setBillPayment(pm)}
                                style={{flex:1,minWidth:70,padding:"10px 0",borderRadius:9,border:billPayment===pm?`2px solid #3a3d80`:`1px solid ${th.ghostBorder}`,cursor:"pointer",fontSize:13,fontWeight:700,background:billPayment===pm?"#3a3d8018":"transparent",color:billPayment===pm?"#3a3d80":th.ghostText}}>
                                {pm}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Discount */}
                        <div style={{marginBottom:20}}>
                          <div style={{fontSize:12,fontWeight:700,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.6}}>Discount (₹)</div>
                          <input type="number" value={billDiscount||""} onChange={e=>setBillDiscount(e.target.value)}
                            placeholder="0" min="0"
                            style={{...IS,width:"100%",fontSize:14}}/>
                        </div>
                      </div>
                      {/* Actions */}
                      <div style={{padding:"12px 16px",borderTop:`2px solid #3a3d80`,background:th.cardBg,display:"flex",flexDirection:"column",gap:8,flexShrink:0}}>
                        <button onClick={()=>saveBill(null)} style={{...btn("#3a3d80"),width:"100%",padding:"14px 0",fontSize:16,fontWeight:900,borderRadius:12}}>
                          ✅ Save Bill · {fmt(cartTotal)}
                        </button>
                        <div style={{display:"flex",gap:8}}>
                          <button onClick={()=>saveBill(defPrintMode==="80mm"?"thermal":"A4")} style={{...btn("#2980b9"),flex:1,padding:"12px 0",fontSize:13,borderRadius:10}}>
                            🖨 Print {defPrintMode==="80mm"?"80mm":"A4"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Cart summary bar at bottom for mobile */}
                  {cart.length>0&&touchStep!=="checkout"&&(
                    <div style={{borderTop:`2px solid #3a3d80`,padding:"12px 16px",background:th.cardBg,flexShrink:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                        <span style={{fontSize:13,color:th.textSub}}>{cart.length} items · {fmt(cartSubtotal)}</span>
                        <button onClick={()=>setCart([])} style={{background:"none",border:"none",color:"#e74c3c",cursor:"pointer",fontSize:12}}>Clear</button>
                      </div>
                      <div style={{maxHeight:isMobileView?160:undefined,overflowY:"auto",marginBottom:10}}>
                        {cart.map(item=>(
                          <div key={item.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 0",borderBottom:`1px solid ${th.cardBorder}`}}>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:13,fontWeight:700,color:th.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{lang==="en"?item.name:item.nameTa}</div>
                              <div style={{fontSize:11,color:th.textSub}}>₹{item.salePrice} × {item.qty} = <span style={{color:"#3a3d80",fontWeight:700}}>₹{item.salePrice*item.qty}</span></div>
                            </div>
                            <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                              <button onClick={()=>updateQty(item.id,item.qty-1)} style={{width:26,height:26,borderRadius:6,border:"none",background:"rgba(231,76,60,.12)",color:"#e74c3c",cursor:"pointer",fontSize:16,lineHeight:1,fontWeight:800}}>−</button>
                              <span style={{fontSize:14,fontWeight:800,color:th.text,minWidth:20,textAlign:"center"}}>{item.qty}</span>
                              <button onClick={()=>updateQty(item.id,item.qty+1)} style={{width:26,height:26,borderRadius:6,border:"none",background:"rgba(58,61,128,0.12)",color:"#3a3d80",cursor:"pointer",fontSize:16,lineHeight:1,fontWeight:800}}>+</button>
                              <button onClick={()=>setCart(cart.filter(c=>c.id!==item.id))} style={{width:26,height:26,borderRadius:6,border:"none",background:"rgba(231,76,60,.12)",color:"#e74c3c",cursor:"pointer",fontSize:14,fontWeight:800}}>✕</button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button onClick={()=>{setTouchStep("checkout");}} style={{...btn("#3a3d80"),width:"100%",fontSize:15,padding:"12px 0",borderRadius:12}}>
                        Checkout · {fmt(cartTotal)}
                      </button>
                    </div>
                  )}
                </div>

              ) : null}

              {/* ── PC KEYBOARD-FIRST POS BILLING ── */}
              {!isMobileView && renderPcBilling()}
            </div>
          )}

          {/* ════ INVENTORY ════ */}
          {tab==="inventory"&&(
            <div>
              <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                <input value={invSearch} onChange={e=>setInvSearch(e.target.value)} placeholder={t.searchPlaceholder} style={{...IS,flex:1,minWidth:isMobileView?120:200}}/>
                <select value={invCat} onChange={e=>{setInvCat(e.target.value);setInvSubCat("all");}} style={{...IS,width:160}}>
                  <option value="all">{t.allCat}</option>
                  {cats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                {invCat!=="all"&&activeInvSubCats.length>0&&(
                  <select value={invSubCat} onChange={e=>setInvSubCat(e.target.value)} style={{...IS,width:170}}>
                    <option value="all">{t.allSubCat}</option>
                    {activeInvSubCats.map(sc=><option key={sc.id} value={sc.name}>{sc.name}</option>)}
                  </select>
                )}
                <div style={{flex:1}}/>
                <button onClick={downloadInventoryTemplate} style={{...ghost,fontSize:12}}>⬇ Template</button>
                <label style={{...btn("#2980b9"),fontSize:12,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                  ⬆ Import CSV
                  <input type="file" accept=".csv" style={{display:"none"}} onChange={e=>{
                    const file=e.target.files[0]; if(!file) return;
                    const reader=new FileReader();
                    reader.onload=ev=>{
                      try {
                        const items=parseInventoryCSV(ev.target.result, cats);
                        if(!items.length){setImportError("No valid items found in CSV.");return;}
                        setInventory(prev=>[...prev,...items]);
                        showToast(`Imported ${items.length} items!`);
                        setImportError(null);
                      } catch(err){ setImportError("CSV parse error: "+err.message); }
                    };
                    reader.readAsText(file);
                    e.target.value="";
                  }}/>
                </label>
                <button onClick={()=>exportInventoryCSV(inventory)} style={{...btn("#27ae60"),fontSize:12}}>⬇ Export CSV</button>
                <button onClick={()=>{
                  const rows=[["INVENTORY REPORT — "+storeName,"","","","","","","","","","",""],
                    ["Generated:",new Date().toLocaleString(),"","","","","","","","","",""],
                    ["","","","","","","","","","","",""],
                    ["#","Item Name","Category","Sub-Category","Brand","Item Code","Unit","Stock","Sale Price","Purchase Price","Stock Value (Cost)","Status"]
                  ];
                  inventory.forEach((item,i)=>{
                    rows.push([
                      i+1, item.name, item.category, item.subCategory||"", item.brand||"", item.itemCode||"",
                      item.unit, item.stock, item.salePrice, item.purchasePrice,
                      (item.purchasePrice*item.stock).toFixed(2),
                      item.stock===0?"Out of Stock":item.stock<10?"Low Stock":"In Stock"
                    ]);
                  });
                  rows.push(["","","","","","","","","","","",""]);
                  rows.push(["SUMMARY","","","","","","","","","","",""]);
                  rows.push(["Total Items","",inventory.length,"","","","","","","","",""]);
                  rows.push(["Total Stock Value (Cost)","","₹"+inventory.reduce((a,i)=>a+i.purchasePrice*i.stock,0).toFixed(2),"","","","","","","","",""]);
                  rows.push(["Total Stock Value (Sale)","","₹"+inventory.reduce((a,i)=>a+i.salePrice*i.stock,0).toFixed(2),"","","","","","","","",""]);
                  rows.push(["Low Stock Items","","",inventory.filter(i=>i.stock>0&&i.stock<10).length,"","","","","","","",""]);
                  rows.push(["Out of Stock Items","","",inventory.filter(i=>i.stock===0).length,"","","","","","","",""]);
                  const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
                  const a=document.createElement("a");
                  a.href="data:text/csv;charset=utf-8,"+encodeURIComponent(csv);
                  a.download=`Inventory_Report_${today()}.csv`;
                  a.click();
                }} style={{...btn("#8e44ad"),fontSize:12}}>📊 Download Report</button>
                <button onClick={openAddItem} style={btn("#3a3d80")}>+ {t.addItem}</button>
              </div>
              {importError&&<div style={{background:"rgba(231,76,60,.08)",border:"1px solid rgba(231,76,60,.3)",borderRadius:8,padding:"8px 14px",fontSize:12,color:"#e74c3c",marginBottom:10}}>⚠ {importError} <button onClick={()=>setImportError(null)} style={{background:"none",border:"none",color:"#e74c3c",cursor:"pointer",marginLeft:8}}>✕</button></div>}
              {/* Category + Sub-Category chips */}
              <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
                {cats.map(cat=>(
                  <div key={cat.id} onClick={()=>{setInvCat(invCat===cat.name?"all":cat.name);setInvSubCat("all");}}
                    style={{...card,padding:"7px 13px",display:"flex",alignItems:"center",gap:8,borderColor:invCat===cat.name?cat.color+"88":`${cat.color}33`,cursor:"pointer",transition:"all .18s",background:invCat===cat.name?`${cat.color}0f`:th.cardBg}}>
                    <div style={{width:4,height:32,borderRadius:3,background:cat.color,flexShrink:0}}/>
                    <div>
                      <div style={{fontSize:11,fontWeight:600,color:th.textSub}}>{cat.name}</div>
                      <div style={{fontSize:13,fontWeight:800,color:cat.color}}>{inventory.filter(i=>i.category===cat.name).length} items</div>
                    </div>
                  </div>
                ))}
              </div>
              {invCat!=="all"&&activeInvSubCats.length>0&&(
                <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
                  <button onClick={()=>setInvSubCat("all")} style={{...ghost,padding:"5px 12px",fontSize:11,background:invSubCat==="all"?"rgba(58,61,128,0.12)":th.ghostBg,color:invSubCat==="all"?"#3a3d80":th.ghostText,border:invSubCat==="all"?"1px solid #3a3d80":`1px solid ${th.ghostBorder}`}}>{t.allSubCat}</button>
                  {activeInvSubCats.map(sc=>(
                    <button key={sc.id} onClick={()=>setInvSubCat(invSubCat===sc.name?"all":sc.name)}
                      style={{...ghost,padding:"5px 12px",fontSize:11,background:invSubCat===sc.name?"rgba(58,61,128,0.12)":th.ghostBg,color:invSubCat===sc.name?"#3a3d80":th.ghostText,border:invSubCat===sc.name?"1px solid #3a3d80":`1px solid ${th.ghostBorder}`}}>{sc.name}</button>
                  ))}
                </div>
              )}
              <div style={{...card,overflow:"hidden"}}>
                <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
                <table style={{width:"100%",minWidth:isMobileView?800:"100%",borderCollapse:"collapse",tableLayout:"auto"}}>
                  <thead>
                    <tr style={{background:th.tableThead}}>
                      {[t.itemName,"Code",t.brand,t.category,t.subCategory,t.stock,t.salePrice,t.purchasePrice,"GST",t.unit,"Status",""].map((h,hi)=>(
                        <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:11,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,fontWeight:700,whiteSpace:"nowrap",...(hi===0&&isMobileView?{position:"sticky",left:0,background:th.tableThead,zIndex:2,boxShadow:"2px 0 6px #0002"}:{})}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInv.map(item=>(
                      <tr key={item.id} style={{borderTop:`1px solid ${th.cardBorder}`,transition:"background .15s"}}
                        onMouseEnter={e=>e.currentTarget.style.background=th.tableHover}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <td style={{padding:"11px 12px",...(isMobileView?{position:"sticky",left:0,background:th.cardBg,zIndex:1,boxShadow:"2px 0 6px #0002"}:{})}}>
                          <div style={{fontSize:13,fontWeight:700,color:th.text,minWidth:100}}>{lang==="en"?item.name:item.nameTa}</div>
                          {item.hsn&&<div style={{fontSize:11,color:th.textSub}}>HSN: {item.hsn}</div>}
                        </td>
                        <td style={{padding:"11px 12px",fontSize:12,color:th.textSub,fontWeight:600}}>{item.itemCode||"—"}</td>
                        <td style={{padding:"11px 12px",fontSize:12,color:th.text}}>{item.brand||"—"}</td>
                        <td style={{padding:"11px 12px"}}><span style={{fontSize:12,padding:"4px 10px",borderRadius:6,fontWeight:700,background:`${catColor(item.category)}20`,color:catColor(item.category),display:"inline-block",whiteSpace:"nowrap"}}>{item.category}</span></td>
                        <td style={{padding:"11px 12px"}}>{item.subCategory?<span style={{fontSize:12,padding:"4px 10px",borderRadius:6,fontWeight:600,background:th.innerCard,color:th.text,display:"inline-block",whiteSpace:"nowrap",border:`1px solid ${th.cardBorder}`}}>{item.subCategory}</span>:<span style={{color:th.textSub,fontSize:12}}>—</span>}</td>
                        <td style={{padding:"11px 12px",fontSize:15,fontWeight:800,color:item.stock===0?"#e74c3c":item.stock<10?"#e67e22":"#27ae60"}}>{item.stock}</td>
                        <td style={{padding:"11px 12px",fontSize:13,color:"#3a3d80",fontWeight:700}}>₹{item.salePrice}</td>
                        <td style={{padding:"11px 12px",fontSize:13,color:th.textSub}}>₹{item.purchasePrice}</td>
                        <td style={{padding:"11px 12px",fontSize:13,color:th.textSub}}>{item.gst}%</td>
                        <td style={{padding:"11px 12px",fontSize:13,color:th.textSub}}>{item.unit}</td>
                        <td style={{padding:"11px 12px"}}><span style={{fontSize:12,padding:"4px 10px",borderRadius:6,fontWeight:700,background:item.stock===0?"rgba(231,76,60,.12)":item.stock<10?"rgba(58,61,128,0.12)":"rgba(39,174,96,.12)",color:item.stock===0?"#e74c3c":item.stock<10?"#e67e22":"#27ae60",display:"inline-block"}}>{item.stock===0?t.outStock:item.stock<10?t.lowStockBadge:t.inStock}</span></td>
                        <td style={{padding:"11px 12px"}}>
                          <div style={{display:"flex",gap:6}}>
                            <button onClick={()=>openEditItem(item)} style={{padding:"5px 12px",borderRadius:7,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:12,fontWeight:600}}>Edit</button>
                            <button onClick={()=>setConfirmDel(item.id)} style={{padding:"5px 12px",borderRadius:7,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {!filteredInv.length&&<div style={{textAlign:"center",color:th.textSub,padding:40,fontSize:13}}>No items found</div>}
              </div>
            </div>
          )}

          {/* ════ CATEGORIES ════ */}
          {tab==="categories"&&(
            <div>
              <div style={{background:"linear-gradient(135deg,#3a3d8018,#e74c3c0a)",border:"1px solid #3a3d8033",borderRadius:14,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:22}}>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:th.textHeading,fontFamily:"'Segoe UI',system-ui,sans-serif",letterSpacing:"0.2px"}}>{t.manageCat}</div>
                  <div style={{fontSize:12,color:th.textSub,marginTop:3}}>Manage categories and their sub-categories</div>
                </div>
                <button onClick={openAddCat} style={btn("#3a3d80")}>+ {t.addCat}</button>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {cats.map(cat=>{
                  const itemCount  = inventory.filter(i=>i.category===cat.name).length;
                  const isExpanded = expandedCat===cat.id;
                  return (
                    <div key={cat.id} style={{...card,borderColor:`${cat.color}44`,overflow:"hidden"}}>
                      {/* Category Header */}
                      <div style={{padding:"18px 20px",display:"flex",alignItems:"center",gap:14}}>
                        <div style={{width:14,height:36,borderRadius:6,background:cat.color,flexShrink:0}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:15,fontWeight:700,color:th.textHeading,fontFamily:"'Segoe UI',system-ui,sans-serif",letterSpacing:"0.3px"}}>{cat.name}</div>
                          <div style={{fontSize:11,fontWeight:400,color:th.textSub,marginTop:3,letterSpacing:"0.2px"}}>
                            {itemCount} items · {(cat.subCategories||[]).length} sub-categories
                          </div>
                        </div>
                        <div style={{display:"flex",gap:8,alignItems:"center"}}>
                          <button onClick={()=>openAddSubCat(cat.id)} style={{...btn("#27ae60"),padding:"6px 12px",fontSize:11}}>+ Sub-Cat</button>
                          <button onClick={()=>openEditCat(cat)} style={{...ghost,padding:"6px 12px",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:5}}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            Edit
                          </button>
                          <button onClick={()=>setConfirmDelCat(cat.id)} style={{padding:"6px 14px",borderRadius:8,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Delete</button>
                          <button onClick={()=>setExpandedCat(isExpanded?null:cat.id)}
                            style={{...ghost,padding:"6px 12px",fontSize:13,minWidth:36,textAlign:"center"}}>{isExpanded?"▲":"▼"}</button>
                        </div>
                      </div>

                      {/* Sub-categories */}
                      {isExpanded&&(
                        <div style={{borderTop:`1px solid ${th.cardBorder}`,padding:"14px 20px",background:th.innerCard}}>
                          <div style={{fontSize:12,fontWeight:600,color:th.textSub,marginBottom:12,textTransform:"uppercase",letterSpacing:.8}}>{t.manageSubCat}</div>
                          <div style={{display:"grid",gridTemplateColumns:isMobileView?"repeat(2,1fr)":"repeat(auto-fill,minmax(200px,1fr))",gap:10}}>
                            {(cat.subCategories||[]).map(sc=>{
                              const scCount=inventory.filter(i=>i.category===cat.name&&i.subCategory===sc.name).length;
                              return (
                                <div key={sc.id} style={{background:th.cardBg,borderRadius:10,padding:"12px 14px",border:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                                  <div>
                                    <div style={{fontSize:13,fontWeight:600,color:th.text}}>{sc.name}</div>
                                    <div style={{fontSize:10,color:th.textSub,marginTop:2}}>{scCount} items</div>
                                  </div>
                                  <div style={{display:"flex",gap:5}}>
                                    <button onClick={()=>openEditSubCat(cat.id,sc)} style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:11,fontWeight:600}}>Edit</button>
                                    <button onClick={()=>setConfirmDelSubCat({catId:cat.id,scId:sc.id})} style={{padding:"4px 10px",borderRadius:6,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>Delete</button>
                                  </div>
                                </div>
                              );
                            })}
                            <button onClick={()=>openAddSubCat(cat.id)}
                              style={{background:"transparent",borderRadius:10,padding:"12px 14px",border:`2px dashed ${th.cardBorder}`,color:th.textSub,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6,transition:"border-color .18s"}}
                              onMouseEnter={e=>e.currentTarget.style.borderColor="#3a3d80"}
                              onMouseLeave={e=>e.currentTarget.style.borderColor=th.cardBorder}>
                              + Add Sub-Category
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Add category card */}
                <button onClick={openAddCat}
                  style={{...card,padding:22,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",border:`2px dashed ${th.cardBorder}`,background:"transparent",color:th.textSub,gap:10,height:80,transition:"border-color .18s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor="#3a3d80"}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=th.cardBorder}>
                  <span style={{fontSize:22}}>+</span>
                  <span style={{fontSize:13,fontWeight:600}}>{t.addCat}</span>
                </button>
              </div>
            </div>
          )}

          {/* ════ CUSTOMERS ════ */}
          {tab==="customers"&&(()=>{
            const filteredCusts = customers.filter(c=>
              c.name.toLowerCase().includes(custSearch.toLowerCase())||
              (c.phone&&c.phone.includes(custSearch))||
              (c.email&&c.email.toLowerCase().includes(custSearch.toLowerCase()))
            );
            const custTotalSpend = c => sales.filter(s=>s.phone&&s.phone===c.phone&&c.phone).reduce((a,s)=>a+s.total,0);
            const custTotalBills = c => sales.filter(s=>s.phone&&s.phone===c.phone&&c.phone).length;
            return (
              <div>
                {/* Header */}
                <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18,flexWrap:"wrap"}}>
                  <div style={{flex:1,minWidth:200}}>
                    <input value={custSearch} onChange={e=>setCustSearch(e.target.value)}
                      placeholder="Search by name, phone or email..."
                      style={{...IS,width:"100%",fontSize:13,padding:"8px 12px"}}/>
                  </div>
                  <button onClick={()=>{setEditingCust(null);setCustForm({name:"",phone:"",email:"",address:"",pan:"",gstin:"",aadhar:"",notes:""});setShowCustModal(true);}}
                    style={{...btn("#3a3d80"),padding:"8px 20px",fontSize:13}}>+ Add Customer</button>
                </div>
                {/* Summary cards */}
                <div style={{display:"flex",gap:12,marginBottom:18,flexWrap:"wrap"}}>
                  {[{l:"Total Customers",v:customers.length,c:"#3a3d80"},{l:"With Phone",v:customers.filter(c=>c.phone).length,c:"#27ae60"},{l:"With Email",v:customers.filter(c=>c.email).length,c:"#2980b9"}].map(s=>(
                    <div key={s.l} style={{...card,flex:1,minWidth:140,padding:"12px 16px"}}>
                      <div style={{fontSize:11,color:th.textSub,fontWeight:600,marginBottom:4}}>{s.l}</div>
                      <div style={{fontSize:22,fontWeight:800,color:s.c}}>{s.v}</div>
                    </div>
                  ))}
                </div>
                {/* Table */}
                <div style={{...card,overflow:"hidden"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
                      <thead>
                        <tr style={{background:th.tableThead}}>
                          {["#","Name","Phone","Address","Bills","Total Spend",""].map(h=>(
                            <th key={h} style={{padding:"10px 12px",textAlign:"left",fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.6,whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCusts.map((c,i)=>(
                          <tr key={c.id} style={{borderBottom:`1px solid ${th.cardBorder}`}}
                            onMouseEnter={e=>e.currentTarget.style.background=th.tableHover}
                            onMouseLeave={e=>e.currentTarget.style.background=""}>
                            <td style={{padding:"10px 12px",fontSize:12,color:th.textSub}}>{i+1}</td>
                            <td style={{padding:"10px 12px",fontSize:13,fontWeight:700,color:th.text}}>{c.name}</td>
                            <td style={{padding:"10px 12px",fontSize:12,color:th.text}}>{c.phone||"—"}</td>
                            <td style={{padding:"10px 12px",fontSize:12,color:th.textSub,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.address||"—"}</td>
                            <td style={{padding:"10px 12px",fontSize:12,fontWeight:600,color:"#3a3d80"}}>{custTotalBills(c)}</td>
                            <td style={{padding:"10px 12px",fontSize:12,fontWeight:700,color:"#27ae60"}}>₹{custTotalSpend(c).toFixed(2)}</td>
                            <td style={{padding:"10px 12px"}}>
                              <div style={{display:"flex",gap:6}}>
                                <button onClick={()=>{setEditingCust(c);setCustForm({name:c.name,phone:c.phone||"",email:c.email||"",address:c.address||"",pan:c.pan||"",gstin:c.gstin||"",aadhar:c.aadhar||"",notes:c.notes||""});setShowCustModal(true);}}
                                  style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${th.cardBorder}`,background:th.appBg,color:th.text,cursor:"pointer",fontSize:11,fontWeight:600}}>Edit</button>
                                <button onClick={()=>{if(window.confirm(`Delete ${c.name}?`))setCustomers(customers.filter(x=>x.id!==c.id));}}
                                  style={{padding:"4px 10px",borderRadius:6,border:"1px solid #e74c3c33",background:"rgba(231,76,60,.08)",color:"#e74c3c",cursor:"pointer",fontSize:11,fontWeight:600}}>Delete</button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!filteredCusts.length&&<div style={{padding:"40px",textAlign:"center",color:th.textSub,fontSize:14}}>{custSearch?"No customers match your search.":"No customers yet. Click \"+ Add Customer\" to get started."}</div>}
                </div>
              </div>
            );
          })()}

          {/* ════ EXPENSES ════ */}
          {tab==="expenses"&&(
            <div>
              {/* Period Summary Cards */}
              <div style={{overflowX:isMobileView?"auto":"visible",marginBottom:16,paddingBottom:isMobileView?4:0}}>
                <div style={{display:"grid",gridTemplateColumns:isMobileView?"repeat(5,130px)":"repeat(5,1fr)",gap:isMobileView?8:12}}>
                  {[
                    {key:"daily",   label:t.dailyExp,   val:expSummary.daily},
                    {key:"weekly",  label:t.weeklyExp,  val:expSummary.weekly},
                    {key:"monthly", label:t.monthlyExp, val:expSummary.monthly},
                    {key:"quarter", label:t.quarterExp, val:expSummary.quarter},
                    {key:"annual",  label:t.annualExp,  val:expSummary.annual},
                  ].map(p=>(
                    <div key={p.key} onClick={()=>setExpPeriod(p.key)}
                      style={{...card,padding:"12px 14px",cursor:"pointer",borderColor:expPeriod===p.key?"#3a3d8088":th.cardBorder,background:expPeriod===p.key?"rgba(247,147,26,0.07)":th.cardBg,transition:"all .18s",boxShadow:expPeriod===p.key?"0 4px 16px rgba(247,147,26,0.15)":"none",flexShrink:0}}>
                      <div style={{fontSize:10,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:4,whiteSpace:"nowrap"}}>{p.label}</div>
                      <div style={{fontSize:16,fontWeight:800,color:expPeriod===p.key?"#3a3d80":th.textHeading}}>{fmt(p.val)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controls */}
              <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
                <div style={{fontSize:13,fontWeight:600,color:th.textHeading}}>
                  Showing: <span style={{color:"#3a3d80"}}>{[{k:"daily",l:t.dailyExp},{k:"weekly",l:t.weeklyExp},{k:"monthly",l:t.monthlyExp},{k:"quarter",l:t.quarterExp},{k:"annual",l:t.annualExp}].find(p=>p.k===expPeriod)?.l}</span>
                </div>
                <select value={expCatFilter} onChange={e=>setExpCatFilter(e.target.value)} style={{...IS,width:180,marginLeft:"auto"}}>
                  <option value="all">All Categories</option>
                  {expCats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button onClick={()=>exportExpensesCSV(filteredExp)} style={{...btn("#27ae60"),fontSize:12}}>⬇ Export CSV</button>
                <button onClick={openAddExpCat} style={{...btn("#8e44ad"),fontSize:12}}>🏷 Manage Categories</button>
                <button onClick={openAddExp} style={btn("#3a3d80")}>+ {t.addExpense}</button>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Expense List - expandable rows */}
                <div style={{...card,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>{t.allExpenses} ({filteredExp.length})</div>
                    <div style={{fontSize:14,fontWeight:800,color:"#e74c3c"}}>{fmt(expTotal)}</div>
                  </div>
                  {filteredExp.length===0
                    ?<div style={{textAlign:"center",color:th.textSub,padding:40,fontSize:13}}>{t.noExpenses}</div>
                    :<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:14,padding:16}}>
                      {filteredExp.map(exp=>(
                        <div key={exp.id} style={{borderRadius:14,border:`1.5px solid ${expCatColor(exp.category)}33`,background:th.cardBg,overflow:"hidden",boxShadow:"0 2px 8px #0001"}}>
                          {/* Colored top bar */}
                          <div style={{height:5,background:expCatColor(exp.category)}}/>
                          <div style={{padding:"14px 16px"}}>
                            {/* Icon + title */}
                            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                              <div style={{width:36,height:36,borderRadius:10,background:`${expCatColor(exp.category)}18`,border:`1px solid ${expCatColor(exp.category)}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>
                                {expCatIcon(exp.category)}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:700,color:th.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{exp.title}</div>
                                <div style={{fontSize:10,color:th.textSub,marginTop:1}}>{exp.category}</div>
                              </div>
                            </div>
                            {/* Amount */}
                            <div style={{fontSize:20,fontWeight:900,color:"#e74c3c",marginBottom:6}}>-{fmt(exp.amount)}</div>
                            {/* Date & note */}
                            <div style={{fontSize:11,color:th.textSub,marginBottom:exp.note?4:10}}>{exp.date}</div>
                            {exp.note&&<div style={{fontSize:11,color:th.textSub,marginBottom:10,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>"{exp.note}"</div>}
                            {/* Actions */}
                            <div style={{display:"flex",gap:8,borderTop:`1px solid ${th.cardBorder}`,paddingTop:10}}>
                              <button onClick={()=>openEditExp(exp)} style={{flex:1,padding:"6px 0",borderRadius:8,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:12,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",gap:5}}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                Edit
                              </button>
                              <button onClick={()=>setConfirmDelExp(exp.id)} style={{flex:1,padding:"6px 0",borderRadius:8,border:"none",background:"rgba(231,76,60,.1)",color:"#e74c3c",cursor:"pointer",fontSize:12,fontWeight:700}}>Delete</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                </div>

                {/* By Category breakdown - collapsible */}
                <div style={{...card,overflow:"hidden"}}>
                  <div onClick={()=>setExpCatOpen(o=>!o)} style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>📊 By Category</div>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:12,color:"#e74c3c",fontWeight:700}}>{fmt(expTotal)}</span>
                      <span style={{color:th.textSub,fontSize:14,transition:"transform .2s",display:"inline-block",transform:expCatOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
                    </div>
                  </div>
                  {expCatOpen&&<div style={{padding:"0 18px 16px"}}>
                    {expByCat.length===0
                      ?<div style={{textAlign:"center",color:th.textSub,fontSize:12,padding:20}}>No data</div>
                      :expByCat.sort((a,b)=>b.total-a.total).map(({cat,total,color,icon})=>{
                        const pct=expTotal>0?Math.round((total/expTotal)*100):0;
                        return (
                          <div key={cat} style={{marginBottom:14}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:12}}>
                              <span style={{color:th.text}}>{icon} {cat}</span>
                              <span style={{color,fontWeight:700}}>{pct}% · {fmt(total)}</span>
                            </div>
                            <div style={{background:th.appBg,borderRadius:20,height:7,overflow:"hidden"}}>
                              <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:color,transition:"width .5s ease"}}/>
                            </div>
                          </div>
                        );
                      })
                    }
                    {expByCat.length>0&&(
                      <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:12,marginTop:4,display:"flex",justifyContent:"space-between"}}>
                        <span style={{fontSize:12,fontWeight:600,color:th.text}}>Total</span>
                        <span style={{fontSize:14,fontWeight:800,color:"#e74c3c"}}>{fmt(expTotal)}</span>
                      </div>
                    )}
                  </div>}
                </div>
              </div>
            </div>
          )}


          {/* ════ PURCHASES ════ */}
          {tab==="purchases"&&(
            <div>
              {/* Period Summary Cards */}
              <div style={{overflowX:isMobileView?"auto":"visible",marginBottom:16,paddingBottom:isMobileView?4:0}}>
                <div style={{display:"grid",gridTemplateColumns:isMobileView?"repeat(5,140px)":"repeat(5,1fr)",gap:isMobileView?8:12,minWidth:isMobileView?undefined:0}}>
                  {[
                    {key:"daily",   label:"Today"},
                    {key:"weekly",  label:"This Week"},
                    {key:"monthly", label:"This Month"},
                    {key:"quarter", label:"Quarter"},
                    {key:"annual",  label:"This Year"},
                  ].map(p=>(
                    <div key={p.key} onClick={()=>setPurPeriod(p.key)}
                      style={{...card,padding:"12px 14px",cursor:"pointer",borderColor:purPeriod===p.key?"#8e44ad88":th.cardBorder,background:purPeriod===p.key?"rgba(142,68,173,0.07)":th.cardBg,transition:"all .18s",boxShadow:purPeriod===p.key?"0 4px 16px rgba(142,68,173,0.15)":"none",flexShrink:0}}>
                      <div style={{fontSize:10,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:4,whiteSpace:"nowrap"}}>{p.label}</div>
                      <div style={{fontSize:16,fontWeight:800,color:purPeriod===p.key?"#8e44ad":th.textHeading}}>{fmt(filterExpenses(purchases,p.key).reduce((s,x)=>s+x.total,0))}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Controls */}
              <div style={{display:"flex",gap:10,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
                <input value={purSearch} onChange={e=>setPurSearch(e.target.value)} placeholder="Search by item, brand, supplier..." style={{...IS,maxWidth:280}}/>
                <select value={purCatFilter} onChange={e=>setPurCatFilter(e.target.value)} style={{...IS,width:170}}>
                  <option value="all">All Categories</option>
                  {cats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <div style={{marginLeft:"auto",fontSize:13,fontWeight:700,color:th.textSub}}>Total: <span style={{color:"#8e44ad",fontSize:15}}>{fmt(purTotal)}</span></div>
                <button onClick={openAddPur} style={btn("#8e44ad")}>+ {t.addPurchase}</button>
              </div>

              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                {/* Purchase Records - expandable cards */}
                <div style={{...card,overflow:"hidden"}}>
                  <div style={{padding:"12px 16px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:14,fontWeight:700,color:th.textHeading}}>Purchase Records ({filteredPur.length})</div>
                    <div style={{fontSize:14,fontWeight:800,color:"#8e44ad"}}>{fmt(purTotal)}</div>
                  </div>
                  {filteredPur.length===0
                    ?<div style={{textAlign:"center",color:th.textSub,padding:40,fontSize:13}}>{t.noPurchases}</div>
                    :filteredPur.map(pur=>(
                      <div key={pur.id} style={{borderBottom:`1px solid ${th.cardBorder}`}}>
                        {/* Summary row - always visible, tap to expand */}
                        <div onClick={()=>setExpandedPur(expandedPur===pur.id?null:pur.id)}
                          style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:"pointer",background:expandedPur===pur.id?"rgba(142,68,173,.06)":"transparent",transition:"background .15s"}}>
                          <div style={{width:38,height:38,borderRadius:10,background:"rgba(142,68,173,.1)",border:"1px solid rgba(142,68,173,.25)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>🛒</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:700,color:th.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pur.title}</div>
                            <div style={{fontSize:12,color:th.textSub,marginTop:2}}>{pur.date} · {pur.category}</div>
                          </div>
                          <div style={{flexShrink:0,textAlign:"right"}}>
                            <div style={{fontSize:15,fontWeight:800,color:"#8e44ad"}}>{fmt(pur.total)}</div>
                            <div style={{fontSize:12,color:th.textSub}}>{expandedPur===pur.id?"▲":"▼"}</div>
                          </div>
                        </div>
                        {/* Expanded details */}
                        {expandedPur===pur.id&&(
                          <div style={{padding:"0 16px 14px 66px",background:"rgba(142,68,173,.03)"}}>
                            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
                              {pur.brand&&<span style={{background:"rgba(142,68,173,.1)",color:"#8e44ad",padding:"3px 10px",borderRadius:6,fontWeight:700,fontSize:12}}>{pur.brand}</span>}
                              <span style={{background:`${catColor(pur.category)}18`,color:catColor(pur.category),padding:"3px 10px",borderRadius:6,fontWeight:700,fontSize:12}}>{pur.category}</span>
                            </div>
                            <div style={{fontSize:12,color:th.textSub,lineHeight:2}}>
                              {pur.supplier&&<div>Supplier: <span style={{color:th.text,fontWeight:600}}>{pur.supplier}</span></div>}
                              <div>Qty: <span style={{color:th.text,fontWeight:600}}>{pur.qty} {pur.unit} × ₹{pur.costPerUnit}</span></div>
                              {pur.note&&<div>Note: <span style={{color:th.text}}>{pur.note}</span></div>}
                            </div>
                            <div style={{display:"flex",gap:8,marginTop:10}}>
                              <button onClick={e=>{e.stopPropagation();openEditPur(pur);}} style={{padding:"6px 14px",borderRadius:7,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:12,fontWeight:600}}>Edit</button>
                              <button onClick={e=>{e.stopPropagation();setConfirmDelPur(pur.id);}} style={{padding:"6px 14px",borderRadius:7,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>Delete</button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  }
                </div>

                {/* Brand & Category breakdown - side by side on desktop, stacked on mobile */}
                <div style={{display:"grid",gridTemplateColumns:isMobileView?"1fr":"1fr 1fr",gap:14}}>
                  {/* Brand breakdown - collapsible */}
                  <div style={{...card,overflow:"hidden"}}>
                    <div onClick={()=>setPurBrandOpen(o=>!o)} style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
                      <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>📊 By Brand</div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:12,color:"#8e44ad",fontWeight:700}}>{purByBrand.length} brand{purByBrand.length!==1?"s":""}</span>
                        <span style={{color:th.textSub,fontSize:14,transition:"transform .2s",display:"inline-block",transform:purBrandOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
                      </div>
                    </div>
                    {purBrandOpen&&<div style={{padding:"0 18px 16px"}}>
                      {purByBrand.length===0
                        ?<div style={{textAlign:"center",color:th.textSub,fontSize:12,padding:16}}>No data</div>
                        :purByBrand.map(({brand,total,count})=>{
                          const pct=purTotal>0?Math.round((total/purTotal)*100):0;
                          return (
                            <div key={brand} style={{marginBottom:14}}>
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5,fontSize:13}}>
                                <span style={{fontWeight:700,color:th.text}}>{brand||"Unknown"}</span>
                                <span style={{color:"#8e44ad",fontWeight:700}}>{pct}% · {fmt(total)}</span>
                              </div>
                              <div style={{background:th.appBg,borderRadius:20,height:7,overflow:"hidden",marginBottom:3}}>
                                <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:"linear-gradient(90deg,#8e44ad,#6c3483)",transition:"width .5s ease"}}/>
                              </div>
                              <div style={{fontSize:11,color:th.textSub}}>{count} purchase{count>1?"s":""}</div>
                            </div>
                          );
                        })
                      }
                    </div>}
                  </div>
                  {/* Category breakdown - collapsible */}
                  <div style={{...card,overflow:"hidden"}}>
                    <div onClick={()=>setPurCatOpen(o=>!o)} style={{padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer",userSelect:"none"}}>
                      <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>🏷 By Category</div>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:12,color:th.textSub,fontWeight:700}}>{purByCat.length} categor{purByCat.length!==1?"ies":"y"}</span>
                        <span style={{color:th.textSub,fontSize:14,transition:"transform .2s",display:"inline-block",transform:purCatOpen?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
                      </div>
                    </div>
                    {purCatOpen&&<div style={{padding:"0 18px 16px"}}>
                      {purByCat.length===0
                        ?<div style={{textAlign:"center",color:th.textSub,fontSize:12,padding:16}}>No data</div>
                        :purByCat.map(({cat,total,count:_count})=>{
                          const pct=purTotal>0?Math.round((total/purTotal)*100):0;
                          const cc=catColor(cat);
                          return (
                            <div key={cat} style={{marginBottom:12}}>
                              <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:13}}>
                                <span style={{color:cc,fontWeight:700}}>{cat}</span>
                                <span style={{color:cc,fontWeight:700}}>{fmt(total)}</span>
                              </div>
                              <div style={{background:th.appBg,borderRadius:20,height:6,overflow:"hidden"}}>
                                <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:cc,transition:"width .5s ease"}}/>
                              </div>
                            </div>
                          );
                        })
                      }
                    </div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════ SALES ════ */}
          {tab==="sales"&&(()=>{
            const parseSaleDate = s => { if(!s) return null; if(s.includes('-')) return new Date(s); return toDate(s); };
            const filterByPeriod = (arr,period) => arr.filter(s=>{
              if(s.status==="cancelled") return false;
              const d=parseSaleDate(s.date); if(!d||isNaN(d)) return false;
              const n=new Date();
              switch(period){
                case "daily":   return d.toDateString()===n.toDateString();
                case "weekly":  { const w=new Date(n); w.setDate(w.getDate()-6); return d>=w; }
                case "monthly": return d.getMonth()===n.getMonth()&&d.getFullYear()===n.getFullYear();
                case "annual":  return d.getFullYear()===n.getFullYear();
                default: return true;
              }
            });
            const summaryData = [
              {key:"daily",   label:"Today",      color:"#3a3d80"},
              {key:"weekly",  label:"This Week",  color:"#2980b9"},
              {key:"monthly", label:"This Month", color:"#27ae60"},
              {key:"annual",  label:"This Year",  color:"#e67e22"},
            ].map(p=>({...p, total:filterByPeriod(sales,p.key).reduce((a,s)=>a+s.total,0), count:filterByPeriod(sales,p.key).length}));

            const filteredSales = sales.filter(s=>{
              const matchSearch = !salesSearch||s.customer.toLowerCase().includes(salesSearch.toLowerCase())||s.id.toLowerCase().includes(salesSearch.toLowerCase());
              const matchPay  = salesPayFilter==="all"||s.payment===salesPayFilter;
              const matchType = salesTypeFilter==="all"||s.type===salesTypeFilter;
              const d = parseSaleDate(s.date);
              const matchFrom = !salesDateFrom||(d&&d>=new Date(salesDateFrom));
              const matchTo   = !salesDateTo  ||(d&&d<=new Date(salesDateTo+"T23:59:59"));
              return matchSearch&&matchPay&&matchType&&matchFrom&&matchTo;
            });
            const filteredTotal = filteredSales.filter(s=>s.status!=="cancelled").reduce((a,s)=>a+s.total,0);

            // Analytics date filter
            const analyticsSales = sales.filter(s=>{
              if(s.status==="cancelled") return false;
              const d=parseSaleDate(s.date); if(!d||isNaN(d)) return false;
              const from=analyticsDateFrom?new Date(analyticsDateFrom):null;
              const to=analyticsDateTo?new Date(analyticsDateTo+"T23:59:59"):null;
              return (!from||d>=from)&&(!to||d<=to);
            });

            // Top selling items
            const itemMap={};
            analyticsSales.forEach(s=>s.items.forEach(it=>{
              if(!itemMap[it.name]) itemMap[it.name]={name:it.name,qty:0,revenue:0};
              itemMap[it.name].qty+=it.qty; itemMap[it.name].revenue+=it.price*it.qty;
            }));
            const topItems=Object.values(itemMap).sort((a,b)=>b.revenue-a.revenue).slice(0,5);
            const maxItemRev=topItems[0]?.revenue||1;

            // Sales by category
            const catMap={};
            analyticsSales.forEach(s=>s.items.forEach(it=>{
              const cat=inventory.find(i=>i.name===it.name)?.category||"Other";
              if(!catMap[cat]) catMap[cat]={name:cat,total:0};
              catMap[cat].total+=it.price*it.qty;
            }));
            const salesByCat=Object.values(catMap).sort((a,b)=>b.total-a.total);
            const catTotal=salesByCat.reduce((a,c)=>a+c.total,0);

            // Payment breakdown
            const payMap={Cash:0,UPI:0,Card:0,Credit:0};
            analyticsSales.forEach(s=>{ payMap[s.payment]=(payMap[s.payment]||0)+s.total; });
            const payColors={Cash:"#27ae60",UPI:"#2980b9",Card:"#8e44ad",Credit:"#e74c3c"};
            const totalPay=Object.values(payMap).reduce((a,v)=>a+v,0);

            const handleRefund=(sale,reason)=>{
              setSales(prev=>prev.map(s=>s.id===sale.id?{...s,status:"cancelled",cancelReason:reason}:s));
              setInventory(prev=>{ let inv=[...prev]; sale.items.forEach(it=>{ const idx=inv.findIndex(i=>i.name===it.name); if(idx>=0) inv[idx]={...inv[idx],stock:inv[idx].stock+it.qty}; }); return inv; });
              setShowRefundModal(false); setRefundSale(null); setRefundReason(""); showToast("Sale returned & stock restored!");
            };

            return (
              <div style={{animation:"fadeUp .2s ease"}}>

                {/* ── Summary Cards ── */}
                {isMobileView
                  ? <div style={{display:"flex",gap:12,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:6,marginBottom:16}}>
                      {summaryData.map(p=>(
                        <div key={p.key} onClick={()=>setSalesPeriod(p.key)}
                          style={{...card,flexShrink:0,minWidth:150,padding:"14px 18px",cursor:"pointer",borderColor:salesPeriod===p.key?`${p.color}88`:`${p.color}22`,background:salesPeriod===p.key?`${p.color}0d`:th.cardBg,transition:"all .2s"}}>
                          <div style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>{p.label}</div>
                          <div style={{fontSize:20,fontWeight:900,color:p.color,marginBottom:4}}>{fmt(p.total)}</div>
                          <div style={{fontSize:11,color:th.textSub}}>{p.count} transaction{p.count!==1?"s":""}</div>
                        </div>
                      ))}
                    </div>
                  : <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
                      {summaryData.map(p=>(
                        <div key={p.key} onClick={()=>setSalesPeriod(p.key)}
                          style={{...card,padding:"14px 18px",cursor:"pointer",borderColor:salesPeriod===p.key?`${p.color}88`:`${p.color}22`,background:salesPeriod===p.key?`${p.color}0d`:th.cardBg,transition:"all .2s"}}>
                          <div style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>{p.label}</div>
                          <div style={{fontSize:22,fontWeight:900,color:p.color,marginBottom:4}}>{fmt(p.total)}</div>
                          <div style={{fontSize:11,color:th.textSub}}>{p.count} transaction{p.count!==1?"s":""}</div>
                        </div>
                      ))}
                    </div>
                }

                {/* ── Analytics Date Filter ── */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,padding:"10px 16px",borderRadius:12,background:th.cardBg,border:`1px solid ${th.cardBorder}`,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,flexShrink:0}}>📊 Period</span>
                  <input type="date" value={analyticsDateFrom} onChange={e=>setAnalyticsDateFrom(e.target.value)}
                    style={{...IS,padding:"4px 8px",fontSize:11,width:isMobileView?130:130,cursor:"pointer",flex:isMobileView?"1":undefined}}/>
                  <span style={{fontSize:11,color:th.textSub,flexShrink:0}}>→</span>
                  <input type="date" value={analyticsDateTo} onChange={e=>setAnalyticsDateTo(e.target.value)}
                    style={{...IS,padding:"4px 8px",fontSize:11,width:isMobileView?130:130,cursor:"pointer",flex:isMobileView?"1":undefined}}/>
                  <button onClick={()=>{setAnalyticsDateFrom(`${new Date().getFullYear()}-01-01`);setAnalyticsDateTo(`${new Date().getFullYear()}-12-31`);}}
                    style={{padding:"5px 12px",borderRadius:8,border:"none",background:"rgba(58,61,128,0.1)",color:"#3a3d80",cursor:"pointer",fontSize:11,fontWeight:700,flexShrink:0}}>
                    This Year
                  </button>
                  <span style={{fontSize:11,color:th.textSub,flexShrink:0}}>{analyticsSales.length} sales</span>
                </div>

                {/* ── Analytics Row ── */}
                <div style={{display:"grid",gridTemplateColumns:isMobileView?"1fr":"1fr 1fr 1fr",gap:14,marginBottom:20}}>
                  {/* Top Selling Items */}
                  <div style={{...card,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:14}}>🏆 Top Selling Items</div>
                    {topItems.length===0
                      ?<div style={{textAlign:"center",color:th.textSub,fontSize:12,padding:16}}>No sales data</div>
                      :topItems.map((it,i)=>(
                        <div key={it.name} style={{marginBottom:12}}>
                          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:12}}>
                            <span style={{fontWeight:600,color:th.text,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginRight:8}}>
                              <span style={{color:"#e67e22",fontWeight:700,marginRight:5}}>#{i+1}</span>{it.name}
                            </span>
                            <span style={{fontWeight:700,color:"#3a3d80",flexShrink:0}}>{fmt(it.revenue)}</span>
                          </div>
                          <div style={{background:th.appBg,borderRadius:20,height:5,overflow:"hidden"}}>
                            <div style={{width:`${Math.round((it.revenue/maxItemRev)*100)}%`,height:"100%",borderRadius:20,background:"linear-gradient(90deg,#3a3d80,#5558b0)",transition:"width .5s"}}/>
                          </div>
                          <div style={{fontSize:10,color:th.textSub,marginTop:2}}>{it.qty} units sold</div>
                        </div>
                      ))
                    }
                  </div>

                  {/* Sales by Category */}
                  <div style={{...card,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:14}}>🏷 Sales by Category</div>
                    {salesByCat.length===0
                      ?<div style={{textAlign:"center",color:th.textSub,fontSize:12,padding:16}}>No data</div>
                      :salesByCat.map((c,i)=>{
                        const colors=["#e74c3c","#2980b9","#27ae60","#e67e22","#8e44ad"];
                        const color=colors[i%colors.length];
                        const pct=catTotal>0?Math.round((c.total/catTotal)*100):0;
                        return (
                          <div key={c.name} style={{marginBottom:12}}>
                            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:12}}>
                              <span style={{fontWeight:600,color:th.text}}>{c.name}</span>
                              <span style={{fontWeight:700,color}}>{pct}% · {fmt(c.total)}</span>
                            </div>
                            <div style={{background:th.appBg,borderRadius:20,height:5,overflow:"hidden"}}>
                              <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:color,transition:"width .5s"}}/>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>

                  {/* Payment Breakdown */}
                  <div style={{...card,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:14}}>💳 Payment Breakdown</div>
                    {Object.entries(payMap).map(([method,amt])=>{
                      const pct=totalPay>0?Math.round((amt/totalPay)*100):0;
                      const color=payColors[method]||"#888";
                      return (
                        <div key={method} style={{marginBottom:14}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4,fontSize:12}}>
                            <span style={{fontWeight:700,color:th.text,display:"flex",alignItems:"center",gap:6}}>
                              <span style={{width:8,height:8,borderRadius:"50%",background:color,display:"inline-block"}}/>
                              {method}
                            </span>
                            <span style={{fontWeight:800,color}}>{fmt(amt)}</span>
                          </div>
                          <div style={{background:th.appBg,borderRadius:20,height:6,overflow:"hidden"}}>
                            <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:color,transition:"width .5s"}}/>
                          </div>
                          <div style={{fontSize:10,color:th.textSub,marginTop:2}}>{pct}% of total</div>
                        </div>
                      );
                    })}
                    <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:10,marginTop:4,display:"flex",justifyContent:"space-between",fontSize:13}}>
                      <span style={{fontWeight:600,color:th.textSub}}>Total</span>
                      <span style={{fontWeight:900,color:"#3a3d80"}}>{fmt(totalPay)}</span>
                    </div>
                  </div>
                </div>

                {/* ── Sales List ── */}
                <div style={{...card,overflow:"hidden"}}>
                  <div style={{padding:"14px 18px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                    <div style={{fontSize:14,fontWeight:700,color:th.textHeading,flex:1}}>🧾 Sales List</div>
                    <button onClick={()=>exportToCSV(
                        filteredSales.map(s=>[s.id,s.customer,s.phone||"",s.date,s.time||"",s.type,s.payment,s.subtotal,s.gstAmt,s.discount,s.total,s.status||"active"]),
                        ["Invoice No","Customer","Phone","Date","Time","Type","Payment","Subtotal","GST","Discount","Total","Status"],
                        "sales_export.csv"
                      )} style={{padding:"6px 14px",borderRadius:8,border:"none",background:"#27ae60",color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700}}>
                      ⬇ Export CSV
                    </button>
                  </div>
                  {/* Filters */}
                  <div style={{padding:"8px 18px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",gap:6,alignItems:"center",background:th.cardBg,overflowX:"auto",WebkitOverflowScrolling:"touch",flexWrap:isMobileView?"nowrap":"wrap"}}>
                    <input value={salesSearch} onChange={e=>setSalesSearch(e.target.value)}
                      placeholder="🔍 Search..." style={{...IS,flex:1,minWidth:0,padding:"5px 8px",fontSize:11}}/>
                    <select value={salesTypeFilter} onChange={e=>setSalesTypeFilter(e.target.value)} style={{...IS,padding:"5px 4px",fontSize:11,width:90}}>
                      <option value="all">All Types</option>
                      <option value="GST">GST</option>
                      <option value="Non-GST">Non-GST</option>
                    </select>
                    <select value={salesPayFilter} onChange={e=>setSalesPayFilter(e.target.value)} style={{...IS,padding:"5px 4px",fontSize:11,width:100}}>
                      <option value="all">All Pay</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Card">Card</option>
                      <option value="Credit">Credit</option>
                    </select>
                    <input type="date" value={salesDateFrom} onChange={e=>setSalesDateFrom(e.target.value)} style={{...IS,padding:"5px 4px",fontSize:11,width:120}}/>
                    <span style={{fontSize:10,color:th.textSub}}>→</span>
                    <input type="date" value={salesDateTo} onChange={e=>setSalesDateTo(e.target.value)} style={{...IS,padding:"5px 4px",fontSize:11,width:120}}/>
                    {(salesSearch||salesPayFilter!=="all"||salesTypeFilter!=="all"||salesDateFrom||salesDateTo)&&(
                      <button onClick={()=>{setSalesSearch("");setSalesPayFilter("all");setSalesTypeFilter("all");setSalesDateFrom("");setSalesDateTo("");}}
                        style={{padding:"6px 12px",borderRadius:8,border:"none",background:"rgba(231,76,60,.1)",color:"#e74c3c",cursor:"pointer",fontSize:12,fontWeight:700}}>
                        ✕ Clear
                      </button>
                    )}
                  </div>
                  {/* Table */}
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr style={{background:th.tableThead}}>
                          {["Invoice No","Customer","Date & Time","Amount","Payment","Type","Status","Actions"].map(h=>(
                            <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.6,whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSales.length===0
                          ?<tr><td colSpan={8} style={{textAlign:"center",padding:40,color:th.textSub,fontSize:13}}>No sales found</td></tr>
                          :filteredSales.slice().reverse().map(s=>(
                            <tr key={s.id} style={{borderBottom:`1px solid ${th.cardBorder}`,opacity:s.status==="cancelled"?.5:1,background:s.status==="cancelled"?"rgba(231,76,60,.03)":"transparent"}}
                              onMouseEnter={e=>e.currentTarget.style.background=s.status==="cancelled"?"rgba(231,76,60,.05)":th.tableHover}
                              onMouseLeave={e=>e.currentTarget.style.background=s.status==="cancelled"?"rgba(231,76,60,.03)":"transparent"}>
                              <td style={{padding:"11px 14px",fontSize:12,fontWeight:700,color:"#3a3d80"}}>{s.id}</td>
                              <td style={{padding:"11px 14px"}}>
                                <div style={{fontSize:13,fontWeight:600,color:th.text}}>{s.customer}</div>
                                {s.phone&&<div style={{fontSize:11,color:th.textSub}}>{s.phone}</div>}
                              </td>
                              <td style={{padding:"11px 14px",fontSize:12,color:th.textSub,whiteSpace:"nowrap"}}>
                                <div>{s.date}</div><div style={{fontSize:11}}>{s.time||""}</div>
                              </td>
                              <td style={{padding:"11px 14px",fontSize:14,fontWeight:800,color:s.status==="cancelled"?"#e74c3c":"#3a3d80",textDecoration:s.status==="cancelled"?"line-through":"none",whiteSpace:"nowrap"}}>{fmt(s.total)}</td>
                              <td style={{padding:"11px 14px"}}>
                                <span style={{fontSize:11,padding:"3px 9px",borderRadius:20,fontWeight:700,background:s.payment==="Credit"?"rgba(231,76,60,.12)":"rgba(39,174,96,.1)",color:s.payment==="Credit"?"#e74c3c":"#27ae60"}}>{s.payment}</span>
                              </td>
                              <td style={{padding:"11px 14px"}}>
                                <span style={{fontSize:11,padding:"3px 9px",borderRadius:20,fontWeight:700,background:s.type==="GST"?"rgba(41,128,185,.12)":"rgba(39,174,96,.1)",color:s.type==="GST"?"#2980b9":"#27ae60"}}>{s.type}</span>
                              </td>
                              <td style={{padding:"11px 14px"}}>
                                {s.status==="cancelled"
                                  ?<span style={{fontSize:11,padding:"3px 9px",borderRadius:20,fontWeight:700,background:"rgba(231,76,60,.12)",color:"#e74c3c"}}>Returned</span>
                                  :<span style={{fontSize:11,padding:"3px 9px",borderRadius:20,fontWeight:700,background:"rgba(39,174,96,.12)",color:"#27ae60"}}>Active</span>
                                }
                              </td>
                              <td style={{padding:"11px 14px"}}>
                                <div style={{display:"flex",gap:6}}>
                                  {s.status!=="cancelled"&&<button onClick={()=>showPrint(doPrintA4(s,t,pCfg))} style={{padding:"4px 10px",borderRadius:7,border:"none",background:"rgba(41,128,185,.12)",color:"#2980b9",cursor:"pointer",fontSize:11,fontWeight:700}}>Print</button>}
                                  {s.status!=="cancelled"&&<button onClick={()=>{setRefundSale(s);setRefundReason("");setShowRefundModal(true);}} style={{padding:"4px 10px",borderRadius:7,border:"none",background:"rgba(231,76,60,.12)",color:"#e74c3c",cursor:"pointer",fontSize:11,fontWeight:700}}>Return</button>}
                                </div>
                              </td>
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                  <div style={{padding:"12px 18px",borderTop:`2px solid #3a3d80`,background:th.cardBg,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{fontSize:12,color:th.textSub}}>{filteredSales.filter(s=>s.status!=="cancelled").length} active · {filteredSales.filter(s=>s.status==="cancelled").length} returned</div>
                    <div style={{fontSize:16,fontWeight:900,color:"#3a3d80"}}>{fmt(filteredTotal)}</div>
                  </div>
                </div>

                {/* ── Refund Modal ── */}
                {showRefundModal&&refundSale&&(
                  <div style={{position:"fixed",inset:0,background:"#000b",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
                    <div style={{background:th.modalBg,borderRadius:18,width:"100%",maxWidth:420,boxShadow:"0 24px 80px #0006",overflow:"hidden"}}>
                      <div style={{background:"linear-gradient(135deg,#e74c3c,#c0392b)",padding:"18px 22px",display:"flex",alignItems:"center",gap:12}}>
                        <span style={{fontSize:22}}>↩️</span>
                        <div>
                          <div style={{fontSize:16,fontWeight:800,color:"#fff"}}>Return / Refund</div>
                          <div style={{fontSize:12,color:"rgba(255,255,255,.8)"}}>{refundSale.id} · {refundSale.customer} · {fmt(refundSale.total)}</div>
                        </div>
                      </div>
                      <div style={{padding:"18px 22px"}}>
                        <div style={{fontSize:12,color:th.textSub,marginBottom:10}}>Items that will be restocked:</div>
                        {refundSale.items.map((it,i)=>(
                          <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"6px 0",borderBottom:`1px solid ${th.cardBorder}`}}>
                            <span style={{color:th.text,fontWeight:600}}>{it.name}</span>
                            <span style={{color:"#27ae60",fontWeight:700}}>+{it.qty} units</span>
                          </div>
                        ))}
                        <div style={{marginTop:14,marginBottom:4,fontSize:12,fontWeight:600,color:th.textSub}}>Reason for return</div>
                        <input value={refundReason} onChange={e=>setRefundReason(e.target.value)}
                          placeholder="e.g. Customer returned damaged goods..."
                          style={{...IS,width:"100%",padding:"9px 12px",fontSize:13}}/>
                        <div style={{display:"flex",gap:10,marginTop:16}}>
                          <button onClick={()=>{setShowRefundModal(false);setRefundSale(null);}}
                            style={{flex:1,padding:"11px 0",borderRadius:10,border:`1px solid ${th.cardBorder}`,background:th.appBg,color:th.text,cursor:"pointer",fontSize:14,fontWeight:700}}>
                            Cancel
                          </button>
                          <button onClick={()=>handleRefund(refundSale,refundReason)}
                            style={{flex:1,padding:"11px 0",borderRadius:10,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:14,fontWeight:700}}>
                            ↩ Confirm Return
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ════ REPORTS ════ */}
          {tab==="reports"&&(()=>{
            // ── Report filter state (inline derived) ──
            const now2 = new Date();
            const rSales = sales.filter(s=>{
              const d=new Date(s.date);
              if(srPeriod==="daily")   return s.date===today();
              if(srPeriod==="weekly"){  const w=new Date(now2); w.setDate(w.getDate()-6); return d>=w; }
              if(srPeriod==="monthly"){ return d.getMonth()===now2.getMonth()&&d.getFullYear()===now2.getFullYear(); }
              return true;
            }).filter(s=> srCat==="all" || s.items.some(it=>{const inv=inventory.find(i=>i.name===it.name);return inv&&inv.category===srCat;}));

            const rTotal    = rSales.reduce((s,i)=>s+i.total,0);
            const rGST      = rSales.reduce((s,i)=>s+i.gstAmt,0);
            const rDiscount = rSales.reduce((s,i)=>s+(i.discount||0),0);
            const rProfit   = rSales.reduce((s,b)=>{
              const cost=b.items.reduce((c,bi)=>{const inv=inventory.find(i=>i.name===bi.name);return c+(inv?inv.purchasePrice*bi.qty:0);},0);
              return s+(b.subtotal-cost);
            },0);

            // Category breakdown
            const rCatBreak = cats.map(cat=>{
              const total=rSales.reduce((s,b)=>s+b.items.filter(it=>{const inv=inventory.find(i=>i.name===it.name);return inv&&inv.category===cat.name;}).reduce((a,it)=>a+it.price*it.qty,0),0);
              const qty  =rSales.reduce((s,b)=>s+b.items.filter(it=>{const inv=inventory.find(i=>i.name===it.name);return inv&&inv.category===cat.name;}).reduce((a,it)=>a+it.qty,0),0);
              // Sub-category breakdown
              const subBreak=(cat.subCategories||[]).map(sc=>{
                const st=rSales.reduce((s,b)=>s+b.items.filter(it=>{const inv=inventory.find(i=>i.name===it.name);return inv&&inv.category===cat.name&&inv.subCategory===sc.name;}).reduce((a,it)=>a+it.price*it.qty,0),0);
                const sq=rSales.reduce((s,b)=>s+b.items.filter(it=>{const inv=inventory.find(i=>i.name===it.name);return inv&&inv.category===cat.name&&inv.subCategory===sc.name;}).reduce((a,it)=>a+it.qty,0),0);
                return {name:sc.name,total:st,qty:sq};
              }).filter(s=>s.total>0);
              // Item breakdown
              const itemBreak=inventory.filter(i=>i.category===cat.name).map(item=>{
                const it=rSales.reduce((s,b)=>s+b.items.filter(bi=>bi.name===item.name||bi.name===item.nameTa).reduce((a,bi)=>a+bi.price*bi.qty,0),0);
                const iq=rSales.reduce((s,b)=>s+b.items.filter(bi=>bi.name===item.name||bi.name===item.nameTa).reduce((a,bi)=>a+bi.qty,0),0);
                return {name:item.name,total:it,qty:iq};
              }).filter(i=>i.total>0).sort((a,b)=>b.total-a.total);
              return {name:cat.name,color:cat.color,total,qty,subBreak,itemBreak};
            }).filter(c=>c.total>0).sort((a,b)=>b.total-a.total);

            // Payment breakdown
            const rPayBreak=["Cash","UPI","Card","Credit"].map(p=>({
              name:p,total:rSales.filter(s=>s.payment===p).reduce((s,i)=>s+i.total,0),
              count:rSales.filter(s=>s.payment===p).length
            })).filter(p=>p.total>0);

            // eslint-disable-next-line no-unused-vars
            const periodLabel={daily:"Today",weekly:"This Week",monthly:"This Month",all:"All Time"}[srPeriod];

            // ── Download helpers ──
            const dlCSV=(rows,filename)=>{
              const csv=rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
              const a=document.createElement("a"); a.href="data:text/csv,"+encodeURIComponent(csv);
              a.download=filename; a.click();
            };
            const dlByCategory=()=>dlCSV(
              [["Category","Sub-Category","Total Sales (₹)","Units Sold","% of Total"],
               ...rCatBreak.flatMap(c=>[
                 [c.name,"(All)",c.total,c.qty,rTotal>0?Math.round(c.total/rTotal*100)+"%":"0%"],
                 ...c.subBreak.map(s=>[c.name,s.name,s.total,s.qty,rTotal>0?Math.round(s.total/rTotal*100)+"%":"0%"])
               ])],
              `sales-by-category-${srPeriod}-${today()}.csv`
            );
            const dlByItem=()=>dlCSV(
              [["Item Name","Category","Sub-Category","Total Sales (₹)","Units Sold"],
               ...rCatBreak.flatMap(c=>c.itemBreak.map(i=>{
                 const inv=inventory.find(iv=>iv.name===i.name);
                 return [i.name,c.name,inv?.subCategory||"",i.total,i.qty];
               }))],
              `sales-by-item-${srPeriod}-${today()}.csv`
            );
            const dlInvoices=()=>dlCSV(
              [["Invoice ID","Customer","Date","Time","Items","Subtotal","GST","Discount","Total","Payment","Type"],
               ...rSales.map(s=>[s.id,s.customer,s.date,s.time||"",s.items.map(i=>i.name+"x"+i.qty).join("; "),s.subtotal,s.gstAmt,s.discount||0,s.total,s.payment,s.type])],
              `invoices-${srPeriod}-${today()}.csv`
            );

            return (
            <div>
              {/* ── Period + Category filters ── */}
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
                {[{k:"daily",l:"Today"},{k:"weekly",l:"This Week"},{k:"monthly",l:"This Month"},{k:"all",l:"All Time"}].map(p=>(
                  <button key={p.k} onClick={()=>setSrPeriod(p.k)} style={{padding:"8px 16px",borderRadius:20,border:srPeriod===p.k?"none":`1px solid ${th.ghostBorder}`,background:srPeriod===p.k?"linear-gradient(135deg,#3a3d80,#e74c3c)":th.ghostBg,color:srPeriod===p.k?"#fff":th.ghostText,cursor:"pointer",fontSize:13,fontWeight:600}}>{p.l}</button>
                ))}
                <select value={srCat} onChange={e=>setSrCat(e.target.value)} style={{...IS,flex:1,minWidth:140,maxWidth:220}}>
                  <option value="all">All Categories</option>
                  {cats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <div style={{marginLeft:"auto",fontSize:13,color:th.textSub}}>
                  <span style={{fontWeight:700,color:th.textHeading}}>{rSales.length}</span> invoices · <span style={{fontWeight:700,color:"#3a3d80"}}>{fmt(rTotal)}</span>
                </div>
              </div>

              {/* ── KPI Cards ── */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:16}}>
                {[
                  {l:"Total Sales",    v:fmt(rTotal),    c:"#3a3d80",icon:"💰"},
                  {l:"Est. Profit",    v:fmt(rProfit),   c:"#27ae60",icon:"📈"},
                  {l:"GST Collected",  v:fmt(rGST),      c:"#2980b9",icon:"🏛️"},
                  {l:"Discounts Given",v:fmt(rDiscount), c:"#e74c3c",icon:"🏷"},
                ].map((k,i)=>(
                  <div key={i} style={{...card,padding:"14px 16px",borderColor:`${k.c}33`}}>
                    <div style={{fontSize:18,marginBottom:4}}>{k.icon}</div>
                    <div style={{fontSize:10,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:3}}>{k.l}</div>
                    <div style={{fontSize:20,fontWeight:900,color:k.c}}>{k.v}</div>
                  </div>
                ))}
              </div>

              {/* ── Download buttons ── */}
              <div style={{...card,padding:"14px 16px",marginBottom:16,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
                <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>⬇ Download Report:</div>
                <button onClick={dlByCategory} style={{...btn("#8e44ad"),fontSize:12,padding:"8px 14px"}}>📦 By Category / Sub-Cat</button>
                <button onClick={dlByItem}     style={{...btn("#2980b9"),fontSize:12,padding:"8px 14px"}}>🏷 By Item</button>
                <button onClick={dlInvoices}   style={{...btn("#27ae60"),fontSize:12,padding:"8px 14px"}}>🧾 All Invoices</button>
              </div>

              {/* ── Payment method breakdown ── */}
              {rPayBreak.length>0&&(
                <div style={{...card,padding:16,marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:12}}>💳 By Payment Method</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10}}>
                    {rPayBreak.map(p=>(
                      <div key={p.name} style={{background:th.appBg,borderRadius:10,padding:"12px 14px",border:`1px solid ${th.cardBorder}`}}>
                        <div style={{fontSize:12,color:th.textSub,marginBottom:3}}>{p.name}</div>
                        <div style={{fontSize:17,fontWeight:800,color:p.name==="Credit"?"#e74c3c":"#27ae60"}}>{fmt(p.total)}</div>
                        <div style={{fontSize:11,color:th.textSub}}>{p.count} invoice{p.count!==1?"s":""}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Category / Sub-category / Item breakdown ── */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:10}}>📦 Sales by Category</div>
                {rCatBreak.length===0
                  ?<div style={{...card,padding:24,textAlign:"center",color:th.textSub}}>No sales in this period</div>
                  :rCatBreak.map(cat=>{
                    const pct=rTotal>0?Math.round((cat.total/rTotal)*100):0;
                    return (
                      <div key={cat.name} style={{...card,padding:16,marginBottom:10}}>
                        {/* Category header */}
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                          <div style={{display:"flex",alignItems:"center",gap:8}}>
                            <div style={{width:12,height:12,borderRadius:3,background:cat.color,flexShrink:0}}/>
                            <span style={{fontSize:14,fontWeight:800,color:cat.color}}>{cat.name}</span>
                          </div>
                          <div style={{textAlign:"right"}}>
                            <span style={{fontSize:15,fontWeight:800,color:cat.color}}>{fmt(cat.total)}</span>
                            <span style={{fontSize:11,color:th.textSub,marginLeft:6}}>({pct}%) · {cat.qty} units</span>
                          </div>
                        </div>
                        <div style={{background:th.appBg,borderRadius:20,height:8,overflow:"hidden",marginBottom:12}}>
                          <div style={{width:`${pct}%`,height:"100%",borderRadius:20,background:cat.color,transition:"width .6s ease"}}/>
                        </div>
                        {/* Sub-category rows */}
                        {cat.subBreak.length>0&&(
                          <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:10,marginBottom:cat.itemBreak.length>0?10:0}}>
                            <div style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Sub-Categories</div>
                            {cat.subBreak.map(sc=>{
                              const sp=cat.total>0?Math.round((sc.total/cat.total)*100):0;
                              return (
                                <div key={sc.name} style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:3}}>
                                      <span style={{color:th.text,fontWeight:600}}>{sc.name}</span>
                                      <span style={{color:th.textSub}}>{fmt(sc.total)} · {sc.qty} units</span>
                                    </div>
                                    <div style={{background:th.appBg,borderRadius:10,height:5,overflow:"hidden"}}>
                                      <div style={{width:`${sp}%`,height:"100%",borderRadius:10,background:`${cat.color}99`}}/>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Item rows */}
                        {cat.itemBreak.length>0&&(
                          <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:10}}>
                            <div style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:6}}>Items</div>
                            {cat.itemBreak.map(item=>{
                              const ip=cat.total>0?Math.round((item.total/cat.total)*100):0;
                              return (
                                <div key={item.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${th.cardBorder}33`,fontSize:12}}>
                                  <span style={{color:th.text}}>{item.name}</span>
                                  <span style={{color:th.textSub}}>{item.qty} units · <span style={{color:"#3a3d80",fontWeight:700}}>{fmt(item.total)}</span> <span style={{color:th.textSub}}>({ip}%)</span></span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                }
              </div>

              {/* ── Customer Sales Report ── */}
              {customers.length>0&&(
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:10}}>👥 Sales by Customer</div>
                  {(()=>{
                    const custRows = customers.map(c=>{
                      const cSales = rSales.filter(s=>s.phone&&s.phone===c.phone);
                      const total  = cSales.reduce((a,s)=>a+s.total,0);
                      const count  = cSales.length;
                      const last   = cSales.length>0?cSales.slice().sort((a,b)=>new Date(b.date)-new Date(a.date))[0].date:"—";
                      return {c,total,count,last};
                    }).filter(r=>r.count>0).sort((a,b)=>b.total-a.total);
                    if(!custRows.length) return <div style={{...card,padding:24,textAlign:"center",color:th.textSub}}>No customer sales in this period</div>;
                    return (
                      <div style={{...card,overflow:"hidden"}}>
                        <table style={{width:"100%",borderCollapse:"collapse"}}>
                          <thead>
                            <tr style={{background:th.tableThead}}>
                              {["#","Customer","Phone","Address","Bills","Total Spend","Last Purchase"].map(h=>(
                                <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.6,whiteSpace:"nowrap"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {custRows.map(({c,total,count,last},i)=>(
                              <tr key={c.id} style={{borderBottom:`1px solid ${th.cardBorder}`}}
                                onMouseEnter={e=>e.currentTarget.style.background=th.tableHover}
                                onMouseLeave={e=>e.currentTarget.style.background=""}>
                                <td style={{padding:"10px 14px",fontSize:12,color:th.textSub,fontWeight:700}}>{i+1}</td>
                                <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:th.text}}>{c.name}</td>
                                <td style={{padding:"10px 14px",fontSize:12,color:th.textSub}}>{c.phone||"—"}</td>
                                <td style={{padding:"10px 14px",fontSize:12,color:th.textSub,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.address||"—"}</td>
                                <td style={{padding:"10px 14px",fontSize:13,fontWeight:700,color:"#3a3d80"}}>{count}</td>
                                <td style={{padding:"10px 14px",fontSize:14,fontWeight:800,color:"#27ae60"}}>{fmt(total)}</td>
                                <td style={{padding:"10px 14px",fontSize:12,color:th.textSub}}>{last}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}

            </div>
            );
          })()}

        </div>
      </div>

      {/* ── INVOICE EDIT MODAL ── */}
      {showInvEdit&&editingInv&&invEditForm&&(
        <div style={{position:"fixed",inset:0,background:"#000c",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:20,width:"100%",maxWidth:600,maxHeight:"92vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 80px #0006",overflow:"hidden"}}>
            {/* Header */}
            <div style={{padding:"18px 22px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
              <div style={{flex:1}}>
                <div style={{fontSize:17,fontWeight:800,color:th.textHeading,display:"flex",alignItems:"center"}}><PenIcon/> Edit Invoice</div>
                <div style={{fontSize:12,color:th.textSub,marginTop:2}}>{editingInv.id} · {editingInv.date}</div>
              </div>
              <button onClick={()=>{setShowInvEdit(false);setEditingInv(null);setInvEditForm(null);}} style={{background:"none",border:"none",fontSize:22,color:th.textSub,cursor:"pointer"}}>✕</button>
            </div>

            <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>
              {/* Customer details */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                <div>
                  <label style={{fontSize:11,color:th.textSub,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.7}}>Customer Name</label>
                  <input value={invEditForm.customer} onChange={e=>setInvEditForm({...invEditForm,customer:e.target.value})} style={{...IS,width:"100%"}}/>
                </div>
                <div>
                  <label style={{fontSize:11,color:th.textSub,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.7}}>Phone</label>
                  <input value={invEditForm.phone} onChange={e=>setInvEditForm({...invEditForm,phone:e.target.value})} style={{...IS,width:"100%"}}/>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                <div>
                  <label style={{fontSize:11,color:th.textSub,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.7}}>Invoice Type</label>
                  <div style={{display:"flex",gap:6}}>
                    {["GST","Non-GST"].map(tp=>(
                      <button key={tp} onClick={()=>setInvEditForm({...invEditForm,type:tp})} style={{flex:1,padding:"8px 0",borderRadius:8,border:invEditForm.type===tp?"none":`1px solid ${th.ghostBorder}`,cursor:"pointer",fontSize:12,fontWeight:600,background:invEditForm.type===tp?"linear-gradient(135deg,#3a3d80,#e74c3c)":th.ghostBg,color:invEditForm.type===tp?"#fff":th.ghostText}}>{tp}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{fontSize:11,color:th.textSub,display:"block",marginBottom:4,textTransform:"uppercase",letterSpacing:.7}}>Payment</label>
                  <select value={invEditForm.payment} onChange={e=>setInvEditForm({...invEditForm,payment:e.target.value})} style={{...IS,width:"100%"}}>
                    {["Cash","UPI","Card","Credit"].map(p=><option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* Items editor */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Items</div>
                <div style={{...card,overflow:"hidden"}}>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 32px",gap:0,padding:"8px 12px",background:th.appBg,borderBottom:`1px solid ${th.cardBorder}`}}>
                    {["Item","Qty","Price",""].map((h,i)=><div key={i} style={{fontSize:11,fontWeight:700,color:th.textSub}}>{h}</div>)}
                  </div>
                  {invEditForm.items.map((it,idx)=>(
                    <div key={idx} style={{display:"grid",gridTemplateColumns:"1fr 80px 80px 32px",gap:0,padding:"8px 12px",borderBottom:`1px solid ${th.cardBorder}`,alignItems:"center"}}>
                      <div style={{fontSize:13,fontWeight:600,color:th.text}}>{it.name}</div>
                      <input type="number" min="1" value={it.qty}
                        onChange={e=>setInvEditForm({...invEditForm,items:invEditForm.items.map((x,i)=>i===idx?{...x,qty:Math.max(1,+e.target.value)}:x)})}
                        style={{...IS,padding:"5px 8px",fontSize:13,textAlign:"center"}}/>
                      <div style={{fontSize:13,fontWeight:700,color:"#3a3d80",textAlign:"right"}}>₹{it.price*it.qty}</div>
                      <button onClick={()=>setInvEditForm({...invEditForm,items:invEditForm.items.filter((_,i)=>i!==idx)})}
                        style={{background:"none",border:"none",color:"#e74c3c",cursor:"pointer",fontSize:18,padding:0,textAlign:"center"}}>✕</button>
                    </div>
                  ))}
                  {invEditForm.items.length===0&&<div style={{padding:16,textAlign:"center",color:th.textSub,fontSize:13}}>No items — add below</div>}
                </div>
                {/* Add item from inventory */}
                <div style={{marginTop:8}}>
                  <select style={{...IS,width:"100%"}} defaultValue=""
                    onChange={e=>{
                      const inv=inventory.find(i=>i.id===e.target.value);
                      if(inv&&!invEditForm.items.find(it=>it.name===inv.name)){
                        setInvEditForm({...invEditForm,items:[...invEditForm.items,{name:inv.name,qty:1,price:inv.salePrice,gst:inv.gst||0}]});
                      }
                      e.target.value="";
                    }}>
                    <option value="">+ Add item from inventory...</option>
                    {inventory.filter(i=>i.stock>0&&!invEditForm.items.find(it=>it.name===i.name)).map(i=>(
                      <option key={i.id} value={i.id}>{i.name} — ₹{i.salePrice} (Stock: {i.stock})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Discount */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <label style={{fontSize:12,color:th.textSub,minWidth:80}}>Discount (₹)</label>
                <input type="number" min="0" value={invEditForm.discount}
                  onChange={e=>setInvEditForm({...invEditForm,discount:+e.target.value})}
                  style={{...IS,width:120}}/>
              </div>

              {/* Totals preview */}
              {(()=>{
                const sub=invEditForm.items.reduce((s,it)=>s+it.price*it.qty,0);
                const gst=invEditForm.type==="GST"?invEditForm.items.reduce((s,it)=>s+(it.price*it.qty*(it.gst||0)/100),0):0;
                const tot=sub+gst-Number(invEditForm.discount||0);
                return (
                  <div style={{...card,padding:"12px 16px",marginBottom:16,background:th.appBg}}>
                    {[["Subtotal",fmt(sub)],["GST",fmt(gst)],["Discount",`−${fmt(invEditForm.discount||0)}`]].map(([l,v])=>(
                      <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:th.textSub,marginBottom:4}}><span>{l}</span><span>{v}</span></div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",borderTop:`2px solid #3a3d80`,paddingTop:8,marginTop:4}}>
                      <span style={{fontSize:14,fontWeight:700,color:th.textHeading}}>New Total</span>
                      <span style={{fontSize:20,fontWeight:900,color:"#3a3d80"}}>{fmt(tot)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* Cancel section */}
              <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:14}}>
                <div style={{fontSize:12,fontWeight:700,color:"#e74c3c",marginBottom:8}}>Cancel Invoice</div>
                <input value={invEditForm.cancelReason} onChange={e=>setInvEditForm({...invEditForm,cancelReason:e.target.value})}
                  placeholder="Enter reason to cancel (required)..."
                  style={{...IS,width:"100%",marginBottom:8,borderColor:invEditForm.cancelReason?"#e74c3c":th.inputBorder}}/>
                <button onClick={cancelInvoice} disabled={!invEditForm.cancelReason?.trim()}
                  style={{...btn("#e74c3c"),width:"100%",opacity:invEditForm.cancelReason?.trim()?1:.4}}>
                  🚫 Cancel Invoice & Restore Stock
                </button>
              </div>
            </div>

            {/* Footer actions */}
            <div style={{padding:"14px 22px",borderTop:`1px solid ${th.cardBorder}`,display:"flex",gap:10,flexShrink:0}}>
              <button onClick={()=>{setShowInvEdit(false);setEditingInv(null);setInvEditForm(null);}} style={{...ghost,flex:1}}>Discard</button>
              <button onClick={saveInvEdit} disabled={invEditForm.items.length===0} style={{...btn("#27ae60"),flex:2,opacity:invEditForm.items.length?1:.4}}>
                ✅ Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD/EDIT ITEM MODAL ── */}
      {showItemModal&&(
        <Modal title={editingItem?<span style={{display:"flex",alignItems:"center"}}><PenIcon/>{t.editItem}</span>:`+ ${t.addItem}`} onClose={()=>setShowItemModal(false)} width={540} th={th}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
            <Field label={`${t.itemName} (EN)`} th={th}><input value={itemForm.name||""} onChange={e=>setItemForm({...itemForm,name:e.target.value})} style={IS} placeholder="e.g. Sparklers Box"/></Field>
            <Field label={`${t.itemName} (Tamil)`} th={th}><input value={itemForm.nameTa||""} onChange={e=>setItemForm({...itemForm,nameTa:e.target.value})} style={IS} placeholder="e.g. மெழுகுவர்த்தி"/></Field>
            <Field label={t.itemCode} th={th}><input value={itemForm.itemCode||""} onChange={e=>setItemForm({...itemForm,itemCode:e.target.value})} style={IS} placeholder="e.g. CRK001"/></Field>
            <Field label={t.brand} th={th}><input value={itemForm.brand||""} onChange={e=>setItemForm({...itemForm,brand:e.target.value})} style={IS} placeholder="e.g. Standard"/></Field>
          </div>
          {/* Product Image Upload */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,color:th.textSub,marginBottom:5,textTransform:"uppercase",letterSpacing:.8}}>Product Image (for mobile billing)</label>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <div style={{width:80,height:80,borderRadius:12,background:itemForm.image?`url(${itemForm.image}) center/cover no-repeat`:th.innerCard,border:`2px dashed ${itemForm.image?"#3a3d80":th.cardBorder}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,color:th.textSub}}>
                {!itemForm.image&&"📷"}
              </div>
              <div style={{flex:1}}>
                <label style={{...btn("#2980b9"),fontSize:12,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,marginBottom:6}}>
                  📷 Upload Image
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                    const file=e.target.files[0]; if(!file) return;
                    const reader=new FileReader();
                    reader.onload=ev=>setItemForm(f=>({...f,image:ev.target.result}));
                    reader.readAsDataURL(file);
                    e.target.value="";
                  }}/>
                </label>
                {itemForm.image&&<button onClick={()=>setItemForm(f=>({...f,image:""}))} style={{...ghost,fontSize:11,padding:"4px 10px",color:"#e74c3c",marginLeft:8}}>Remove</button>}
                <div style={{fontSize:11,color:th.textSub,marginTop:2}}>JPG, PNG · Shows on mobile billing screen</div>
              </div>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
            <Field label={t.category} th={th}>
              <select value={itemForm.category||""} onChange={e=>setItemForm({...itemForm,category:e.target.value,subCategory:""})} style={{...IS}}>
                {cats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
                <option value="Uncategorized">📦 Uncategorized</option>
              </select>
            </Field>
            <Field label={t.subCategory} th={th}>
              <select value={itemForm.subCategory||""} onChange={e=>setItemForm({...itemForm,subCategory:e.target.value})} style={{...IS}}>
                <option value="">— None —</option>
                {(cats.find(c=>c.name===itemForm.category)?.subCategories||[]).map(sc=><option key={sc.id} value={sc.name}>{sc.name}</option>)}
              </select>
            </Field>
            <Field label={t.unit} th={th}>
              <select value={itemForm.unit||"Piece"} onChange={e=>setItemForm({...itemForm,unit:e.target.value})} style={{...IS}}>
                {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label={`${t.gstRate} (%)`} th={th}>
              <select value={itemForm.gst??18} onChange={e=>setItemForm({...itemForm,gst:+e.target.value})} style={{...IS}}>
                {GST_RATES.map(r=><option key={r} value={r}>{r}%</option>)}
              </select>
            </Field>
            <Field label={`${t.salePrice} (₹)`} th={th}><input type="number" value={itemForm.salePrice||""} onChange={e=>setItemForm({...itemForm,salePrice:e.target.value})} style={IS} placeholder="0"/></Field>
            <Field label={`${t.purchasePrice} (₹)`} th={th}><input type="number" value={itemForm.purchasePrice||""} onChange={e=>setItemForm({...itemForm,purchasePrice:e.target.value})} style={IS} placeholder="0"/></Field>
            <Field label={t.stock} th={th}><input type="number" value={itemForm.stock||""} onChange={e=>setItemForm({...itemForm,stock:e.target.value})} style={IS} placeholder="0"/></Field>
            <Field label={t.hsn} th={th}><input value={itemForm.hsn||""} onChange={e=>setItemForm({...itemForm,hsn:e.target.value})} style={{...IS}} placeholder="e.g. 3604"/></Field>
          </div>
          <div style={{display:"flex",gap:10,marginTop:20,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowItemModal(false)} style={ghost}>{t.cancel}</button>
            <button onClick={saveItem} style={btn("#3a3d80")}>{t.save}</button>
          </div>
        </Modal>
      )}



      {/* ── CUSTOMER MODAL ── */}
      {showCustModal&&(
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:16,width:"100%",maxWidth:480,boxShadow:"0 24px 80px #0006",overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"90vh"}}>
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:20}}>👤</span>
              <div style={{flex:1,fontSize:16,fontWeight:800,color:th.textHeading}}>{editingCust?"Edit Customer":"Add Customer"}</div>
              <button onClick={()=>setShowCustModal(false)} style={{background:"none",border:"none",fontSize:20,color:th.textSub,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"18px 20px",display:"flex",flexDirection:"column",gap:12,overflowY:"auto"}}>
              {[{f:"name",l:"Name *",p:"Customer name"},{f:"phone",l:"Phone *",p:"+91 98765 43210"},{f:"address",l:"Address",p:"Street, City"},{f:"pan",l:"PAN (optional)",p:"ABCDE1234F"},{f:"gstin",l:"GSTIN (optional)",p:"33XXXXX1234X1ZX"},{f:"aadhar",l:"Aadhar (optional)",p:"1234 5678 9012"},{f:"email",l:"Email (optional)",p:"email@example.com"},{f:"notes",l:"Notes (optional)",p:"Any notes..."}].map(({f,l,p})=>(
                <div key={f}>
                  <label style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.6,display:"block",marginBottom:4}}>{l}</label>
                  {f==="notes"
                    ? <textarea value={custForm[f]} onChange={e=>setCustForm({...custForm,[f]:e.target.value})} placeholder={p} rows={2} style={{...IS,width:"100%",resize:"vertical",fontSize:13}}/>
                    : <input value={custForm[f]} onChange={e=>setCustForm({...custForm,[f]:e.target.value})} placeholder={p} style={{...IS,width:"100%",fontSize:13}}/>
                  }
                </div>
              ))}
            </div>
            <div style={{padding:"12px 20px",borderTop:`1px solid ${th.cardBorder}`,display:"flex",gap:8}}>
              <button onClick={()=>setShowCustModal(false)} style={{flex:1,padding:"10px",borderRadius:8,border:`1px solid ${th.cardBorder}`,background:th.appBg,color:th.text,cursor:"pointer",fontSize:13,fontWeight:600}}>Cancel</button>
              <button onClick={()=>{
                if(!custForm.name.trim()){showToast("Name is required");return;}
                if(editingCust){
                  setCustomers(customers.map(c=>c.id===editingCust.id?{...c,...custForm}:c));
                  showToast("Customer updated");
                } else {
                  setCustomers([...customers,{...custForm,id:Date.now(),createdAt:today()}]);
                  showToast("Customer added");
                }
                setShowCustModal(false);
              }} style={{flex:2,padding:"10px",borderRadius:8,border:"none",background:"linear-gradient(135deg,#3a3d80,#2980b9)",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700}}>
                {editingCust?"Save Changes":"Add Customer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BILL SUCCESS DIALOG ── */}
      {/* ── SETTINGS MODAL ── */}
      {showSettings&&(
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:20,width:"100%",maxWidth:460,boxShadow:"0 24px 80px #0006",overflow:"hidden",display:"flex",flexDirection:"column",maxHeight:"90vh"}}>
            {/* Header */}
            <div style={{padding:"18px 22px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:22}}>⚙️</span>
              <div style={{flex:1,fontSize:17,fontWeight:800,color:th.textHeading}}>Settings & Defaults</div>
              <button onClick={()=>setShowSettings(false)} style={{background:"none",border:"none",fontSize:22,color:th.textSub,cursor:"pointer"}}>✕</button>
            </div>
            <div style={{padding:"20px 22px",display:"flex",flexDirection:"column",gap:18,overflowY:"auto"}}>

              {/* Default Theme */}


              {/* Default Invoice Type */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🧾 Default Invoice Type</div>
                <div style={{display:"flex",gap:8}}>
                  {["GST","Non-GST"].map(opt=>(
                    <button key={opt} onClick={()=>{setDefInvType(opt);setBillType(opt);}}
                      style={{flex:1,padding:"10px 12px",borderRadius:10,border:defInvType===opt?`2px solid #2980b9`:`1px solid ${th.cardBorder}`,background:defInvType===opt?"rgba(41,128,185,.1)":th.appBg,color:defInvType===opt?"#2980b9":th.text,cursor:"pointer",textAlign:"center",fontWeight:700,fontSize:14}}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Default Bill Label */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🧾 Default Bill Label</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {["TAX INVOICE","BILL / RECEIPT","ESTIMATE","DELIVERY NOTE","PROFORMA INVOICE"].map(tp=>(
                    <button key={tp} onClick={()=>{setDefBillInvoiceType(tp);setBillInvoiceType(tp);}}
                      style={{padding:"7px 12px",borderRadius:8,border:defBillInvoiceType===tp?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:defBillInvoiceType===tp?"rgba(58,61,128,.1)":th.appBg,color:defBillInvoiceType===tp?"#3a3d80":th.text,cursor:"pointer",fontWeight:defBillInvoiceType===tp?700:500,fontSize:12}}>
                      {tp}
                    </button>
                  ))}
                </div>
              </div>

              {/* Default Language */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🌐 Default Language</div>
                <div style={{display:"flex",gap:8}}>
                  {[{k:"en",l:"English",f:"EN"},{k:"ta",l:"Tamil · தமிழ்",f:"த"}].map(opt=>(
                    <button key={opt.k} onClick={()=>{setDefLang(opt.k);setLang(opt.k);}}
                      style={{flex:1,padding:"10px 12px",borderRadius:10,border:defLang===opt.k?`2px solid #8e44ad`:`1px solid ${th.cardBorder}`,background:defLang===opt.k?"rgba(142,68,173,.1)":th.appBg,color:defLang===opt.k?"#8e44ad":th.text,cursor:"pointer",textAlign:"center"}}>
                      <div style={{fontSize:14,fontWeight:700}}>{opt.l}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Store Selection */}
              {currentUser?.role==="owner"&&(
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🏪 Store</div>
                  <button onClick={()=>{setShowSettings(false);onSwitchStore();}}
                    style={{width:"100%",padding:"10px 14px",borderRadius:10,border:`1px solid ${th.cardBorder}`,background:th.appBg,color:th.text,cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:10,fontSize:14,fontWeight:600}}>
                    <span>🏪</span>
                    <span style={{flex:1}}>{storeName||"Store"}</span>
                    <span style={{fontSize:12,color:th.textSub}}>Switch →</span>
                  </button>
                </div>
              )}


              {/* Print Setup */}
              <div>
                <div style={{fontSize:12,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>🖨️ Print Setup</div>
                <div style={{background:th.appBg,borderRadius:12,border:`1px solid ${th.cardBorder}`,padding:"14px 16px",display:"flex",flexDirection:"column",gap:10}}>
                  <div style={{display:"flex",alignItems:"center",gap:14}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:th.text,marginBottom:3}}>Print Layout &amp; Templates</div>
                      <div style={{fontSize:11,color:th.textSub}}>Configure logo, invoice type, colours, header/footer, live preview</div>
                    </div>
                    <button onClick={()=>{setPrintSetupTab(printPaperSize||"A4");setShowSettings(false);setShowPrintSetup(true);}}
                      style={{padding:"9px 18px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#3a3d80,#e74c3c)",color:"#fff",cursor:"pointer",fontSize:13,fontWeight:700,flexShrink:0,whiteSpace:"nowrap"}}>
                      Open →
                    </button>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,borderTop:`1px solid ${th.cardBorder}`,paddingTop:10}}>
                    <span style={{fontSize:12,fontWeight:700,color:th.textSub,whiteSpace:"nowrap"}}>Default Paper Size</span>
                    <div style={{display:"flex",gap:6,flex:1}}>
                      {["A4","A5","80mm"].map(sz=>{
                        const active = (printPaperSize||"A4")===sz;
                        return (
                          <button key={sz} onClick={()=>{setPrintPaperSize(sz);setDefPrintMode(sz==="80mm"?"80mm":"A4");setPrintSetupTab(sz);}}
                            style={{flex:1,padding:"6px 0",borderRadius:7,border:active?`2px solid #3a3d80`:`1.5px solid ${th.cardBorder}`,background:active?"#3a3d80":th.cardBg,color:active?"#fff":th.text,cursor:"pointer",fontSize:13,fontWeight:active?800:600,transition:"all .15s"}}>
                            {sz}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div style={{background:th.appBg,borderRadius:10,padding:"10px 14px",border:`1px solid ${th.cardBorder}`,fontSize:12,color:th.textSub}}>
                ℹ️ These settings apply immediately and will be used as defaults when you open a new bill.
              </div>

              <button onClick={()=>setShowSettings(false)} style={{...btn("#3a3d80"),width:"100%",padding:"13px 0",fontSize:15,fontWeight:800}}>
                ✅ Save & Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PRINT SETUP WINDOW ── */}
      {showPrintSetup&&(()=>{
        // Sample bill for preview
        const previewBill = {
          id:"INV-2026-0042", customer:"Sample Customer", phone:"98765 43210",
          items:[
            {name:"Sparklers Box",qty:2,price:120,gst:5},
            {name:"Flower Pot 10pcs",qty:1,price:250,gst:0},
            {name:"Ground Chakkar",qty:3,price:80,gst:5},
          ],
          subtotal:570, gstAmt:20, discount:50, total:540,
          type:(printSizeConfigs&&printSizeConfigs[printSetupTab]&&printSizeConfigs[printSetupTab].invoiceType)==="TAX INVOICE"?"GST":"NON-GST",
          payment:"Cash", date:"24/03/2026", time:"10:30 AM", cashier:"Admin"
        };

        const activeTab   = printSetupTab;
        const activeCfg   = (printSizeConfigs&&printSizeConfigs[activeTab])||{};
        const updateCfg   = (field,val) => setPrintSizeConfigs(prev=>({...prev,[activeTab]:{...(prev[activeTab]||{}), [field]:val}}));
        const is80mm      = activeTab==="80mm";
        const isA5        = activeTab==="A5";
        const isLandscape = activeCfg.orientation==="landscape" && !is80mm;
        const themeColor  = {red:"#e74c3c",blue:"#2980b9",green:"#27ae60",dark:"#2c3e50",purple:"#8e44ad"}[activeCfg.colorTheme]||"#e74c3c";
        const fSize       = {small:"11",medium:"12",large:"15"}[activeCfg.fontSize]||activeCfg.fontSize||"12";
        const hFSize      = activeCfg.headerFontSize||"11";
        const prevGrey    = activeCfg.greyscale||false;

        // Paper dimensions at 96dpi
        const paperW = is80mm ? 302 : isA5 ? (isLandscape?794:559) : (isLandscape?1123:794);
        const paperH = is80mm ? 600 : isA5 ? (isLandscape?559:794) : (isLandscape?794:1123);

        const gR = previewBill.type==="GST"?`<tr><td colspan="5" align="right" style="padding:4px 8px;color:#555">GST</td><td align="right" style="padding:4px 8px;font-weight:600">₹${previewBill.gstAmt.toFixed(2)}</td></tr>`:"";
        const dR = previewBill.discount>0?`<tr><td colspan="5" align="right" style="padding:4px 8px;color:#c0392b">Discount</td><td align="right" style="padding:4px 8px;color:#c0392b">-₹${previewBill.discount.toFixed(2)}</td></tr>`:"";

        // Logo stays full-colour in both modes; only the two ink-heavy blocks change in greyscale
        const prevLogoHtml   = printLogoUrl ? `<img class="logo" src="${printLogoUrl}" style="height:${printLogoSize||60}px;width:auto;object-fit:contain;"/>` : `<div style="font-size:${isA5?"16":"20"}px;font-weight:900;color:${prevGrey?"#111":themeColor}">🎆 Nethran Crackers</div>`;
        const prevThStyle    = prevGrey ? `background:none;color:#111;padding:7px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.8px;border-top:2px solid #333;border-bottom:2px solid #333;` : `background:${themeColor};color:#fff;padding:7px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.8px`;
        const prevGtStyle    = prevGrey ? `background:none;color:#111;font-size:14px;font-weight:800;border-top:2.5px solid #333;border-bottom:2.5px solid #333;` : `background:${themeColor};color:#fff;font-size:14px;font-weight:800`;
        const prevAddress    = storeAddressLine || "Srivilliputhur, Tamil Nadu";
        const prevPhone      = storePhone || "+91 98765 43210";
        const prevGstin      = storeGSTIN || "";
        const prevEmail      = storeEmail ? `<br>${storeEmail}` : "";
        const prevQrHtml     = activeCfg.qrImage ? `<div class="c" style="margin:6px 0"><img src="${activeCfg.qrImage}" style="width:${activeCfg.qrSize||60}px;height:${activeCfg.qrSize||60}px;object-fit:contain;${prevGrey?"filter:grayscale(100%)":""}"/><div class="c" style="font-size:8px;margin-top:2px">${activeCfg.qrLabel||"Scan to find us"}</div></div>` : "";
        const prevQrTop      = activeCfg.qrImage && activeCfg.qrPosition==="top" ? prevQrHtml : "";
        const prevQrBottom   = activeCfg.qrImage && activeCfg.qrPosition!=="top" ? prevQrHtml : "";

        const a4Html = `<!DOCTYPE html><html><head><title>Preview</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',sans-serif;font-size:${fSize}px;padding:12px 16px;color:#222;width:${paperW}px;${prevGrey?"filter:grayscale(100%);":""}}img.logo{filter:none!important}
@page{size:${isA5?"A5":"A4"} ${isLandscape?"landscape":"portrait"};margin:10mm}
.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${prevGrey?"#333":themeColor};padding-bottom:8px;margin-bottom:6px}
.ss{font-size:10px;color:#888;letter-spacing:1px;margin-top:2px}
.si{font-size:${activeCfg.detailFontSize||"11"}px;color:#555;margin-top:2px;line-height:1.3}.it{font-size:${isA5?"14":"18"}px;font-weight:800;text-align:right;color:${prevGrey?"#111":themeColor}}
.im{font-size:${activeCfg.detailFontSize||"11"}px;color:#555;margin-top:2px;text-align:right;line-height:1.3}
.bt{background:#f9f9f9;border-radius:6px;padding:4px 10px;margin-bottom:6px;font-size:${activeCfg.detailFontSize||"11"}px;color:#444;line-height:1.4}
table{width:100%;border-collapse:collapse}th{${prevThStyle}}
td{padding:7px 8px;border-bottom:1px solid #eee;font-size:${fSize}px}tfoot tr:last-child td{${prevGtStyle}}
.ft{margin-top:18px;text-align:center;font-size:10px;color:#999;border-top:1px dashed #ccc;padding-top:8px;white-space:pre-line}
.gst-box{margin-top:8px;border:1px solid #ddd;border-radius:6px;padding:6px 10px;font-size:10px;color:#555;background:#fafafa}
</style></head><body>
${activeCfg.headerText?`<div style="text-align:center;font-size:${hFSize}px;color:#555;margin-bottom:8px;white-space:pre-line">${activeCfg.headerText}</div>`:""}
<div class="hdr">
  <div>${prevLogoHtml}
  <div class="si">${prevAddress}<br>${prevPhone}${prevEmail}${prevGstin?`<br><b>GSTIN: ${prevGstin}</b>`:""}${storeExtraLines.filter(Boolean).map(l=>`<br>${l}`).join("")}</div></div>
  <div style="padding-top:20px"><div class="it">${activeCfg.invoiceType||"TAX INVOICE"}</div>
  ${(()=>{const lbl={"TAX INVOICE":"Invoice No","BILL / RECEIPT":"Bill No","ESTIMATE":"Estimate No","DELIVERY NOTE":"DN No","PROFORMA INVOICE":"Proforma No"}[activeCfg.invoiceType||"TAX INVOICE"]||"Invoice No";return`<div class="im"><b>${lbl}:</b> ${previewBill.id}<br><b>Date:</b> ${previewBill.date}<br><b>Time:</b> ${previewBill.time}${activeCfg.showPayment!==false?`<br><b>Payment:</b> ${previewBill.payment}`:""}</div>`;})()}
  </div>
</div>
<div class="bt"><b>Bill To:</b> ${previewBill.customer} &nbsp;| 📞 ${previewBill.phone}</div>
<table><thead><tr><th>#</th><th>Item</th><th align="center">Qty</th><th align="right">Rate</th>${previewBill.type==="GST"?`<th align="center">GST%</th>`:"<th></th>"}<th align="right">Amount</th></tr></thead>
<tbody>${previewBill.items.map((it,i)=>`<tr><td>${i+1}</td><td>${it.name}</td><td align="center">${it.qty}</td><td align="right">₹${it.price.toFixed(2)}</td><td align="center">${previewBill.type==="GST"?it.gst+"%":""}</td><td align="right">₹${(it.price*it.qty).toFixed(2)}</td></tr>`).join("")}</tbody>
<tfoot>
  <tr><td colspan="5" align="right" style="padding:4px 10px;color:#555">Subtotal</td><td align="right" style="padding:4px 10px;font-weight:600">₹${previewBill.subtotal.toFixed(2)}</td></tr>
  ${gR}${dR}
  <tr><td colspan="5" align="right" style="padding:6px 10px">GRAND TOTAL</td><td align="right" style="padding:6px 10px">₹${previewBill.total.toFixed(2)}</td></tr>
</tfoot></table>
<div class="ft">${activeCfg.footerText||"Thank you for shopping with us!"}${activeCfg.showCashier!==false?`\nCashier: ${previewBill.cashier}`:""}</div>
</body></html>`;

        const prevThermalLogoHtml = `<div class="c b xl">Nethran Crackers</div>`;

        const thermalHtml = `<!DOCTYPE html><html><head><title>Preview</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Courier New',monospace;font-size:${fSize}px;padding:15px;width:302px;max-width:302px;margin:0 auto;color:#000;}
.c{text-align:center}.b{font-weight:700}.xl{font-size:15px}
.ln{border-top:1px dashed #000;margin:5px 0}.rw{display:flex;justify-content:space-between;margin:2px 0}
.tot{display:flex;justify-content:space-between;font-weight:700;font-size:13px;border-top:2px solid #000;padding-top:4px;margin-top:4px}
</style></head><body>
${prevThermalLogoHtml}
<div class="c" style="font-size:9px">${prevAddress} | ${prevPhone}</div>
${prevGstin?`<div class="c" style="font-size:9px">GSTIN: ${prevGstin}</div>`:""}
${storeExtraLines.filter(Boolean).map(l=>`<div class="c" style="font-size:9px">${l}</div>`).join("")}
${activeCfg.headerText?`<div class="c" style="font-size:${hFSize}px;white-space:pre-line;margin:3px 0">${activeCfg.headerText}</div>`:""}
${prevQrTop}
<div class="ln"></div>
<div class="c b" style="font-size:10px">${activeCfg.invoiceType||"RECEIPT"}</div>
<div class="ln"></div>
<div class="rw"><span>${{"TAX INVOICE":"Invoice No","BILL / RECEIPT":"Bill No","ESTIMATE":"Estimate No","DELIVERY NOTE":"DN No","PROFORMA INVOICE":"Proforma No"}[activeCfg.invoiceType]||"Invoice No"}: ${previewBill.id}</span></div>
<div class="rw"><span>${previewBill.date} ${previewBill.time}</span></div>
<div class="rw"><span>Customer: ${previewBill.customer}</span></div>
<div class="ln"></div>
<div class="rw b"><span>Item</span><span>Amt</span></div>
<div class="ln"></div>
${previewBill.items.map(i=>`<div style="margin:3px 0"><div>${i.name}</div><div class="rw"><span>${i.qty} x ₹${i.price}</span><span>₹${(i.qty*i.price).toFixed(2)}</span></div>${previewBill.type==="GST"&&i.gst>0?`<div style="font-size:9px;color:#555">GST ${i.gst}%: ₹${(i.qty*i.price*i.gst/100).toFixed(2)}</div>`:""}</div>`).join("")}
<div class="ln"></div>
<div class="rw"><span>Subtotal</span><span>₹${previewBill.subtotal.toFixed(2)}</span></div>
${previewBill.type==="GST"?`<div class="rw b"><span>GST</span><span>₹${previewBill.gstAmt.toFixed(2)}</span></div>`:""}
${previewBill.discount>0?`<div class="rw"><span>Discount</span><span>-₹${previewBill.discount.toFixed(2)}</span></div>`:""}
<div class="tot"><span>TOTAL</span><span>₹${previewBill.total.toFixed(2)}</span></div>
<div class="ln"></div>
${activeCfg.showPayment!==false?`<div class="rw"><span>Payment: ${previewBill.payment}</span></div>`:""}
${previewBill.type==="GST"?`<div class="ln"></div><div class="c" style="font-size:8px">GSTIN: ${prevGstin} | Tax Inv.</div>`:""}
<div class="ln"></div>
<div class="c" style="margin-top:6px;font-size:10px;white-space:pre-line">${activeCfg.footerText||"Thank you!"}</div>
${activeCfg.showCashier!==false?`<div class="c" style="font-size:9px">Cashier: ${previewBill.cashier}</div>`:""}
${prevQrBottom}
</body></html>`;

        const previewHtml = is80mm ? thermalHtml : a4Html;

        // Scale factor to fit preview in panel
        const panelW = 520; // approximate preview panel content width
        const scale = Math.min((panelW-40)/paperW, 0.95);

        const settingRow = (label,children) => (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.7,marginBottom:6}}>{label}</div>
            {children}
          </div>
        );

        const pillGroup = (options, value, setValue) => (
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {options.map(o=>(
              <button key={o.k} onClick={()=>setValue(o.k)}
                style={{padding:"6px 14px",borderRadius:8,border:value===o.k?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:value===o.k?"rgba(58,61,128,0.12)":th.appBg,color:value===o.k?"#3a3d80":th.text,cursor:"pointer",fontSize:12,fontWeight:value===o.k?700:500,transition:"all .15s"}}>
                {o.l}
              </button>
            ))}
          </div>
        );

        return (
          <div style={{position:"fixed",inset:0,zIndex:600,background:th.appBg,display:"flex",flexDirection:"column",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
            {/* Header */}
            <div style={{height:54,borderBottom:`1.5px solid ${th.cardBorder}`,display:"flex",alignItems:"center",padding:"0 20px",gap:12,flexShrink:0,background:th.cardBg}}>
              <span style={{fontSize:18}}>🖨️</span>
              <div style={{fontWeight:800,fontSize:16,color:th.textHeading}}>Print Setup</div>
              <div style={{display:"flex",gap:4,background:th.appBg,borderRadius:8,padding:3}}>
                {["A4","A5","80mm"].map(sz=>{
                  const isActive = printSetupTab===sz;
                  return (
                    <button key={sz} onClick={()=>setPrintSetupTab(sz)}
                      style={{padding:"5px 14px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:isActive?800:500,fontSize:13,background:isActive?"#3a3d80":th.appBg,color:isActive?"#fff":th.text,transition:"all .15s"}}>
                      {sz}
                    </button>
                  );
                })}
              </div>
              <div style={{flex:1}}/>
              <button onClick={()=>setShowPrintSetup(false)}
                style={{padding:"6px 18px",borderRadius:8,border:`1px solid ${th.cardBorder}`,background:th.appBg,color:th.text,cursor:"pointer",fontSize:13,fontWeight:600}}>
                Close
              </button>
            </div>

            {/* Body */}
            <div style={{display:"flex",flex:1,overflow:"hidden"}}>

              {/* LEFT — Settings */}
              <div style={{width:340,borderRight:`1.5px solid ${th.cardBorder}`,overflowY:"auto",padding:"20px 18px",display:"flex",flexDirection:"column",gap:4,background:th.cardBg,flexShrink:0}}>

                {!is80mm && settingRow("Orientation",
                  pillGroup([{k:"portrait",l:"⬆ Portrait"},{k:"landscape",l:"⬅ Landscape"}], activeCfg.orientation||"portrait", val=>updateCfg("orientation",val))
                )}

                {settingRow("Invoice Type",
                  <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                    {["TAX INVOICE","BILL / RECEIPT","ESTIMATE","DELIVERY NOTE","PROFORMA INVOICE"].map(tp=>(
                      <button key={tp} onClick={()=>updateCfg("invoiceType",tp)}
                        style={{padding:"5px 10px",borderRadius:7,border:(activeCfg.invoiceType||"TAX INVOICE")===tp?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:(activeCfg.invoiceType||"TAX INVOICE")===tp?"rgba(58,61,128,0.1)":th.appBg,color:(activeCfg.invoiceType||"TAX INVOICE")===tp?"#3a3d80":th.text,cursor:"pointer",fontSize:11,fontWeight:(activeCfg.invoiceType||"TAX INVOICE")===tp?700:500}}>
                        {tp}
                      </button>
                    ))}
                  </div>
                )}

                {!is80mm && settingRow("Color Theme",
                  <div style={{display:"flex",gap:8}}>
                    {[{k:"red",c:"#e74c3c"},{k:"blue",c:"#2980b9"},{k:"green",c:"#27ae60"},{k:"dark",c:"#2c3e50"},{k:"purple",c:"#8e44ad"}].map(o=>(
                      <button key={o.k} onClick={()=>updateCfg("colorTheme",o.k)} title={o.k}
                        style={{width:32,height:32,borderRadius:"50%",border:(activeCfg.colorTheme||"red")===o.k?"3px solid #333":"3px solid transparent",background:o.c,cursor:"pointer",boxShadow:(activeCfg.colorTheme||"red")===o.k?"0 0 0 2px #fff,0 0 0 4px "+o.c:"none"}}>
                      </button>
                    ))}
                  </div>
                )}

                {settingRow("Details Font Size",
                  <div>
                    <div style={{fontSize:11,color:th.textSub,marginBottom:4}}>Size: {activeCfg.detailFontSize||"11"}px — address, invoice no, date, bill-to</div>
                    <input type="range" min={7} max={16} value={Number(activeCfg.detailFontSize||"11")} onChange={e=>updateCfg("detailFontSize",String(e.target.value))}
                      style={{width:"100%",cursor:"pointer"}}/>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:th.textSub}}>
                      <span>7</span><span>11</span><span>16</span>
                    </div>
                  </div>
                )}
                {settingRow("Item Font Size",
                  <div>
                    <div style={{fontSize:11,color:th.textSub,marginBottom:4}}>Size: {({small:"11",medium:"12",large:"15"}[activeCfg.fontSize]||activeCfg.fontSize||"12")}px — item rows in the bill table</div>
                    <input type="range" min={8} max={16} value={Number({small:"11",medium:"12",large:"15"}[activeCfg.fontSize]||activeCfg.fontSize||"12")} onChange={e=>updateCfg("fontSize",String(e.target.value))}
                      style={{width:"100%",cursor:"pointer"}}/>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:th.textSub}}>
                      <span>8</span><span>12</span><span>16</span>
                    </div>
                  </div>
                )}

                {/* Greyscale */}
                {!is80mm&&settingRow("Print Mode",
                  <div style={{display:"flex",gap:8}}>
                    {[{k:false,l:"🎨 Colour",d:"Full colour"},{k:true,l:"⬛ Greyscale",d:"B&W printer"}].map(o=>(
                      <button key={String(o.k)} onClick={()=>updateCfg("greyscale",o.k)}
                        style={{flex:1,padding:"8px 6px",borderRadius:8,border:(activeCfg.greyscale||false)===o.k?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:(activeCfg.greyscale||false)===o.k?"rgba(58,61,128,0.12)":th.appBg,color:(activeCfg.greyscale||false)===o.k?"#3a3d80":th.text,cursor:"pointer",textAlign:"center"}}>
                        <div style={{fontWeight:700,fontSize:13}}>{o.l}</div>
                        <div style={{fontSize:10,color:th.textSub,marginTop:2}}>{o.d}</div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Logo Upload */}
                {settingRow("Shop Logo",
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {printLogoUrl
                        ? <img src={printLogoUrl} style={{height:40,width:"auto",maxWidth:100,objectFit:"contain",border:`1px solid ${th.cardBorder}`,borderRadius:6,padding:4,background:"#fff"}} alt="logo"/>
                        : <div style={{height:40,width:80,border:`1px dashed ${th.cardBorder}`,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:th.textSub}}>No logo</div>
                      }
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        <label style={{padding:"5px 12px",borderRadius:7,border:`1px solid #3a3d80`,color:"#3a3d80",cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"center"}}>
                          📁 Upload Logo
                          <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                            const f=e.target.files[0]; if(!f) return;
                            const r=new FileReader(); r.onload=ev=>setPrintLogoUrl(ev.target.result); r.readAsDataURL(f);
                          }}/>
                        </label>
                        {printLogoUrl&&<button onClick={()=>setPrintLogoUrl("")} style={{padding:"4px 10px",borderRadius:7,border:"1px solid #e74c3c33",background:"rgba(231,76,60,.08)",color:"#e74c3c",cursor:"pointer",fontSize:11}}>Remove</button>}
                      </div>
                    </div>
                    {printLogoUrl&&(
                      <div>
                        <div style={{fontSize:11,color:th.textSub,marginBottom:4}}>Logo size: {printLogoSize}px</div>
                        <input type="range" min={30} max={120} value={printLogoSize} onChange={e=>setPrintLogoSize(Number(e.target.value))}
                          style={{width:"100%",cursor:"pointer"}}/>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:th.textSub}}>
                          <span>Small</span><span>Medium</span><span>Large</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Store Details */}
                {settingRow("Store Details",
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    <input value={storeName} onChange={e=>{setStoreNameLocal(e.target.value);_updateStoreField("name",e.target.value);}} placeholder="Store name"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box",fontWeight:700}}/>
                    <input value={storeLocation} onChange={e=>{setStoreLocationLocal(e.target.value);_updateStoreField("location",e.target.value);}} placeholder="Location (city)"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                    <div style={{height:1,background:th.cardBorder,margin:"2px 0"}}/>
                    <input value={storeAddressLine} onChange={e=>setStoreAddressLine(e.target.value)} placeholder="Store address"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                    <input value={storePhone} onChange={e=>setStorePhone(e.target.value)} placeholder="Phone number"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                    <input value={storeEmail} onChange={e=>setStoreEmail(e.target.value)} placeholder="Email (optional)"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                    <input value={storeGSTIN} onChange={e=>setStoreGSTIN(e.target.value)} placeholder="GSTIN number"
                      style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                    {storeExtraLines.map((line,i)=>(
                      <div key={i} style={{display:"flex",gap:6,alignItems:"center"}}>
                        <input value={line} onChange={e=>{const a=[...storeExtraLines];a[i]=e.target.value;setStoreExtraLines(a);}}
                          placeholder={`Extra line ${i+1}`}
                          style={{...IS,flex:1,fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                        <button onClick={()=>setStoreExtraLines(storeExtraLines.filter((_,j)=>j!==i))}
                          style={{padding:"4px 10px",borderRadius:7,border:"none",background:"rgba(231,76,60,.1)",color:"#e74c3c",cursor:"pointer",fontSize:13,fontWeight:700,flexShrink:0}}>✕</button>
                      </div>
                    ))}
                    <button onClick={()=>setStoreExtraLines([...storeExtraLines,""])}
                      style={{padding:"6px 10px",borderRadius:7,border:`1.5px dashed ${th.cardBorder}`,background:"none",color:th.textSub,cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"left"}}>
                      + Add line
                    </button>
                  </div>
                )}

                {/* QR Code — 80mm only */}
                {is80mm && settingRow("Location QR Code",
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    <div style={{fontSize:11,color:th.textSub}}>Upload a QR code image (Google Maps, website, contact etc.)</div>
                    <div style={{display:"flex",gap:8,alignItems:"center"}}>
                      {activeCfg.qrImage
                        ? <img src={activeCfg.qrImage} style={{width:50,height:50,objectFit:"contain",border:`1px solid ${th.cardBorder}`,borderRadius:6,background:"#fff"}} alt="QR"/>
                        : <div style={{width:50,height:50,border:`1px dashed ${th.cardBorder}`,borderRadius:6,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>📷</div>
                      }
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        <label style={{padding:"5px 12px",borderRadius:7,border:`1px solid #3a3d80`,color:"#3a3d80",cursor:"pointer",fontSize:12,fontWeight:600,textAlign:"center"}}>
                          📁 Upload QR
                          <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                            const f=e.target.files[0]; if(!f) return;
                            const r=new FileReader(); r.onload=ev=>updateCfg("qrImage",ev.target.result); r.readAsDataURL(f);
                          }}/>
                        </label>
                        {activeCfg.qrImage&&<button onClick={()=>updateCfg("qrImage","")} style={{padding:"4px 10px",borderRadius:7,border:"1px solid #e74c3c33",background:"rgba(231,76,60,.08)",color:"#e74c3c",cursor:"pointer",fontSize:11}}>Remove</button>}
                      </div>
                    </div>
                    {activeCfg.qrImage&&(<>
                      <input value={activeCfg.qrLabel||""} onChange={e=>updateCfg("qrLabel",e.target.value)} placeholder="Label below QR e.g. Scan to find us"
                        style={{...IS,width:"100%",fontSize:12,padding:"6px 10px",boxSizing:"border-box"}}/>
                      <div>
                        <div style={{fontSize:11,color:th.textSub,marginBottom:4}}>QR size: {activeCfg.qrSize||60}px</div>
                        <input type="range" min={40} max={120} value={activeCfg.qrSize||60} onChange={e=>updateCfg("qrSize",Number(e.target.value))} style={{width:"100%",cursor:"pointer"}}/>
                      </div>
                      <div style={{fontSize:11,color:th.textSub,marginBottom:4}}>Position</div>
                      <div style={{display:"flex",gap:6}}>
                        {[{k:"top",l:"⬆ Top"},{k:"bottom",l:"⬇ Bottom"}].map(o=>(
                          <button key={o.k} onClick={()=>updateCfg("qrPosition",o.k)}
                            style={{flex:1,padding:"6px",borderRadius:7,border:(activeCfg.qrPosition||"bottom")===o.k?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:(activeCfg.qrPosition||"bottom")===o.k?"rgba(58,61,128,0.12)":th.appBg,color:(activeCfg.qrPosition||"bottom")===o.k?"#3a3d80":th.text,cursor:"pointer",fontSize:12,fontWeight:(activeCfg.qrPosition||"bottom")===o.k?700:500}}>
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </>)}
                  </div>
                )}

{settingRow("Footer Text",
                  <textarea value={activeCfg.footerText||""} onChange={e=>updateCfg("footerText",e.target.value)}
                    placeholder={"e.g. Thank you! Visit again.\nGod bless you 🙏"}
                    rows={2}
                    style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
                )}

                {settingRow("Show on Invoice",
                  <div style={{display:"flex",flexDirection:"column",gap:8}}>
                    {[{k:"showCashier",l:"Cashier name",v:activeCfg.showCashier!==false},{k:"showPayment",l:"Payment method",v:activeCfg.showPayment!==false}].map(o=>(
                      <label key={o.k} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
                        <input type="checkbox" checked={o.v} onChange={e=>updateCfg(o.k,e.target.checked)} style={{width:16,height:16,cursor:"pointer"}}/>
                        {o.l}
                      </label>
                    ))}
                  </div>
                )}

                <button onClick={()=>setShowPrintSetup(false)}
                  style={{marginTop:8,padding:"11px 0",borderRadius:10,border:"none",background:"linear-gradient(135deg,#3a3d80,#e74c3c)",color:"#fff",cursor:"pointer",fontSize:14,fontWeight:800,width:"100%"}}>
                  ✅ Save & Close
                </button>
              </div>

              {/* RIGHT — Preview */}
              <div style={{flex:1,overflowY:"auto",background:"#555",display:"flex",flexDirection:"column",alignItems:"center",padding:"30px 20px",gap:20}}>
                <div style={{fontSize:12,fontWeight:700,color:"#ddd",letterSpacing:1,textTransform:"uppercase",alignSelf:"flex-start"}}>
                  Preview — {activeTab} {!is80mm?activeCfg.orientation:""} · {activeCfg.invoiceType||"TAX INVOICE"}
                </div>
                <div style={{
                  width:paperW*scale,height:paperH*scale,
                  background:"#fff",
                  boxShadow:"0 8px 40px rgba(0,0,0,0.5)",
                  borderRadius:2,
                  overflow:"hidden",
                  position:"relative",
                  flexShrink:0
                }}>
                  <iframe
                    srcDoc={previewHtml}
                    title="Print Preview"
                    style={{width:paperW,height:paperH,border:"none",transformOrigin:"top left",transform:`scale(${scale})`,overflow:"hidden"}}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── OWNER ANALYTICS WINDOW ── */}
      {showOwnerAnalytics&&currentUser?.role==="owner"&&(()=>{
        // ── filter helper ─────────────────────────────────────────────────────
        const filterByPeriod = arr => {
          const now = new Date();
          return arr.filter(e=>{
            const d = toDate(e.date);
            if(isNaN(d)) return false;
            if(analyticsPeriod==="today")   return dStr(d)===dStr(now);
            if(analyticsPeriod==="weekly")  { const w=new Date(now);w.setDate(w.getDate()-6);return d>=w; }
            if(analyticsPeriod==="monthly") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
            if(analyticsPeriod==="custom"){
              const from = analyticsFrom ? new Date(analyticsFrom) : null;
              const to   = analyticsTo   ? new Date(analyticsTo)   : null;
              if(from) from.setHours(0,0,0,0);
              if(to)   to.setHours(23,59,59,999);
              return (!from||d>=from)&&(!to||d<=to);
            }
            return true;
          });
        };

        const periodLabel = {today:"Today",weekly:"This Week",monthly:"This Month",custom:"Custom Range"}[analyticsPeriod];
        const filtSales   = filterByPeriod(sales);
        const filtPur     = filterByPeriod(purchases);
        const filtExp     = filterByPeriod(expenses);
        const salesAmt    = filtSales.reduce((a,s)=>a+s.total,0);
        const purAmt      = filtPur.reduce((a,p)=>a+p.total,0);
        const expAmt      = filtExp.reduce((a,e)=>a+e.amount,0);
        const grossProfit = salesAmt - purAmt - expAmt;
        const creditAmt   = filtSales.filter(s=>s.payment==="Credit").reduce((a,s)=>a+s.total,0);

        // ── chart: build day buckets depending on period ──────────────────────
        const buildChart = () => {
          let days;
          const now = new Date();
          if(analyticsPeriod==="today"){
            days=[new Date(now)];
          } else if(analyticsPeriod==="weekly"){
            days=Array.from({length:7},(_,i)=>{const d=new Date(now);d.setDate(d.getDate()-6+i);return d;});
          } else if(analyticsPeriod==="monthly"){
            const totalDays=new Date(now.getFullYear(),now.getMonth()+1,0).getDate();
            days=Array.from({length:totalDays},(_,i)=>{const d=new Date(now.getFullYear(),now.getMonth(),i+1);return d;});
          } else {
            // custom: build days between from and to (max 31)
            const from = analyticsFrom ? new Date(analyticsFrom) : new Date(now.getFullYear(),now.getMonth(),1);
            const to   = analyticsTo   ? new Date(analyticsTo)   : new Date(now);
            const diff = Math.min(Math.round((to-from)/(1000*60*60*24))+1,31);
            days=Array.from({length:diff},(_,i)=>{const d=new Date(from);d.setDate(from.getDate()+i);return d;});
          }
          return days.map(d=>{
            const ds=d.toLocaleDateString("en-IN");
            const showDay = days.length<=14;
            return {
              label: showDay
                ? d.toLocaleDateString("en-IN",{weekday:"short"})
                : String(d.getDate()),
              sales: sales.filter(s=>s.date===ds).reduce((a,s)=>a+s.total,0),
              pur:   purchases.filter(p=>p.date===ds).reduce((a,p)=>a+p.total,0),
            };
          });
        };
        const chartData = buildChart();
        const chartMax  = Math.max(...chartData.map(d=>Math.max(d.sales,d.pur)),1);

        // ── top 5 items ───────────────────────────────────────────────────────
        const itemMap={};
        filtSales.forEach(bill=>bill.items&&bill.items.forEach(item=>{
          if(!itemMap[item.name]) itemMap[item.name]={name:item.name,qty:0,revenue:0};
          itemMap[item.name].qty+=item.qty;
          itemMap[item.name].revenue+=item.price*item.qty;
        }));
        const top5   = Object.values(itemMap).sort((a,b)=>b.revenue-a.revenue).slice(0,5);
        const maxRev = top5[0]?.revenue||1;

        // eslint-disable-next-line no-unused-vars
        const MEDAL = ["#f1c40f","#bdc3c7","#cd7f32","#7f8c8d","#7f8c8d"];
        const IS2   = {padding:"7px 10px",borderRadius:8,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.inputText,fontSize:12,outline:"none"};

        return (
          <div style={{position:"fixed",inset:0,zIndex:600,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>

            {/* ── Top bar ── */}
            <div style={{background:"linear-gradient(135deg,#3a3d80,#5558b0)",padding:"0 24px",height:60,display:"flex",alignItems:"center",gap:14,flexShrink:0,boxShadow:"0 2px 12px #0003"}}>
              <div style={{fontSize:22}}>📊</div>
              <div style={{flex:1}}>
                <div style={{fontSize:16,fontWeight:800,color:"#fff"}}>Owner Analytics</div>
                <div style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}>{storeName}</div>
              </div>
              <button onClick={()=>setShowOwnerAnalytics(false)}
                style={{background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"#fff",borderRadius:9,padding:"7px 16px",cursor:"pointer",fontSize:13,fontWeight:700}}>
                ← Back
              </button>
            </div>

            {/* ── Filter bar ── */}
            <div style={{background:th.cardBg,borderBottom:`1.5px solid ${th.cardBorder}`,padding:"12px 24px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
              <span style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginRight:4}}>Period:</span>
              {[["today","Today"],["weekly","This Week"],["monthly","This Month"],["custom","Custom"]].map(([k,l])=>(
                <button key={k} onClick={()=>setAnalyticsPeriod(k)}
                  style={{padding:"6px 16px",borderRadius:20,border:analyticsPeriod===k?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:analyticsPeriod===k?"#3a3d80":th.appBg,color:analyticsPeriod===k?"#fff":th.text,cursor:"pointer",fontSize:12,fontWeight:analyticsPeriod===k?700:500,transition:"all .15s"}}>
                  {l}
                </button>
              ))}
              {analyticsPeriod==="custom"&&(
                <div style={{display:"flex",alignItems:"center",gap:8,marginLeft:8}}>
                  <input type="date" value={analyticsFrom} onChange={e=>setAnalyticsFrom(e.target.value)} style={IS2}/>
                  <span style={{fontSize:12,color:th.textSub}}>to</span>
                  <input type="date" value={analyticsTo} onChange={e=>setAnalyticsTo(e.target.value)} style={IS2}/>
                </div>
              )}
              <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:"#3a3d80",background:"rgba(58,61,128,0.1)",padding:"4px 12px",borderRadius:20}}>{periodLabel}</span>
            </div>

            {/* ── Scrollable content ── */}
            <div style={{flex:1,overflowY:"auto",padding:"20px 24px",display:"flex",flexDirection:"column",gap:18}}>

              {/* KPI Strip */}
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:14}}>
                {[
                  {label:"Total Sales",       value:fmt(salesAmt),               icon:"💰", color:"#3a3d80", sub:`${filtSales.length} transactions`},
                  {label:"Total Purchases",   value:fmt(purAmt),                 icon:"🛒", color:"#e67e22", sub:`${filtPur.length} purchase orders`},
                  {label:"Total Expenses",    value:fmt(expAmt),                 icon:"💸", color:"#8e44ad", sub:`${filtExp.length} expense entries`},
                  {label:"Gross Profit",      value:fmt(Math.max(0,grossProfit)),icon:"📈", color:"#27ae60", sub:"Sales − Purchases − Exp"},
                  {label:"Credit Outstanding",value:fmt(creditAmt),              icon:"⚠️", color:"#e74c3c", sub:`${filtSales.filter(s=>s.payment==="Credit").length} unpaid bills`},
                ].map((kpi,i)=>(
                  <div key={i} style={{background:th.cardBg,borderRadius:14,border:`1px solid ${kpi.color}33`,padding:"16px 18px",boxShadow:`0 2px 10px ${kpi.color}10`}}>
                    <div style={{fontSize:22,marginBottom:8}}>{kpi.icon}</div>
                    <div style={{fontSize:20,fontWeight:900,color:kpi.color}}>{kpi.value}</div>
                    <div style={{fontSize:10,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginTop:6}}>{kpi.label}</div>
                    <div style={{fontSize:10,color:th.textSub,marginTop:3}}>{kpi.sub}</div>
                  </div>
                ))}
              </div>

              {/* Bar Chart + Top 5 */}
              <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:16}}>

                {/* Sales vs Purchases chart */}
                <div style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,padding:20}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                    <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>Sales vs Purchases — {periodLabel}</div>
                    <div style={{display:"flex",gap:10,fontSize:10,fontWeight:700}}>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:9,height:9,borderRadius:2,background:"#3a3d80",display:"inline-block"}}/>Sales</span>
                      <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:9,height:9,borderRadius:2,background:"#e67e22",display:"inline-block"}}/>Purchases</span>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"flex-end",gap:chartData.length>20?2:6,height:160,paddingTop:4}}>
                    {chartData.map((d,i)=>(
                      <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",height:"100%",justifyContent:"flex-end",minWidth:0}}>
                        <div style={{width:"100%",display:"flex",gap:1,alignItems:"flex-end",height:140}}>
                          <div title={`Sales: ${fmt(d.sales)}`}
                            style={{flex:1,background:"#3a3d80",borderRadius:"3px 3px 0 0",
                              height:`${Math.max((d.sales/chartMax)*100,d.sales>0?3:0)}%`,
                              minHeight:d.sales>0?2:0}}/>
                          <div title={`Purchases: ${fmt(d.pur)}`}
                            style={{flex:1,background:"#e67e22",borderRadius:"3px 3px 0 0",
                              height:`${Math.max((d.pur/chartMax)*100,d.pur>0?3:0)}%`,
                              minHeight:d.pur>0?2:0}}/>
                        </div>
                        {chartData.length<=20&&(
                          <div style={{fontSize:8,color:th.textSub,fontWeight:600,marginTop:4,textAlign:"center"}}>{d.label}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  {chartData.every(d=>d.sales===0&&d.pur===0)&&(
                    <div style={{textAlign:"center",fontSize:12,color:th.textSub,marginTop:12}}>No data for this period</div>
                  )}
                </div>

                {/* Top 5 items */}
                <div style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,padding:20}}>
                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:16}}>🏆 Top 5 Items — {periodLabel}</div>
                  {top5.length===0
                    ? <div style={{textAlign:"center",fontSize:12,color:th.textSub,padding:24}}>No sales in this period</div>
                    : top5.map((item,i)=>(
                      <div key={item.name} style={{marginBottom:14}}>
                        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:5}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                            <span style={{fontSize:12,fontWeight:900,color:MEDAL[i],flexShrink:0,width:20}}>#{i+1}</span>
                            <span style={{fontSize:12,fontWeight:600,color:th.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.name}</span>
                          </div>
                          <div style={{textAlign:"right",flexShrink:0,marginLeft:8}}>
                            <div style={{fontSize:13,fontWeight:800,color:"#3a3d80"}}>{fmt(item.revenue)}</div>
                            <div style={{fontSize:10,color:th.textSub}}>{item.qty} units</div>
                          </div>
                        </div>
                        <div style={{height:6,borderRadius:4,background:th.cardBorder,overflow:"hidden"}}>
                          <div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,#3a3d80,#5558b0)`,width:`${(item.revenue/maxRev)*100}%`}}/>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ── INVOICE HISTORY FULL WINDOW ── */}
      {showInvHistory&&(()=>{
        const filtered = sales.filter(s=>{
          const matchSearch = !invHisSearch || s.customer.toLowerCase().includes(invHisSearch.toLowerCase()) || s.id.toLowerCase().includes(invHisSearch.toLowerCase());
          const matchDate   = !invHisDate   || s.date===invHisDate;
          const matchType   = invHisType==="all" || s.type===invHisType;
          const matchPay    = invHisPay==="all"  || s.payment===invHisPay;
          return matchSearch&&matchDate&&matchType&&matchPay;
        });
        const filteredTotal = filtered.reduce((s,i)=>s+i.total,0);
        const IS2 = {...IS, fontSize:13, padding:"8px 12px"};
        return (
          <div style={{position:"fixed",inset:0,zIndex:500,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
            {/* Header */}
            <div style={{padding:"16px 20px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",alignItems:"center",gap:12,flexShrink:0,background:th.cardBg,boxShadow:"0 2px 8px #0002"}}>
              <button onClick={()=>setShowInvHistory(false)} style={{background:"none",border:"none",fontSize:20,color:th.textSub,cursor:"pointer",lineHeight:1,padding:"4px 8px",borderRadius:8}}>←</button>
              <div style={{fontSize:22}}>🧾</div>
              <div style={{flex:1}}>
                <div style={{fontSize:17,fontWeight:800,color:th.textHeading}}>Invoice History</div>
                <div style={{fontSize:12,color:th.textSub}}>{filtered.length} of {sales.length} invoices · {fmt(filteredTotal)}</div>
              </div>
            </div>
            {/* Filters */}
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${th.cardBorder}`,display:"flex",flexDirection:"column",gap:8,flexShrink:0,background:th.cardBg}}>
              <input value={invHisSearch} onChange={e=>setInvHisSearch(e.target.value)}
                placeholder="Search customer or invoice ID..." style={{...IS2,width:"100%"}}/>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <div style={{position:"relative",flex:1,minWidth:120}}>
                  <input type="text" value={invHisDate} readOnly placeholder="📅 Filter by date"
                    onClick={e=>e.target.nextSibling.showPicker?.()}
                    style={{...IS2,width:"100%",cursor:"pointer"}}/>
                  <input type="date" style={{position:"absolute",opacity:0,inset:0,cursor:"pointer"}}
                    onChange={e=>setInvHisDate(e.target.value)}/>
                  {invHisDate&&<button onClick={()=>setInvHisDate("")} style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:th.textSub,cursor:"pointer",fontSize:14}}>✕</button>}
                </div>
                <select value={invHisType} onChange={e=>setInvHisType(e.target.value)} style={{...IS2,flex:1,minWidth:100}}>
                  <option value="all">All Types</option>
                  <option value="GST">GST</option>
                  <option value="Non-GST">Non-GST</option>
                </select>
                <select value={invHisPay} onChange={e=>setInvHisPay(e.target.value)} style={{...IS2,flex:1,minWidth:100}}>
                  <option value="all">All Payments</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">Card</option>
                  <option value="Credit">Credit</option>
                </select>
              </div>
              {(invHisSearch||invHisDate||invHisType!=="all"||invHisPay!=="all")&&(
                <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{fontSize:11,color:th.textSub}}>Active filters:</span>
                  {invHisSearch&&<span style={{fontSize:11,padding:"3px 8px",borderRadius:10,background:"rgba(58,61,128,0.12)",color:"#3a3d80"}}>"{invHisSearch}" <button onClick={()=>setInvHisSearch("")} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",padding:"0 0 0 4px",fontSize:11}}>✕</button></span>}
                  {invHisDate&&<span style={{fontSize:11,padding:"3px 8px",borderRadius:10,background:"rgba(58,61,128,0.12)",color:"#3a3d80"}}>{invHisDate} <button onClick={()=>setInvHisDate("")} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",padding:"0 0 0 4px",fontSize:11}}>✕</button></span>}
                  {invHisType!=="all"&&<span style={{fontSize:11,padding:"3px 8px",borderRadius:10,background:"rgba(58,61,128,0.12)",color:"#3a3d80"}}>{invHisType} <button onClick={()=>setInvHisType("all")} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",padding:"0 0 0 4px",fontSize:11}}>✕</button></span>}
                  {invHisPay!=="all"&&<span style={{fontSize:11,padding:"3px 8px",borderRadius:10,background:"rgba(58,61,128,0.12)",color:"#3a3d80"}}>{invHisPay} <button onClick={()=>setInvHisPay("all")} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",padding:"0 0 0 4px",fontSize:11}}>✕</button></span>}
                  <button onClick={()=>{setInvHisSearch("");setInvHisDate("");setInvHisType("all");setInvHisPay("all");}} style={{fontSize:11,background:"none",border:"none",color:"#e74c3c",cursor:"pointer",marginLeft:4}}>Clear all</button>
                </div>
              )}
            </div>
            {/* Invoice list */}
            <div style={{flex:1,overflowY:"auto"}}>
              {filtered.length===0
                ?<div style={{textAlign:"center",color:th.textSub,padding:40,fontSize:13}}>No invoices match your filters</div>
                :filtered.slice().reverse().map(s=>(
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 20px",borderBottom:`1px solid ${th.cardBorder}`,opacity:s.status==="cancelled"?.6:1,background:s.status==="cancelled"?"rgba(231,76,60,.04)":"transparent"}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:3}}>
                        <span style={{fontSize:13,fontWeight:700,color:th.text,textDecoration:s.status==="cancelled"?"line-through":"none"}}>{s.customer}</span>
                        <span style={{fontSize:11,padding:"2px 7px",borderRadius:6,fontWeight:700,background:s.type==="GST"?"rgba(41,128,185,.15)":"rgba(39,174,96,.1)",color:s.type==="GST"?"#2980b9":"#27ae60"}}>{s.type}</span>
                        <span style={{fontSize:11,padding:"2px 7px",borderRadius:6,fontWeight:700,background:s.payment==="Credit"?"rgba(231,76,60,.12)":"rgba(39,174,96,.08)",color:s.payment==="Credit"?"#e74c3c":"#27ae60"}}>{s.payment}</span>
                        {s.status==="cancelled"&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:6,fontWeight:700,background:"rgba(231,76,60,.15)",color:"#e74c3c"}}>CANCELLED</span>}
                        {s.editedAt&&s.status!=="cancelled"&&<span style={{fontSize:10,color:th.textSub}}>edited</span>}
                      </div>
                      <div style={{fontSize:11,color:th.textSub,marginBottom:2}}>{s.id} · {s.date}{s.time?` · ${s.time}`:""}</div>
                      <div style={{fontSize:11,color:th.textSub}}>
                        {s.items.slice(0,3).map((it,i)=><span key={i} style={{marginRight:6}}>{it.name} ×{it.qty}</span>)}
                        {s.items.length>3&&<span style={{color:"#3a3d80"}}>+{s.items.length-3} more</span>}
                      </div>
                      {s.status==="cancelled"&&s.cancelReason&&<div style={{fontSize:11,color:"#e74c3c",marginTop:2}}>Reason: {s.cancelReason}</div>}
                    </div>
                    <div style={{flexShrink:0,textAlign:"right",display:"flex",flexDirection:"column",gap:5,alignItems:"flex-end"}}>
                      <div style={{fontSize:15,fontWeight:800,color:s.status==="cancelled"?"#e74c3c":"#3a3d80",textDecoration:s.status==="cancelled"?"line-through":"none"}}>{fmt(s.total)}</div>
                      <div style={{display:"flex",gap:5}}>
                        {s.status!=="cancelled"&&<button onClick={()=>{setEditingInv(s);setInvEditForm({...s,items:[...s.items.map(i=>({...i}))]});setShowInvEdit(true);setShowInvHistory(false);}} style={{padding:"4px 10px",borderRadius:7,border:"none",background:"rgba(58,61,128,0.12)",color:"#3a3d80",cursor:"pointer",fontSize:11,fontWeight:700}}>Edit</button>}
                        {s.status!=="cancelled"&&<button onClick={()=>setInvPrintPick(s)} style={{padding:"4px 10px",borderRadius:7,border:"none",background:"rgba(41,128,185,.15)",color:"#2980b9",cursor:"pointer",fontSize:11,fontWeight:700}}>🖨 Print</button>}
                      </div>
                    </div>
                  </div>
                ))
              }
            </div>
            {/* Footer */}
            <div style={{padding:"12px 20px",borderTop:`2px solid #3a3d80`,background:th.cardBg,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
              <div style={{fontSize:13,color:th.textSub}}>{filtered.length} invoice{filtered.length!==1?"s":""} shown</div>
              <div style={{fontSize:16,fontWeight:800,color:"#3a3d80"}}>{fmt(filteredTotal)}</div>
            </div>
          </div>
        );
      })()}

      {/* ── PRINT TYPE PICKER (Invoice History) ── */}
      {invPrintPick&&(
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={()=>setInvPrintPick(null)}>
          <div style={{background:th.modalBg,borderRadius:18,padding:24,width:"100%",maxWidth:340,boxShadow:"0 16px 60px #0006"}} onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:16,fontWeight:800,color:th.textHeading,marginBottom:4,textAlign:"center"}}>Print Invoice</div>
            <div style={{fontSize:12,color:th.textSub,textAlign:"center",marginBottom:20}}>{invPrintPick.customer} · {fmt(invPrintPick.total)}</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {[
                {label:"🖨 Print A4",  size:"A4",   action:()=>{showPrint(doPrintA4(invPrintPick,t,{...pCfg,printPaperSize:"A4"}));  setInvPrintPick(null);}},
                {label:"🖨 Print A5",  size:"A5",   action:()=>{showPrint(doPrintA4(invPrintPick,t,{...pCfg,printPaperSize:"A5"}));  setInvPrintPick(null);}},
                {label:"🧾 Print 80mm",size:"80mm", action:()=>{showPrint(doPrintThermal(invPrintPick,t,pCfg));                       setInvPrintPick(null);}},
              ].map(opt=>(
                <button key={opt.size} onClick={opt.action}
                  style={{padding:"12px 0",borderRadius:10,border:"none",background:"#3a3d80",color:"#fff",cursor:"pointer",fontSize:14,fontWeight:700,letterSpacing:.3}}>
                  {opt.label}
                </button>
              ))}
              <button onClick={()=>setInvPrintPick(null)} style={{padding:"9px 0",borderRadius:10,border:`1.5px solid ${th.cardBorder}`,background:"none",color:th.textSub,cursor:"pointer",fontSize:13,marginTop:2}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {billSuccess&&(
        <div style={{position:"fixed",inset:0,background:"#000c",zIndex:900,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:22,padding:0,width:"100%",maxWidth:400,boxShadow:"0 24px 80px #0006",overflow:"hidden",animation:"fadeUp .25s ease"}}>
            {/* Green success header */}
            <div style={{background:"linear-gradient(135deg,#27ae60,#1e8449)",padding:"24px 24px 20px",textAlign:"center"}}>
              <div style={{fontSize:44,marginBottom:8}}>✅</div>
              <div style={{fontSize:22,fontWeight:900,color:"#fff",marginBottom:2}}>Bill Saved!</div>
              <div style={{fontSize:13,color:"rgba(255,255,255,.8)"}}>{billSuccess.id} · {billSuccess.customer}</div>
              <div style={{fontSize:32,fontWeight:900,color:"#fff",marginTop:6}}>{fmt(billSuccess.total)}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,.7)",marginTop:2}}>{billSuccess.date} · {billSuccess.payment} · {billSuccess.type}</div>
            </div>
            {/* Items summary */}
            <div style={{maxHeight:160,overflowY:"auto",padding:"12px 20px",borderBottom:`1px solid ${th.cardBorder}`}}>
              {billSuccess.items.map((it,i)=>(
                <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:13,color:th.textSub,padding:"5px 0",borderBottom:i<billSuccess.items.length-1?`1px solid ${th.cardBorder}`:"none"}}>
                  <span style={{color:th.text,fontWeight:600}}>{it.name} <span style={{color:th.textSub,fontWeight:400}}>×{it.qty}</span></span>
                  <span style={{fontWeight:700,color:"#3a3d80"}}>₹{it.price*it.qty}</span>
                </div>
              ))}
            </div>
            {/* Action buttons */}
            <div style={{padding:"16px 20px",display:"flex",flexDirection:"column",gap:10}}>
              <div style={{fontSize:12,fontWeight:700,color:th.textSub,textAlign:"center",textTransform:"uppercase",letterSpacing:.8,marginBottom:2}}>Print this bill?</div>
              <button onClick={()=>{defPrintMode==="80mm"?showPrint(doPrintThermal(billSuccess,t,pCfg)):showPrint(doPrintA4(billSuccess,t,pCfg));}} style={{...btn(defPrintMode==="80mm"?"#2980b9":"#3a3d80"),width:"100%",padding:"13px 0",fontSize:14,borderRadius:12}}>{defPrintMode==="80mm"?"🧾 Print 80mm":"🖨 Print A4"}</button>
              <button onClick={()=>setBillSuccess(null)} style={{...btn("#27ae60"),width:"100%",padding:"14px 0",fontSize:16,fontWeight:900,borderRadius:12}}>
                ➕ Start New Bill
              </button>
              <button onClick={()=>setBillSuccess(null)} style={{...ghost,width:"100%",fontSize:13,padding:"9px 0",borderRadius:12,textAlign:"center"}}>Skip — Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── QTY INPUT MODAL (mobile) ── */}
      {qtyModal&&(
        <div style={{position:"fixed",inset:0,background:"#000a",zIndex:500,display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:0}} onClick={()=>setQtyModal(null)}>
          <div style={{background:th.modalBg,borderRadius:"24px 24px 0 0",padding:24,width:"100%",maxWidth:480,boxShadow:"0 -8px 40px #0004",paddingBottom:"env(safe-area-inset-bottom, 24px)"}} onClick={e=>e.stopPropagation()}>
            {/* Product preview */}
            <div style={{display:"flex",gap:14,alignItems:"center",marginBottom:20}}>
              <div style={{width:64,height:64,borderRadius:14,background:qtyModal.image?`url(${qtyModal.image}) center/cover no-repeat`:`${catColor(qtyModal.category)}18`,border:`2px solid ${catColor(qtyModal.category)}44`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {!qtyModal.image&&<span style={{fontSize:28,opacity:.5}}>📦</span>}
              </div>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:th.textHeading}}>{lang==="en"?qtyModal.name:qtyModal.nameTa}</div>
                <div style={{fontSize:14,color:"#3a3d80",fontWeight:700,marginTop:2}}>₹{qtyModal.salePrice} per {qtyModal.unit}</div>
                <div style={{fontSize:12,color:th.textSub,marginTop:1}}>Stock: {qtyModal.stock} {qtyModal.unit}</div>
              </div>
            </div>
            {/* Qty input */}
            <div style={{fontSize:13,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:8}}>Enter Quantity</div>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <button onClick={()=>setQtyInput(q=>String(Math.max(1,+q-1)))} style={{width:52,height:52,borderRadius:12,border:"none",background:"rgba(231,76,60,.12)",color:"#e74c3c",fontSize:28,fontWeight:800,cursor:"pointer",flexShrink:0}}>−</button>
              <input
                type="number" value={qtyInput} min="1" max={qtyModal.stock}
                onChange={e=>setQtyInput(e.target.value)}
                inputMode="numeric"
                readOnly
                onClick={e=>{e.target.readOnly=false; e.target.focus();}}
                onFocus={e=>e.target.readOnly=false}
                style={{...IS,flex:1,fontSize:"30px",fontWeight:900,textAlign:"center",height:56,borderRadius:12,color:th.text,cursor:"text"}}
              />
              <button onClick={()=>setQtyInput(q=>String(Math.min(qtyModal.stock,+q+1)))} style={{width:52,height:52,borderRadius:12,border:"none",background:"rgba(58,61,128,0.12)",color:"#3a3d80",fontSize:28,fontWeight:800,cursor:"pointer",flexShrink:0}}>+</button>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:16,fontSize:14,color:th.textSub}}>
              <span>Total</span>
              <span style={{fontWeight:800,color:"#3a3d80",fontSize:16}}>{fmt(qtyModal.salePrice * (+qtyInput||1))}</span>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setQtyModal(null)} style={{...ghost,flex:1,padding:"14px 0",fontSize:15,borderRadius:14}}>Cancel</button>
              <button onClick={()=>{
                const qty = Math.max(1, Math.min(qtyModal.stock, +qtyInput||1));
                const ex = cart.find(c=>c.id===qtyModal.id);
                if(ex) setCart(cart.map(c=>c.id===qtyModal.id?{...c,qty}:c));
                else setCart([...cart,{...qtyModal,qty}]);
                setQtyModal(null);

              }} style={{...btn("#3a3d80"),flex:2,padding:"14px 0",fontSize:16,fontWeight:800,borderRadius:14}}>
                Add to Bill ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD/EDIT PURCHASE MODAL ── */}
      {showPurModal&&(
        <Modal title={editingPur?<span style={{display:"flex",alignItems:"center"}}><PenIcon/>Edit Purchase</span>:`+ ${t.addPurchase}`} onClose={()=>setShowPurModal(false)} width={520} th={th}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
            <Field label={t.purchaseTitle} th={th}><input value={purForm.title||""} onChange={e=>setPurForm({...purForm,title:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:700,color:th.text}} placeholder="e.g. Sparklers Box"/></Field>
            <Field label={t.purchaseBrand} th={th}><input value={purForm.brand||""} onChange={e=>setPurForm({...purForm,brand:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text}} placeholder="e.g. Standard"/></Field>
            <Field label={t.supplier} th={th}><input value={purForm.supplier||""} onChange={e=>setPurForm({...purForm,supplier:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text}} placeholder="e.g. ABC Traders"/></Field>
            <Field label={t.purchaseCategory} th={th}>
              <select value={purForm.category||""} onChange={e=>setPurForm({...purForm,category:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text}}>
                {cats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </Field>
            <Field label={t.purchaseQty} th={th}><input type="number" value={purForm.qty||""} onChange={e=>setPurForm({...purForm,qty:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:700,color:th.text}} placeholder="0"/></Field>
            <Field label={t.purchaseUnit} th={th}>
              <select value={purForm.unit||"Piece"} onChange={e=>setPurForm({...purForm,unit:e.target.value})} style={{...IS,fontSize:"14px",color:th.text}}>
                {UNITS.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label={t.purchaseCost+" (₹)"} th={th}><input type="number" value={purForm.costPerUnit||""} onChange={e=>setPurForm({...purForm,costPerUnit:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:700,color:th.text}} placeholder="0"/></Field>
            <Field label={t.purchaseDate} th={th}>
              <div style={{position:"relative"}}>
                <input type="text" value={purForm.date||""} onChange={e=>setPurForm({...purForm,date:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text,paddingRight:36}} placeholder="dd/mm/yyyy"/>
                <input type="date" onChange={e=>{if(!e.target.value)return;const[y,m,d]=e.target.value.split("-");setPurForm({...purForm,date:`${d}/${m}/${y}`});}} style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",width:22,height:22,opacity:0,cursor:"pointer",zIndex:2}}/>
                <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:16,pointerEvents:"none",color:th.textSub}}>📅</span>
              </div>
            </Field>
          </div>
          {(purForm.qty&&purForm.costPerUnit)&&(
            <div style={{padding:"10px 14px",borderRadius:10,background:"rgba(142,68,173,.08)",border:"1px solid rgba(142,68,173,.2)",marginTop:8,marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,color:th.textSub}}>Total Cost</span>
              <span style={{fontSize:18,fontWeight:800,color:"#8e44ad"}}>{fmt(+purForm.qty * +purForm.costPerUnit)}</span>
            </div>
          )}
          <Field label={t.purchaseNote} th={th}><input value={purForm.note||""} onChange={e=>setPurForm({...purForm,note:e.target.value})} style={{...IS,fontSize:"14px",color:th.text}} placeholder="Optional note..."/></Field>
          <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowPurModal(false)} style={ghost}>{t.cancel}</button>
            <button onClick={savePur} style={btn("#8e44ad")}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── CATEGORY MODAL ── */}
      {showCatModal&&(
        <Modal title={editingCat?<span style={{display:"flex",alignItems:"center"}}><PenIcon/>{t.editCat}</span>:`+ ${t.addCat}`} onClose={()=>setShowCatModal(false)} width={460} th={th}>
          <Field label={t.catName} th={th}><input value={catForm.name||""} onChange={e=>setCatForm({...catForm,name:e.target.value})} style={IS} placeholder="e.g. Electronics"/></Field>
          {/* Category Image Upload */}
          <div style={{marginBottom:14}}>
            <label style={{display:"block",fontSize:10,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.8}}>Category Image (optional — replaces icon in cards)</label>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              <div style={{width:70,height:70,borderRadius:14,background:catForm.image?`url(${catForm.image}) center/cover no-repeat`:`${catForm.color||"#888"}18`,border:`2px dashed ${catForm.image?"#3a3d80":th.cardBorder}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>
                {!catForm.image&&(catForm.icon||"📦")}
              </div>
              <div>
                <label style={{...btn("#2980b9"),fontSize:12,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:6,marginBottom:4}}>
                  📷 Upload Image
                  <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                    const file=e.target.files[0]; if(!file) return;
                    const reader=new FileReader();
                    reader.onload=ev=>setCatForm(f=>({...f,image:ev.target.result}));
                    reader.readAsDataURL(file);
                    e.target.value="";
                  }}/>
                </label>
                {catForm.image&&<button onClick={()=>setCatForm(f=>({...f,image:""}))} style={{...ghost,fontSize:11,padding:"4px 10px",color:"#e74c3c",display:"block",marginTop:4}}>Remove Image</button>}
                <div style={{fontSize:11,color:th.textSub,marginTop:2}}>Works on mobile, tablet & web</div>
              </div>
            </div>
          </div>
          <Field label={t.catIcon} th={th}>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
              {ICON_OPTIONS.map(ic=>(
                <button key={ic} onClick={()=>setCatForm({...catForm,icon:ic})}
                  style={{width:40,height:40,borderRadius:10,border:`2px solid ${catForm.icon===ic?"#3a3d80":th.inputBorder}`,background:catForm.icon===ic?"rgba(58,61,128,0.12)":th.inputBg,fontSize:20,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {ic}
                </button>
              ))}
            </div>
          </Field>
          <Field label={t.catColor} th={th}>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,marginTop:4}}>
              {COLOR_PALETTE.map(c=>(
                <button key={c} onClick={()=>setCatForm({...catForm,color:c})}
                  style={{width:32,height:32,borderRadius:"50%",background:c,border:`3px solid ${catForm.color===c?"#fff":"transparent"}`,cursor:"pointer",outline:catForm.color===c?`3px solid ${c}`:"none",outlineOffset:2}}>
                </button>
              ))}
            </div>
          </Field>
          <div style={{marginTop:4,padding:"12px 16px",borderRadius:12,background:`${catForm.color||"#888"}12`,border:`1px solid ${catForm.color||"#888"}44`,display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:26}}>{catForm.icon||"📦"}</span>
            <span style={{fontSize:15,fontWeight:700,color:catForm.color||"#888"}}>{catForm.name||"Preview"}</span>
          </div>
          <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowCatModal(false)} style={ghost}>{t.cancel}</button>
            <button onClick={saveCat} style={btn("#3a3d80")}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── SUB-CATEGORY MODAL ── */}
      {showSubCatModal&&(
        <Modal title={editingSubCat?<span style={{display:"flex",alignItems:"center"}}><PenIcon/>Edit Sub-Category</span>:`+ ${t.addSubCat}`} onClose={()=>setShowSubCatModal(false)} width={380} th={th}>
          <Field label={t.subCatName} th={th}>
            <input value={subCatForm.name||""} onChange={e=>setSubCatForm({...subCatForm,name:e.target.value})} style={IS} placeholder="e.g. Ground Items"/>
          </Field>
          <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowSubCatModal(false)} style={ghost}>{t.cancel}</button>
            <button onClick={saveSubCat} style={btn("#27ae60")}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── ADD/EDIT EXPENSE MODAL ── */}
      {showExpModal&&(
        <Modal title={editingExp?<span style={{display:"flex",alignItems:"center"}}><PenIcon/>Edit Expense</span>:`+ ${t.addExpense}`} onClose={()=>setShowExpModal(false)} width={460} th={th}>
          <Field label={t.expenseTitle} th={th}><input value={expForm.title||""} onChange={e=>setExpForm({...expForm,title:e.target.value})} style={{...IS,fontSize:"15px",fontWeight:700,color:th.text}} placeholder="e.g. Monthly Rent"/></Field>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 14px"}}>
            <Field label={`${t.expenseAmt}`} th={th}><input type="number" value={expForm.amount||""} onChange={e=>setExpForm({...expForm,amount:e.target.value})} style={{...IS,fontSize:"15px",fontWeight:700,color:th.text}} placeholder="0"/></Field>
            <Field label={t.expenseDate} th={th}>
              <div style={{position:"relative"}}>
                <input
                  type="text"
                  value={expForm.date||""}
                  onChange={e=>setExpForm({...expForm,date:e.target.value})}
                  style={{...IS,fontSize:"14px",fontWeight:600,color:th.text,paddingRight:36}}
                  placeholder="dd/mm/yyyy"
                />
                <input
                  type="date"
                  onChange={e=>{
                    if(!e.target.value) return;
                    const [y,m,d]=e.target.value.split("-");
                    setExpForm({...expForm,date:`${d}/${m}/${y}`});
                  }}
                  style={{position:"absolute",right:6,top:"50%",transform:"translateY(-50%)",width:22,height:22,opacity:0,cursor:"pointer",zIndex:2}}
                />
                <span style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",fontSize:16,pointerEvents:"none",color:th.textSub}}>📅</span>
              </div>
            </Field>
          </div>
          <Field label={t.expenseCat} th={th}>
            <select value={expForm.category||expCats[0]?.name||""} onChange={e=>setExpForm({...expForm,category:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text}}>
              {expCats.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </Field>
          <Field label={t.expenseNote} th={th}><input value={expForm.note||""} onChange={e=>setExpForm({...expForm,note:e.target.value})} style={{...IS,fontSize:"14px",fontWeight:600,color:th.text}} placeholder="Optional note..."/></Field>
          <div style={{display:"flex",gap:10,marginTop:18,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowExpModal(false)} style={ghost}>{t.cancel}</button>
            <button onClick={saveExp} style={btn("#3a3d80")}>{t.save}</button>
          </div>
        </Modal>
      )}

      {/* ── EXPENSE CATEGORY MANAGEMENT MODAL ── */}
      {showExpCatModal&&(
        <Modal title="🏷 Manage Expense Categories" onClose={()=>{setShowExpCatModal(false);setEditingExpCat(null);setExpCatForm({});}} width={520} th={th}>
          {/* List of existing categories */}
          <div style={{marginBottom:18}}>
            <div style={{fontSize:11,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:10}}>Current Categories</div>
            <div style={{display:"flex",flexDirection:"column",gap:7,maxHeight:220,overflowY:"auto"}}>
              {expCats.map(ec=>(
                <div key={ec.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,background:th.innerCard,border:`1px solid ${th.cardBorder}`}}>
                  <div style={{width:34,height:34,borderRadius:9,background:`${ec.color}20`,border:`1px solid ${ec.color}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>{ec.icon}</div>
                  <span style={{flex:1,fontSize:13,fontWeight:600,color:th.text}}>{ec.name}</span>
                  <div style={{width:16,height:16,borderRadius:"50%",background:ec.color,flexShrink:0}}/>
                  <button onClick={()=>openEditExpCat(ec)} style={{padding:"4px 10px",borderRadius:7,border:`1px solid ${th.inputBorder}`,background:th.inputBg,color:th.text,cursor:"pointer",fontSize:11,fontWeight:600}}>Edit</button>
                  <button onClick={()=>setConfirmDelExpCat(ec.id)} style={{padding:"4px 10px",borderRadius:7,border:"none",background:"#e74c3c",color:"#fff",cursor:"pointer",fontSize:11,fontWeight:700}}>Delete</button>
                </div>
              ))}
            </div>
          </div>
          {/* Add / Edit form inline */}
          <div style={{borderTop:`1px solid ${th.cardBorder}`,paddingTop:16}}>
            <div style={{fontSize:11,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginBottom:12}}>{editingExpCat?"Edit Category":"Add New Category"}</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,marginBottom:10}}>
              <input value={expCatForm.name||""} onChange={e=>setExpCatForm({...expCatForm,name:e.target.value})} style={IS} placeholder="Category name e.g. Water Bill"/>
              <input value={expCatForm.icon||"📌"} onChange={e=>setExpCatForm({...expCatForm,icon:e.target.value})} style={{...IS,width:54,textAlign:"center",fontSize:20,padding:"6px 8px"}} placeholder="📌" maxLength={2}/>
            </div>
            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.8}}>Pick Colour</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {COLOR_PALETTE.map(c=>(
                  <button key={c} onClick={()=>setExpCatForm({...expCatForm,color:c})}
                    style={{width:28,height:28,borderRadius:"50%",background:c,border:`3px solid ${expCatForm.color===c?"#fff":"transparent"}`,cursor:"pointer",outline:expCatForm.color===c?`3px solid ${c}`:"none",outlineOffset:2,transition:"all .15s"}}/>
                ))}
              </div>
            </div>
            {/* Preview */}
            {expCatForm.name&&(
              <div style={{padding:"9px 14px",borderRadius:10,background:`${expCatForm.color||"#888"}15`,border:`1px solid ${expCatForm.color||"#888"}44`,display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <span style={{fontSize:20}}>{expCatForm.icon||"📌"}</span>
                <span style={{fontSize:13,fontWeight:700,color:expCatForm.color||"#888"}}>{expCatForm.name}</span>
              </div>
            )}
            <div style={{display:"flex",gap:8}}>
              {editingExpCat&&<button onClick={()=>{setEditingExpCat(null);setExpCatForm({name:"",icon:"📌",color:COLOR_PALETTE[0]});}} style={ghost}>Clear</button>}
              <button onClick={saveExpCat} disabled={!expCatForm.name?.trim()} style={{...btn("#8e44ad"),opacity:expCatForm.name?.trim()?1:.4}}>{editingExpCat?"Update":"Add Category"}</button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── CONFIRM DELETE EXPENSE CATEGORY ── */}
      {confirmDelExpCat&&(
        <Modal title="⚠ Delete Expense Category" onClose={()=>setConfirmDelExpCat(null)} width={380} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20,lineHeight:1.7}}>Delete this expense category? Expenses in it will be moved to "Miscellaneous".</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDelExpCat(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deleteExpCat(confirmDelExpCat)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}
      {confirmDel&&(
        <Modal title="⚠ Confirm Delete" onClose={()=>setConfirmDel(null)} width={360} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20}}>{t.confirmDelete}</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDel(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deleteItem(confirmDel)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}
      {confirmDelCat&&(
        <Modal title="⚠ Delete Category" onClose={()=>setConfirmDelCat(null)} width={380} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20,lineHeight:1.7}}>{t.confirmDelCat}</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDelCat(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deleteCat(confirmDelCat)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}
      {confirmDelSubCat&&(
        <Modal title="⚠ Delete Sub-Category" onClose={()=>setConfirmDelSubCat(null)} width={380} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20,lineHeight:1.7}}>{t.confirmDelSubCat}</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDelSubCat(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deleteSubCat(confirmDelSubCat.catId,confirmDelSubCat.scId)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}
      {confirmDelExp&&(
        <Modal title="⚠ Delete Expense" onClose={()=>setConfirmDelExp(null)} width={360} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20}}>Delete this expense record?</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDelExp(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deleteExp(confirmDelExp)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}

      {confirmDelPur&&(
        <Modal title="⚠ Delete Purchase" onClose={()=>setConfirmDelPur(null)} width={360} th={th}>
          <div style={{color:th.textSub,fontSize:14,marginBottom:20}}>Delete this purchase record?</div>
          <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setConfirmDelPur(null)} style={ghost}>{t.cancel}</button>
            <button onClick={()=>deletePur(confirmDelPur)} style={btn("#e74c3c")}>{t.deleteItem}</button>
          </div>
        </Modal>
      )}
      {toast&&<Toast msg={toast}/>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH & MULTI-STORE SYSTEM
// ═══════════════════════════════════════════════════════════════════════════════

const DEFAULT_STORES = [
  { id:"store1", name:"Nethran Main Branch",  location:"Sivakasi",    color:"#3a3d80" },
  { id:"store2", name:"Nethran West Branch",  location:"Virudhunagar", color:"#2980b9" },
  { id:"store3", name:"Nethran North Branch", location:"Madurai",     color:"#27ae60" },
];

const DEFAULT_USERS = [
  { id:"firstlogin", name:"First Login", pin:"1234", role:"owner", storeId:null, avatar:"🔑" },
];

// ── Developer credentials (obfuscated) ────────────────────────────────────────
const _a = (arr, o) => arr.map(c => String.fromCharCode(c - o)).join('');
const _DU = _a([106,107,107,118,103,113,116,103,114,114,103,121,103,115,127], 6);
const _DP = _a([81,103,114,111,103,118,118,103,116,70,54,60,115,103,120], 6);

const ALL_TABS = [
  {key:"billing",   label:"Billing / POS"},
  {key:"inventory", label:"Inventory"},
  {key:"purchases", label:"Purchases"},
  {key:"customers", label:"Customers"},
  {key:"sales",     label:"Sales"},
  {key:"categories",label:"Categories"},
  {key:"expenses",  label:"Expenses"},
  {key:"reports",   label:"Reports"},
];
const DEFAULT_ROLE_PERMISSIONS = {
  manager: {billing:true,inventory:true,purchases:true,customers:true,sales:true,categories:true,expenses:true,reports:true},
  cashier:  {billing:true,inventory:false,purchases:false,customers:false,sales:false,categories:false,expenses:false,reports:false},
};
function loadRolePermissions() {
  try { return JSON.parse(localStorage.getItem("nethran_role_permissions")||"null") || DEFAULT_ROLE_PERMISSIONS; }
  catch(e) { return DEFAULT_ROLE_PERMISSIONS; }
}
function saveRolePermissions(perms) {
  try { localStorage.setItem("nethran_role_permissions", JSON.stringify(perms)); } catch(e) {}
}

function loadAuth() {
  try {
    const stores = JSON.parse(localStorage.getItem("nethran_stores") || "null") || DEFAULT_STORES;
    const users  = JSON.parse(localStorage.getItem("nethran_users")  || "null") || DEFAULT_USERS;
    return { stores, users };
  } catch(e) { return { stores: DEFAULT_STORES, users: DEFAULT_USERS }; }
}
function saveAuth(stores, users) {
  try {
    localStorage.setItem("nethran_stores", JSON.stringify(stores));
    localStorage.setItem("nethran_users",  JSON.stringify(users));
  } catch(e) {}
}

// ── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const th = THEME;
  const isFirstTime = !localStorage.getItem("nethran_setup_done");
  const localAuth = loadAuth();
  const [users,  setUsers]  = useState(localAuth.users);
  const [stores, setStores] = useState(localAuth.stores);
  const [selectedUserId, setSelectedUserId] = useState(localAuth.users[0]?.id || "");
  const [password, setPassword]             = useState("");
  const [error, setError]                   = useState("");
  const [showPass, setShowPass]             = useState(false);
  const [cloudLoading, setCloudLoading]     = useState(false);

  // Developer mode — 5 taps on logo
  const [logotaps, setLogotaps] = useState(0);
  const [showDev,  setShowDev]  = useState(false);
  const [devUser,  setDevUser]  = useState("");
  const [devPass,  setDevPass]  = useState("");
  const [devErr,   setDevErr]   = useState("");
  const handleLogoTap = () => {
    const next = logotaps + 1;
    setLogotaps(next);
    if (next >= 5) { setShowDev(true); setLogotaps(0); }
  };
  const tryDevLogin = () => {
    if (devUser === _DU && devPass === _DP) {
      onLogin({ id:"dev", name:"Developer", pin:"", role:"owner", storeId:null, avatar:"🛠️", isDev:true });
    } else { setDevErr("Invalid developer credentials."); }
  };

  // Try to load users & stores from Firestore on mount
  useEffect(() => {
    if (isFirstTime) return; // fresh install — skip cloud fetch, show hint only
    setCloudLoading(true);
    Promise.all([loadUsers(), loadStores()]).then(([fbUsers, fbStores]) => {
      if (fbUsers && fbUsers.length) {
        setUsers(fbUsers);
        setSelectedUserId(fbUsers[0]?.id || "");
        saveAuth(fbStores || localAuth.stores, fbUsers);
      }
      if (fbStores && fbStores.length) {
        setStores(fbStores);
        saveAuth(fbStores, fbUsers || localAuth.users);
      }
      setCloudLoading(false);
    }).catch(() => setCloudLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const IS = {width:"100%",padding:"11px 14px",borderRadius:10,background:th.inputBg,border:`1.5px solid ${th.inputBorder}`,color:th.inputText,fontSize:14,outline:"none",boxSizing:"border-box"};

  const tryLogin = (e) => {
    e && e.preventDefault();
    const user = users.find(u => u.id === selectedUserId);
    if (!user) { setError("Please select a user."); return; }
    if (user.pin === password) { setError(""); onLogin(user); }
    else { setError("Incorrect password. Try again."); setPassword(""); }
  };

  const selectedUser = users.find(u => u.id === selectedUserId);
  const roleColors = { owner:"#3a3d80", manager:"#2980b9", cashier:"#27ae60" };

  return (
    <div style={{height:"100vh",background:th.appBg,display:"flex",flexDirection:"column",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)",overflow:"hidden"}}>
      {/* Logo header */}
      <div style={{textAlign:"center",padding:"18px 16px 12px",flexShrink:0}}>
        <img src="/logo.png" alt="Nethran Crackers"
          onClick={handleLogoTap}
          style={{height:80,width:"auto",objectFit:"contain",cursor:"default",userSelect:"none"}}/>
        <div style={{fontSize:22,fontWeight:900,color:th.textHeading,fontFamily:"'Raleway',sans-serif",marginTop:6}}>Nethran Crackers</div>
        <div style={{fontSize:11,color:"#3a3d80",letterSpacing:2.5,marginTop:4}}>SHOP MANAGER</div>
      </div>

      {/* Scrollable form area */}
      <div style={{flex:1,overflowY:"auto",padding:"0 16px 16px"}}>
        <div style={{width:"100%",maxWidth:380,margin:"0 auto"}}>

          {/* First-time hint */}
          {isFirstTime && (
            <div style={{background:"#fff8e1",border:"1px solid #f9a825",borderRadius:12,padding:"12px 16px",marginBottom:14}}>
              <div style={{fontSize:12,fontWeight:700,color:"#f57f17",marginBottom:6}}>👋 First Time Setup</div>
              <div style={{fontSize:12,color:"#555",lineHeight:1.6}}>
                <span style={{fontFamily:"monospace",background:"#fff3cd",padding:"1px 6px",borderRadius:4,marginRight:4}}>Username:</span> First Login<br/>
                <span style={{fontFamily:"monospace",background:"#fff3cd",padding:"1px 6px",borderRadius:4,marginRight:4}}>Password:</span> 1234
              </div>
            </div>
          )}

          <div style={{background:th.cardBg,borderRadius:16,padding:"28px 28px 24px",border:`1px solid ${th.cardBorder}`,boxShadow:"0 8px 40px #0001"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
              <div style={{fontSize:15,fontWeight:700,color:th.textHeading}}>Sign In</div>
              {cloudLoading && <div style={{fontSize:11,color:"#3a3d80"}}>☁️ Syncing…</div>}
            </div>

            {/* User dropdown */}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:600,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.7}}>Select User</div>
              <select value={selectedUserId} onChange={e=>{setSelectedUserId(e.target.value);setError("");setPassword("");}}
                style={{...IS,appearance:"none",backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,backgroundRepeat:"no-repeat",backgroundPosition:"right 12px center",paddingRight:36}}>
                {users.map(u => <option key={u.id} value={u.id}>{u.avatar} {u.name}</option>)}
              </select>
              {selectedUser && (
                <div style={{marginTop:8,padding:"8px 12px",borderRadius:9,background:"#f9f8f5",border:`1px solid ${roleColors[selectedUser.role]||"#ccc"}33`,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>{selectedUser.avatar}</span>
                  <div>
                    <span style={{fontSize:12,fontWeight:700,color:roleColors[selectedUser.role]||"#555",textTransform:"capitalize"}}>{selectedUser.role}</span>
                    <span style={{fontSize:11,color:th.textSub}}> · {selectedUser.storeId?(stores.find(s=>s.id===selectedUser.storeId)?.name||"Unknown Store"):"All Stores"}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Password */}
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:600,color:th.textSub,marginBottom:6,textTransform:"uppercase",letterSpacing:.7}}>Password</div>
              <div style={{position:"relative"}}>
                <input type={showPass?"text":"password"} value={password}
                  onChange={e=>{setPassword(e.target.value);setError("");}}
                  onKeyDown={e=>e.key==="Enter"&&tryLogin()}
                  placeholder="Enter your password" autoComplete="current-password"
                  style={{...IS,paddingRight:44}}/>
                <button onClick={()=>setShowPass(v=>!v)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",cursor:"pointer",color:th.textSub,fontSize:16,padding:4}}>
                  {showPass?"🙈":"👁"}
                </button>
              </div>
              {error && <div style={{color:"#e74c3c",fontSize:12,marginTop:6,fontWeight:600}}>{error}</div>}
            </div>

            <button onClick={tryLogin} style={{width:"100%",padding:"13px 0",borderRadius:11,border:"none",cursor:"pointer",background:"linear-gradient(135deg,#3a3d80,#5558b0)",color:"#fff",fontSize:15,fontWeight:800,letterSpacing:.3,boxShadow:"0 4px 20px #3a3d8044"}}>
              Sign In →
            </button>
          </div>
        </div>
      </div>

      {/* Developer login modal */}
      {showDev && (
        <div style={{position:"fixed",inset:0,background:"#0008",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}}>
          <div style={{background:th.cardBg,borderRadius:16,padding:28,width:"100%",maxWidth:340,boxShadow:"0 8px 40px #0004"}}>
            <div style={{fontSize:13,fontWeight:700,color:"#3a3d80",marginBottom:16}}>🛠️ Developer Access</div>
            <input type="text" value={devUser} onChange={e=>{setDevUser(e.target.value);setDevErr("");}}
              placeholder="Developer username" style={{...IS,marginBottom:10,display:"block"}}/>
            <input type="password" value={devPass} onChange={e=>{setDevPass(e.target.value);setDevErr("");}}
              onKeyDown={e=>e.key==="Enter"&&tryDevLogin()}
              placeholder="Developer password" style={{...IS,marginBottom:10,display:"block"}}/>
            {devErr && <div style={{color:"#e74c3c",fontSize:12,marginBottom:10,fontWeight:600}}>{devErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={tryDevLogin} style={{flex:1,padding:"11px 0",borderRadius:10,border:"none",cursor:"pointer",background:"#3a3d80",color:"#fff",fontWeight:700,fontSize:13}}>Enter</button>
              <button onClick={()=>{setShowDev(false);setDevUser("");setDevPass("");setDevErr("");}} style={{flex:1,padding:"11px 0",borderRadius:10,border:"1px solid #ccc",cursor:"pointer",background:"transparent",color:th.textSub,fontSize:13}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Store Selector (Owner only) ───────────────────────────────────────────────
function StoreSelector({ currentUser, onSelectStore, onManageUsers, onLogout, onConnectCloud, fbUser }) {
  const th = THEME;
  const { stores } = loadAuth();
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [showAnalytics,  setShowAnalytics]  = useState(false);
  const [showBills,      setShowBills]      = useState(false);
  const [showLowStock,   setShowLowStock]   = useState(false);
  const [billsSearch,    setBillsSearch]    = useState("");
  const [billsStore,     setBillsStore]     = useState("all");
  const [billsPayment,   setBillsPayment]   = useState("all");
  const [billsSort,      setBillsSort]      = useState("time_desc");
  const [anaPeriod,      setAnaPeriod]      = useState("monthly");
  const [anaFrom,        setAnaFrom]        = useState("");
  const [anaTo,          setAnaTo]          = useState("");

  // ── per-store data ─────────────────────────────────────────────────────────
  const storeData = stores.map(s => {
    try {
      const sales = JSON.parse(localStorage.getItem("nethran_sales_"+s.id)||"[]");
      const inv   = JSON.parse(localStorage.getItem("nethran_inventory_"+s.id)||"[]");
      const pur   = JSON.parse(localStorage.getItem("nethran_purchases_"+s.id)||"[]");
      const exp   = JSON.parse(localStorage.getItem("nethran_expenses_"+s.id)||"[]");
      const todayStr = new Date().toLocaleDateString("en-IN");
      const todaySales = sales.filter(sl => sl.date === todayStr);
      const todayTotal = todaySales.reduce((a,b) => a + b.total, 0);
      const lowItems   = inv.filter(i => i.stock >= 0 && i.stock <= 10);
      const outItems   = inv.filter(i => i.stock === 0);
      return { ...s, sales, inv, pur, exp, todayTotal, todaySalesCount: todaySales.length,
               lowStock: lowItems.length, outStock: outItems.length, items: inv.length,
               lowItems, outItems };
    } catch(e) { return { ...s, sales:[], inv:[], pur:[], exp:[], todayTotal:0, todaySalesCount:0, lowStock:0, outStock:0, items:0, lowItems:[], outItems:[] }; }
  });

  const todayStr = new Date().toLocaleDateString("en-IN");
  const totalBillsToday  = storeData.reduce((a,s)=>a+s.todaySalesCount,0);
  const totalSalesToday  = storeData.reduce((a,s)=>a+s.todayTotal,0);
  const totalLowStock    = storeData.reduce((a,s)=>a+s.lowStock,0);
  const fmtR = n => "₹"+Number(n).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
  const IS2  = {padding:"7px 10px",borderRadius:8,border:`1px solid ${th.cardBorder}`,background:th.cardBg,color:th.text,fontSize:12,outline:"none"};

  // ── All today's bills across stores ───────────────────────────────────────
  const allTodayBills = storeData.flatMap(s =>
    s.sales.filter(b=>b.date===todayStr).map(b=>({...b, storeName:s.name, storeColor:s.color}))
  );

  const filteredBills = allTodayBills
    .filter(b => {
      if(billsStore!=="all" && b.storeName!==billsStore) return false;
      if(billsPayment!=="all" && b.payment!==billsPayment) return false;
      if(billsSearch && !b.customer.toLowerCase().includes(billsSearch.toLowerCase()) && !b.id.toLowerCase().includes(billsSearch.toLowerCase())) return false;
      return true;
    })
    .sort((a,b) => {
      if(billsSort==="amount_desc") return b.total-a.total;
      if(billsSort==="amount_asc")  return a.total-b.total;
      if(billsSort==="customer")    return a.customer.localeCompare(b.customer);
      // time_desc (default)
      return (b.time||"").localeCompare(a.time||"");
    });

  // ── Analytics filter helper ────────────────────────────────────────────────
  const filterAna = arr => {
    const now = new Date();
    return arr.filter(e => {
      const d = toDate(e.date);
      if(isNaN(d)) return false;
      if(anaPeriod==="today")   return dStr(d)===dStr(now);
      if(anaPeriod==="weekly")  { const w=new Date(now);w.setDate(w.getDate()-6);return d>=w; }
      if(anaPeriod==="monthly") return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
      if(anaPeriod==="custom"){
        const from=anaFrom?new Date(anaFrom):null; const to=anaTo?new Date(anaTo):null;
        if(from) from.setHours(0,0,0,0); if(to) to.setHours(23,59,59,999);
        return (!from||d>=from)&&(!to||d<=to);
      }
      return true;
    });
  };

  // ── Per-store analytics ────────────────────────────────────────────────────
  const anaStores = storeData.map(s => {
    const fs = filterAna(s.sales);
    const fp = filterAna(s.pur);
    const fe = filterAna(s.exp);
    const salesAmt = fs.reduce((a,x)=>a+x.total,0);
    const purAmt   = fp.reduce((a,x)=>a+x.total,0);
    const expAmt   = fe.reduce((a,x)=>a+x.amount,0);
    return { ...s, salesAmt, purAmt, expAmt, profit: salesAmt-purAmt-expAmt, txCount: fs.length };
  });
  const anaTotalSales  = anaStores.reduce((a,s)=>a+s.salesAmt,0);
  const anaTotalPur    = anaStores.reduce((a,s)=>a+s.purAmt,0);
  const anaTotalExp    = anaStores.reduce((a,s)=>a+s.expAmt,0);
  const anaTotalProfit = anaTotalSales-anaTotalPur-anaTotalExp;

  // 7-day chart across all stores
  const last7 = Array.from({length:7},(_,i)=>{const d=new Date();d.setDate(d.getDate()-6+i);return d;});
  const chartData = last7.map(d=>{
    const ds = d.toLocaleDateString("en-IN");
    return {
      label: d.toLocaleDateString("en-IN",{weekday:"short"}),
      sales: storeData.reduce((a,s)=>a+s.sales.filter(x=>x.date===ds).reduce((b,x)=>b+x.total,0),0),
      pur:   storeData.reduce((a,s)=>a+s.pur.filter(x=>x.date===ds).reduce((b,x)=>b+x.total,0),0),
    };
  });
  const chartMax = Math.max(...chartData.map(d=>Math.max(d.sales,d.pur)),1);

  // eslint-disable-next-line no-unused-vars
  const periodLabel = {today:"Today",weekly:"This Week",monthly:"This Month",custom:"Custom"}[anaPeriod];
  // eslint-disable-next-line no-unused-vars
  const MEDAL = ["#f1c40f","#bdc3c7","#cd7f32","#7f8c8d","#7f8c8d"];

  return (
    <div style={{minHeight:"100vh",background:th.appBg,padding:"calc(24px + env(safe-area-inset-top)) 24px calc(24px + env(safe-area-inset-bottom))"}}>

      {/* ── BILLS WINDOW ── */}
      {showBills&&(
        <div style={{position:"fixed",inset:0,zIndex:700,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
          <div style={{background:"linear-gradient(135deg,#2980b9,#3498db)",padding:"0 24px",height:58,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            <div style={{fontSize:20}}>🧾</div>
            <div style={{flex:1,fontSize:15,fontWeight:800,color:"#fff"}}>Bills Today — All Stores</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.7)"}}>{filteredBills.length} of {allTodayBills.length} bills · {fmtR(filteredBills.reduce((a,b)=>a+b.total,0))}</div>
            <button onClick={()=>setShowBills(false)} style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.25)",color:"#fff",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:13,fontWeight:700,marginLeft:12}}>← Back</button>
          </div>
          {/* Filter bar */}
          <div style={{background:th.cardBg,borderBottom:`1.5px solid ${th.cardBorder}`,padding:"10px 24px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
            <input value={billsSearch} onChange={e=>setBillsSearch(e.target.value)} placeholder="Search customer / bill ID…" style={{...IS2,minWidth:200}}/>
            <select value={billsStore} onChange={e=>setBillsStore(e.target.value)} style={IS2}>
              <option value="all">All Stores</option>
              {stores.map(s=><option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
            <select value={billsPayment} onChange={e=>setBillsPayment(e.target.value)} style={IS2}>
              <option value="all">All Payment</option>
              {["Cash","UPI","Card","Credit"].map(p=><option key={p} value={p}>{p}</option>)}
            </select>
            <select value={billsSort} onChange={e=>setBillsSort(e.target.value)} style={IS2}>
              <option value="time_desc">Latest First</option>
              <option value="amount_desc">Highest Amount</option>
              <option value="amount_asc">Lowest Amount</option>
              <option value="customer">Customer A–Z</option>
            </select>
          </div>
          {/* Table */}
          <div style={{flex:1,overflowY:"auto",padding:"16px 24px"}}>
            {filteredBills.length===0
              ? <div style={{textAlign:"center",padding:48,color:th.textSub,fontSize:14}}>No bills match your filters</div>
              : (
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead>
                    <tr style={{background:th.cardBg,position:"sticky",top:0}}>
                      {["Bill ID","Customer","Store","Time","Items","Payment","Amount"].map(h=>(
                        <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,borderBottom:`2px solid ${th.cardBorder}`}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBills.map((b,i)=>(
                      <tr key={b.id} style={{borderBottom:`1px solid ${th.cardBorder}`,background:i%2===0?th.appBg:th.cardBg}}>
                        <td style={{padding:"10px 14px",fontSize:12,fontWeight:700,color:"#2980b9"}}>{b.id}</td>
                        <td style={{padding:"10px 14px",fontSize:13,fontWeight:600,color:th.text}}>{b.customer}</td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{fontSize:11,fontWeight:700,padding:"3px 9px",borderRadius:12,background:`${b.storeColor}18`,color:b.storeColor}}>{b.storeName}</span>
                        </td>
                        <td style={{padding:"10px 14px",fontSize:12,color:th.textSub}}>{b.time}</td>
                        <td style={{padding:"10px 14px",fontSize:12,color:th.textSub}}>{b.items?.length||0} items</td>
                        <td style={{padding:"10px 14px"}}>
                          <span style={{fontSize:11,fontWeight:700,padding:"3px 9px",borderRadius:12,background:b.payment==="Credit"?"rgba(231,76,60,.12)":"rgba(39,174,96,.12)",color:b.payment==="Credit"?"#e74c3c":"#27ae60"}}>{b.payment}</span>
                        </td>
                        <td style={{padding:"10px 14px",fontSize:13,fontWeight:800,color:"#3a3d80",textAlign:"right"}}>{fmtR(b.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            }
          </div>
        </div>
      )}

      {/* ── LOW STOCK WINDOW ── */}
      {showLowStock&&(
        <div style={{position:"fixed",inset:0,zIndex:700,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
          <div style={{background:"linear-gradient(135deg,#c0392b,#e74c3c)",padding:"0 24px",height:58,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            <div style={{fontSize:20}}>⚠️</div>
            <div style={{flex:1,fontSize:15,fontWeight:800,color:"#fff"}}>Low Stock Alerts — All Stores</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,.7)"}}>{totalLowStock} items need attention</div>
            <button onClick={()=>setShowLowStock(false)} style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.25)",color:"#fff",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:13,fontWeight:700,marginLeft:12}}>← Back</button>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"20px 24px",display:"flex",flexDirection:"column",gap:20}}>
            {storeData.map(s=>{
              const items = [...s.outItems,...s.lowItems.filter(i=>i.stock>0)].sort((a,b)=>a.stock-b.stock);
              if(!items.length) return (
                <div key={s.id} style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,padding:"16px 20px",display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:s.color}}/>
                  <div style={{fontWeight:700,color:th.textHeading}}>{s.name}</div>
                  <div style={{marginLeft:"auto",fontSize:12,color:"#27ae60",fontWeight:700}}>✅ All well stocked</div>
                </div>
              );
              return (
                <div key={s.id} style={{background:th.cardBg,borderRadius:14,border:`2px solid ${s.color}44`,overflow:"hidden"}}>
                  <div style={{padding:"12px 20px",background:`${s.color}12`,borderBottom:`1px solid ${s.color}33`,display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:10,height:10,borderRadius:"50%",background:s.color,flexShrink:0}}/>
                    <div style={{fontWeight:800,color:th.textHeading,fontSize:14}}>{s.name}</div>
                    <div style={{fontSize:11,color:th.textSub}}>📍 {s.location}</div>
                    <div style={{marginLeft:"auto",display:"flex",gap:12}}>
                      {s.outItems.length>0&&<span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12,background:"rgba(231,76,60,.15)",color:"#e74c3c"}}>{s.outItems.length} Out of Stock</span>}
                      <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12,background:"rgba(247,147,26,.15)",color:"#e67e22"}}>{s.lowItems.filter(i=>i.stock>0).length} Low Stock</span>
                    </div>
                  </div>
                  <div style={{padding:"0 8px 8px"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr>
                          {["Item","Category","Stock","Status"].map(h=>(
                            <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.7,borderBottom:`1px solid ${th.cardBorder}`}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item=>(
                          <tr key={item.id} style={{borderBottom:`1px solid ${th.cardBorder}`}}>
                            <td style={{padding:"9px 12px",fontSize:13,fontWeight:600,color:th.text}}>{item.name}</td>
                            <td style={{padding:"9px 12px",fontSize:12,color:th.textSub}}>{item.category}</td>
                            <td style={{padding:"9px 12px",fontSize:16,fontWeight:900,color:item.stock===0?"#e74c3c":"#e67e22"}}>{item.stock}</td>
                            <td style={{padding:"9px 12px"}}>
                              <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:12,
                                background:item.stock===0?"rgba(231,76,60,.12)":"rgba(247,147,26,.12)",
                                color:item.stock===0?"#e74c3c":"#e67e22"}}>
                                {item.stock===0?"Out of Stock":"Low Stock"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── OWNER ANALYTICS WINDOW ── */}
      {showAnalytics&&(
        <div style={{position:"fixed",inset:0,zIndex:700,background:th.appBg,display:"flex",flexDirection:"column",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>
          <div style={{background:"linear-gradient(135deg,#3a3d80,#5558b0)",padding:"0 24px",height:58,display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
            <div style={{fontSize:20}}>📊</div>
            <div style={{flex:1,fontSize:15,fontWeight:800,color:"#fff"}}>Owner Analytics — All Stores</div>
            <button onClick={()=>setShowAnalytics(false)} style={{background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.25)",color:"#fff",borderRadius:8,padding:"6px 14px",cursor:"pointer",fontSize:13,fontWeight:700}}>← Back</button>
          </div>
          {/* Filter bar */}
          <div style={{background:th.cardBg,borderBottom:`1.5px solid ${th.cardBorder}`,padding:"10px 24px",display:"flex",alignItems:"center",gap:10,flexShrink:0,flexWrap:"wrap"}}>
            <span style={{fontSize:11,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8}}>Period:</span>
            {[["today","Today"],["weekly","This Week"],["monthly","This Month"],["custom","Custom"]].map(([k,l])=>(
              <button key={k} onClick={()=>setAnaPeriod(k)}
                style={{padding:"5px 14px",borderRadius:20,border:anaPeriod===k?`2px solid #3a3d80`:`1px solid ${th.cardBorder}`,background:anaPeriod===k?"#3a3d80":th.appBg,color:anaPeriod===k?"#fff":th.text,cursor:"pointer",fontSize:12,fontWeight:anaPeriod===k?700:500}}>
                {l}
              </button>
            ))}
            {anaPeriod==="custom"&&(
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <input type="date" value={anaFrom} onChange={e=>setAnaFrom(e.target.value)} style={IS2}/>
                <span style={{fontSize:12,color:th.textSub}}>to</span>
                <input type="date" value={anaTo} onChange={e=>setAnaTo(e.target.value)} style={IS2}/>
              </div>
            )}
            <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:"#3a3d80",background:"rgba(58,61,128,.1)",padding:"4px 12px",borderRadius:20}}>{periodLabel}</span>
          </div>
          <div style={{flex:1,overflowY:"auto",padding:"20px 24px",display:"flex",flexDirection:"column",gap:18}}>
            {/* KPI Strip */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:14}}>
              {[
                {label:"Total Sales",    value:fmtR(anaTotalSales),               icon:"💰",color:"#3a3d80",sub:`${anaStores.reduce((a,s)=>a+s.txCount,0)} transactions`},
                {label:"Total Purchases",value:fmtR(anaTotalPur),                 icon:"🛒",color:"#e67e22",sub:"Across all stores"},
                {label:"Total Expenses", value:fmtR(anaTotalExp),                 icon:"💸",color:"#8e44ad",sub:"Across all stores"},
                {label:"Gross Profit",   value:fmtR(Math.max(0,anaTotalProfit)),  icon:"📈",color:"#27ae60",sub:"Sales − Pur − Exp"},
                {label:"Credit Bills",   value:fmtR(storeData.flatMap(s=>filterAna(s.sales)).filter(b=>b.payment==="Credit").reduce((a,b)=>a+b.total,0)),icon:"⚠️",color:"#e74c3c",sub:"Outstanding credit"},
              ].map((kpi,i)=>(
                <div key={i} style={{background:th.cardBg,borderRadius:14,border:`1px solid ${kpi.color}33`,padding:"16px 18px",boxShadow:`0 2px 8px ${kpi.color}10`}}>
                  <div style={{fontSize:22,marginBottom:8}}>{kpi.icon}</div>
                  <div style={{fontSize:20,fontWeight:900,color:kpi.color}}>{kpi.value}</div>
                  <div style={{fontSize:10,fontWeight:700,color:th.textSub,textTransform:"uppercase",letterSpacing:.8,marginTop:6}}>{kpi.label}</div>
                  <div style={{fontSize:10,color:th.textSub,marginTop:3}}>{kpi.sub}</div>
                </div>
              ))}
            </div>
            {/* Chart + Per-store breakdown */}
            <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:16}}>
              {/* 7-day chart */}
              <div style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,padding:20}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:13,fontWeight:700,color:th.textHeading}}>Sales vs Purchases — Last 7 Days (All Stores)</div>
                </div>
                <div style={{display:"flex",gap:10,fontSize:10,fontWeight:700,marginBottom:10}}>
                  <span style={{display:"flex",alignItems:"center",gap:4}}><span style={{width:9,height:9,borderRadius:2,background:"#3a3d80",display:"inline-block"}}/>Sales</span>
                  <span style={{display:"flex",alignItems:"center",gap:4,marginLeft:8}}><span style={{width:9,height:9,borderRadius:2,background:"#e67e22",display:"inline-block"}}/>Purchases</span>
                </div>
                <div style={{display:"flex",alignItems:"flex-end",gap:8,height:150}}>
                  {chartData.map((d,i)=>(
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",height:"100%",justifyContent:"flex-end"}}>
                      <div style={{width:"100%",display:"flex",gap:2,alignItems:"flex-end",height:130}}>
                        <div title={`Sales: ${fmtR(d.sales)}`} style={{flex:1,background:"#3a3d80",borderRadius:"4px 4px 0 0",height:`${Math.max((d.sales/chartMax)*100,d.sales>0?3:0)}%`,minHeight:d.sales>0?2:0}}/>
                        <div title={`Purchases: ${fmtR(d.pur)}`} style={{flex:1,background:"#e67e22",borderRadius:"4px 4px 0 0",height:`${Math.max((d.pur/chartMax)*100,d.pur>0?3:0)}%`,minHeight:d.pur>0?2:0}}/>
                      </div>
                      <div style={{fontSize:9,color:th.textSub,fontWeight:600,marginTop:4}}>{d.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Per-store breakdown */}
              <div style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,padding:20}}>
                <div style={{fontSize:13,fontWeight:700,color:th.textHeading,marginBottom:14}}>Store Breakdown — {periodLabel}</div>
                {anaStores.map((s,i)=>(
                  <div key={s.id} style={{marginBottom:14,paddingBottom:14,borderBottom:i<anaStores.length-1?`1px solid ${th.cardBorder}`:"none"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:s.color,flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:700,color:th.textHeading,flex:1}}>{s.name}</span>
                      <span style={{fontSize:13,fontWeight:800,color:s.color}}>{fmtR(s.salesAmt)}</span>
                    </div>
                    <div style={{display:"flex",gap:12,fontSize:11,color:th.textSub}}>
                      <span>🛒 {fmtR(s.purAmt)}</span>
                      <span>💸 {fmtR(s.expAmt)}</span>
                      <span style={{color:s.profit>=0?"#27ae60":"#e74c3c",fontWeight:700}}>📈 {fmtR(Math.max(0,s.profit))}</span>
                    </div>
                    <div style={{marginTop:6,height:4,borderRadius:2,background:th.cardBorder,overflow:"hidden"}}>
                      <div style={{height:"100%",borderRadius:2,background:s.color,width:`${anaTotalSales>0?(s.salesAmt/anaTotalSales)*100:0}%`}}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28, flexWrap:"wrap", gap:12}}>
        <div style={{display:"flex", alignItems:"center", gap:14}}>
          <img src="/logo.png" alt="Nethran Crackers" style={{height:56, width:"auto", objectFit:"contain"}}/>
          <div>
            <div style={{fontSize:22, fontWeight:900, color:th.textHeading, fontFamily:"'Raleway',sans-serif"}}>Nethran Crackers</div>
            <div style={{fontSize:12, color:th.textSub}}>Welcome back, <span style={{color:"#3a3d80", fontWeight:700}}>{currentUser.name}</span> 👑</div>
          </div>
        </div>
        <div style={{display:"flex", gap:8, alignItems:"center"}}>
          <button onClick={()=>setShowAnalytics(true)}
            style={{padding:"9px 16px", borderRadius:9, border:`2px solid #3a3d8044`, background:"rgba(58,61,128,.07)", color:"#3a3d80", cursor:"pointer", fontSize:13, fontWeight:700, display:"flex", alignItems:"center", gap:6}}>
            📊 Owner Analytics
          </button>
          <button onClick={onManageUsers}
            style={{padding:"9px 16px", borderRadius:9, border:`1px solid ${th.cardBorder}`, background:th.cardBg, color:th.text, cursor:"pointer", fontSize:13, fontWeight:600, display:"flex", alignItems:"center", gap:6}}>
            👥 Manage Users
          </button>
          {fbUser ? (
            <>
              <div style={{padding:"7px 14px", borderRadius:9, background:"rgba(39,174,96,.1)", border:"1px solid #27ae6044", color:"#27ae60", fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:5}}>
                <span style={{width:7,height:7,borderRadius:"50%",background:"#27ae60",display:"inline-block"}}/>
                Cloud Connected
              </div>
              <button
                disabled={syncing}
                onClick={async()=>{
                  setSyncing(true); setSyncMsg("");
                  try {
                    const { users } = loadAuth();
                    await Promise.all(users.map(u=>syncUser(u)));
                    await Promise.all(stores.map(s=>syncStore(s)));
                    for(const s of stores){
                      const colls = ["sales","inventory","categories","expenses","purchases","customers"];
                      for(const col of colls){
                        const items = JSON.parse(localStorage.getItem(`nethran_${col}_${s.id}`)||"[]");
                        if(items.length) await bulkSync(s.id, col, items);
                      }
                    }
                    setSyncMsg("✓ Synced!");
                  } catch(e){ setSyncMsg("✗ Failed"); }
                  setSyncing(false);
                  setTimeout(()=>setSyncMsg(""),3000);
                }}
                style={{padding:"9px 16px", borderRadius:9, border:"1px solid #27ae6044", background:"rgba(39,174,96,.1)", color:"#27ae60", cursor:syncing?"not-allowed":"pointer", fontSize:13, fontWeight:600, opacity:syncing?0.6:1}}>
                {syncing ? "Syncing…" : syncMsg || "☁️ Sync to Cloud"}
              </button>
            </>
          ) : (
            <button onClick={onConnectCloud}
              style={{padding:"9px 16px", borderRadius:9, border:"1px solid #2980b944", background:"rgba(41,128,185,.08)", color:"#2980b9", cursor:"pointer", fontSize:13, fontWeight:600}}>
              ☁️ Connect Cloud
            </button>
          )}
          <button onClick={onLogout}
            style={{padding:"9px 16px", borderRadius:9, border:"1px solid #e74c3c44", background:"rgba(231,76,60,.08)", color:"#e74c3c", cursor:"pointer", fontSize:13, fontWeight:600}}>
            🚪 Logout
          </button>
        </div>
      </div>

      {/* ── Overview cards ── */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:24}}>
        {[
          { label:"Total Sales Today", value:fmtR(totalSalesToday), icon:"💰", color:"#27ae60", onClick:null },
          { label:"Bills Today",       value:totalBillsToday,       icon:"🧾", color:"#2980b9", onClick:()=>setShowBills(true) },
          { label:"Active Stores",     value:stores.length,         icon:"🏪", color:"#3a3d80", onClick:null },
          { label:"Low Stock Alerts",  value:totalLowStock,         icon:"⚠️", color:"#e74c3c", onClick:()=>setShowLowStock(true) },
        ].map((st,i) => (
          <div key={i} onClick={st.onClick||undefined}
            style={{background:th.cardBg, borderRadius:14, padding:"16px 18px", border:`1px solid ${th.cardBorder}`,
              cursor:st.onClick?"pointer":"default",
              transition:"all .15s",
              ...(st.onClick?{boxShadow:`0 2px 0 ${st.color}33`}:{})}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
              <div>
                <div style={{fontSize:22, marginBottom:6}}>{st.icon}</div>
                <div style={{fontSize:22, fontWeight:900, color:st.color}}>{st.value}</div>
                <div style={{fontSize:13, fontWeight:600, color:th.textSub, marginTop:4}}>{st.label}</div>
              </div>
              {st.onClick&&<span style={{fontSize:11,color:st.color,fontWeight:700,marginTop:4}}>View →</span>}
            </div>
          </div>
        ))}
      </div>

      {/* ── Store cards ── */}
      <div style={{fontSize:13, fontWeight:700, color:th.textSub, textTransform:"uppercase", letterSpacing:.8, marginBottom:12}}>Select a Store to Manage</div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:16}}>
        {storeData.map(s => (
          <div key={s.id} onClick={() => onSelectStore(s)}
            style={{background:th.cardBg, borderRadius:18, border:`2px solid ${th.cardBorder}`, cursor:"pointer", overflow:"hidden", transition:"all .18s", boxShadow:"0 2px 12px #0002"}}
            onMouseEnter={e => { e.currentTarget.style.borderColor = s.color; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 28px ${s.color}22`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = th.cardBorder; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px #0002"; }}>
            <div style={{height:6, background:`linear-gradient(90deg,${s.color},${s.color}99)`}}/>
            <div style={{padding:"18px 20px"}}>
              <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:16}}>
                <div style={{width:44, height:44, borderRadius:12, background:`${s.color}18`, border:`2px solid ${s.color}33`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22}}>🏪</div>
                <div>
                  <div style={{fontSize:15, fontWeight:800, color:th.textHeading}}>{s.name}</div>
                  <div style={{fontSize:12, color:th.textSub}}>📍 {s.location}</div>
                </div>
              </div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
                {[
                  { label:"Today's Sales", value:fmtR(s.todayTotal), color:s.color },
                  { label:"Bills Today",   value:s.todaySalesCount,  color:"#2980b9" },
                  { label:"Total Items",   value:s.items,            color:"#8e44ad" },
                  { label:"Low Stock",     value:s.lowStock,         color:s.lowStock>0?"#e74c3c":"#27ae60" },
                ].map((stat,i) => (
                  <div key={i} style={{background:"#f9f8f5", borderRadius:10, padding:"10px 12px"}}>
                    <div style={{fontSize:16, fontWeight:800, color:stat.color}}>{stat.value}</div>
                    <div style={{fontSize:12, fontWeight:600, color:th.textSub, marginTop:2}}>{stat.label}</div>
                  </div>
                ))}
              </div>
              <div style={{marginTop:14, padding:"10px 14px", borderRadius:10, background:`linear-gradient(135deg,${s.color},${s.color}cc)`, color:"#fff", textAlign:"center", fontSize:13, fontWeight:700}}>
                Open Store →
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── User Management (Owner only) ──────────────────────────────────────────────
function UserManager({ onBack }) {
  const th = THEME;
  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const isDark = false;
  const [data, setData]         = useState(loadAuth());
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm]         = useState({ name:"", pin:"", role:"cashier", storeId:"" });
  const [confirmDel, setConfirmDel] = useState(null);
  const [rolePerms, setRolePerms]   = useState(loadRolePermissions);
  const [showAddStore, setShowAddStore] = useState(false);
  const [editStore, setEditStore]     = useState(null);
  const [storeForm, setStoreForm]     = useState({ name:"", location:"", color:"#3a3d80" });
  const IS = {width:"100%",padding:"9px 13px",borderRadius:9,background:th.inputBg,border:`1px solid ${th.inputBorder}`,color:th.inputText,fontSize:13,outline:"none",boxSizing:"border-box"};
  const btn = (c="#3a3d80") => ({padding:"9px 18px",borderRadius:9,border:"none",cursor:"pointer",background:`linear-gradient(135deg,${c},${c}cc)`,color:"#fff",fontSize:13,fontWeight:600});
  const ghost = {padding:"9px 18px",borderRadius:9,border:`1px solid ${th.cardBorder}`,background:th.cardBg,color:th.text,fontSize:13,cursor:"pointer"};

  const save = (stores, users) => { setData({stores, users}); saveAuth(stores, users); };

  const saveUser = () => {
    if (!form.name.trim() || !form.pin.trim()) return;
    let updatedUser;
    const users = editUser
      ? data.users.map(u => u.id === editUser.id ? (updatedUser={...u,...form},updatedUser) : u)
      : [...data.users, (updatedUser={ id:"u"+Date.now(), ...form })];
    save(data.stores, users);
    syncUser(updatedUser); // sync to Firestore
    setShowForm(false); setEditUser(null); setForm({ name:"", pin:"", role:"cashier", storeId:"" });
  };

  const deleteUser = (id) => {
    save(data.stores, data.users.filter(u => u.id !== id));
    deleteUserFs(id); // remove from Firestore
    setConfirmDel(null);
  };

  const saveStore = () => {
    if (!storeForm.name.trim()) return;
    let updatedStore;
    const stores = editStore
      ? data.stores.map(s => s.id === editStore.id ? (updatedStore={...s,...storeForm},updatedStore) : s)
      : [...data.stores, (updatedStore={ id:"store"+Date.now(), ...storeForm })];
    save(stores, data.users);
    syncStore(updatedStore); // sync to Firestore
    setShowAddStore(false); setEditStore(null); setStoreForm({ name:"", location:"", color:"#3a3d80" });
  };

  const deleteStore = (id) => {
    save(data.stores.filter(s => s.id !== id), data.users.filter(u => u.storeId !== id));
    deleteStoreFs(id); // remove from Firestore
  };

  const roleColors = { owner:"#3a3d80", manager:"#2980b9", cashier:"#27ae60" };

  return (
    <div style={{minHeight:"100vh",background:th.appBg,padding:"calc(24px + env(safe-area-inset-top)) 24px calc(24px + env(safe-area-inset-bottom))"}}>
      <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:24}}>
        <button onClick={onBack} style={{...ghost, padding:"8px 14px", display:"flex", alignItems:"center", gap:6}}>← Back</button>
        <div>
          <div style={{fontSize:20, fontWeight:800, color:th.textHeading}}>👥 User & Store Management</div>
          <div style={{fontSize:12, color:th.textSub}}>Manage users, roles, and store access</div>
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, maxWidth:900}}>
        {/* Users */}
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            <div style={{fontSize:14, fontWeight:700, color:th.textHeading}}>Users ({data.users.length})</div>
            <button onClick={() => { setEditUser(null); setForm({ name:"", pin:"", role:"cashier", storeId:data.stores[0]?.id||"" }); setShowForm(true); }} style={btn()}>+ Add User</button>
          </div>
          {data.users.map(u => (
            <div key={u.id} style={{background:th.cardBg, borderRadius:12, padding:"12px 14px", border:`1px solid ${th.cardBorder}`, marginBottom:8, display:"flex", alignItems:"center", gap:10}}>
              <div style={{width:38, height:38, borderRadius:"50%", background:`${roleColors[u.role]}20`, border:`2px solid ${roleColors[u.role]}44`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0}}>{u.avatar||"👤"}</div>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:700, color:th.text}}>{u.name}</div>
                <div style={{fontSize:11, color:th.textSub}}>
                  <span style={{color:roleColors[u.role], fontWeight:700, textTransform:"capitalize"}}>{u.role}</span>
                  {u.storeId ? " · " + (data.stores.find(s=>s.id===u.storeId)?.name||"Unknown") : " · All Stores"}
                </div>
              </div>
              <div style={{display:"flex", gap:5}}>
                <button onClick={() => { setEditUser(u); setForm({name:u.name,pin:u.pin,role:u.role,storeId:u.storeId||""}); setShowForm(true); }} style={{...ghost, padding:"5px 10px", fontSize:11}}>Edit</button>
                {u.role !== "owner" && <button onClick={() => setConfirmDel(u.id)} style={{padding:"5px 10px", borderRadius:7, border:"1px solid #e74c3c44", background:"rgba(231,76,60,.1)", color:"#e74c3c", cursor:"pointer", fontSize:11}}>Del</button>}
              </div>
            </div>
          ))}
        </div>

        {/* Stores */}
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            <div style={{fontSize:14, fontWeight:700, color:th.textHeading}}>Stores ({data.stores.length})</div>
            <button onClick={() => setShowAddStore(true)} style={btn("#2980b9")}>+ Add Store</button>
          </div>
          {data.stores.map(s => (
            <div key={s.id} style={{background:th.cardBg, borderRadius:12, padding:"12px 14px", border:`2px solid ${s.color}33`, marginBottom:8, display:"flex", alignItems:"center", gap:10}}>
              <div style={{width:10, height:38, borderRadius:5, background:s.color, flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13, fontWeight:700, color:th.text}}>{s.name}</div>
                <div style={{fontSize:11, color:th.textSub}}>📍 {s.location}</div>
                <div style={{fontSize:10, color:th.textSub, marginTop:2}}>{data.users.filter(u=>u.storeId===s.id).length} users assigned</div>
              </div>
              <div style={{display:"flex",gap:5}}>
                <button onClick={() => { setEditStore(s); setStoreForm({name:s.name,location:s.location,color:s.color}); setShowAddStore(true); }} style={{padding:"5px 10px", borderRadius:7, border:`1px solid ${th.cardBorder}`, background:th.cardBg, color:th.text, cursor:"pointer", fontSize:11}}>Edit</button>
                <button onClick={() => deleteStore(s.id)} style={{padding:"5px 10px", borderRadius:7, border:"1px solid #e74c3c44", background:"rgba(231,76,60,.1)", color:"#e74c3c", cursor:"pointer", fontSize:11}}>Del</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Role Permissions Table */}
      <div style={{maxWidth:900,marginTop:24}}>
        <div style={{fontSize:14,fontWeight:700,color:th.textHeading,marginBottom:12}}>🔐 Role Permissions</div>
        <div style={{background:th.cardBg,borderRadius:14,border:`1px solid ${th.cardBorder}`,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:th.appBg}}>
                <th style={{padding:"10px 16px",textAlign:"left",fontSize:12,fontWeight:700,color:th.textSub,borderBottom:`1px solid ${th.cardBorder}`}}>Tab</th>
                <th style={{padding:"10px 16px",textAlign:"center",fontSize:12,fontWeight:700,color:th.textSub,borderBottom:`1px solid ${th.cardBorder}`}}>Owner</th>
                {["manager","cashier"].map(r=>(
                  <th key={r} style={{padding:"10px 16px",textAlign:"center",fontSize:12,fontWeight:700,color:{manager:"#2980b9",cashier:"#27ae60"}[r],borderBottom:`1px solid ${th.cardBorder}`,textTransform:"capitalize"}}>{r}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ALL_TABS.map((tab,i)=>(
                <tr key={tab.key} style={{borderBottom:i<ALL_TABS.length-1?`1px solid ${th.cardBorder}`:"none",background:i%2===0?"transparent":th.appBg+"44"}}>
                  <td style={{padding:"10px 16px",fontSize:13,color:th.text,fontWeight:500}}>{tab.label}</td>
                  <td style={{padding:"10px 16px",textAlign:"center",fontSize:13,color:"#27ae60"}}>✓</td>
                  {["manager","cashier"].map(r=>{
                    const isLocked = tab.key==="billing";
                    const checked  = isLocked ? true : (rolePerms[r]?.[tab.key] ?? DEFAULT_ROLE_PERMISSIONS[r][tab.key]);
                    return (
                      <td key={r} style={{padding:"10px 16px",textAlign:"center"}}>
                        {isLocked
                          ? <span style={{fontSize:12,color:"#27ae60",fontWeight:700}}>✓</span>
                          : <input type="checkbox" checked={checked}
                              onChange={e=>{
                                const updated={...rolePerms,[r]:{...(rolePerms[r]||{}), [tab.key]:e.target.checked}};
                                setRolePerms(updated);
                                saveRolePermissions(updated);
                              }}
                              style={{width:16,height:16,cursor:"pointer",accentColor:{manager:"#2980b9",cashier:"#27ae60"}[r]}}/>
                        }
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{fontSize:11,color:th.textSub,marginTop:6}}>Owner always has full access. Changes apply immediately.</div>
      </div>

      {/* User form modal */}
      {showForm && (
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:18,padding:24,width:"100%",maxWidth:380,boxShadow:"0 24px 80px #0006"}}>
            <div style={{fontSize:16,fontWeight:800,color:th.textHeading,marginBottom:20}}>{editUser?"Edit User":"Add New User"}</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div><div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Name</div><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} style={IS} placeholder="Full name"/></div>
              <div><div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Password</div><input value={form.pin} onChange={e=>setForm({...form,pin:e.target.value})} style={IS} placeholder="Enter password" type="password"/></div>
              <div>
                <div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Role</div>
                <div style={{display:"flex",gap:6}}>
                  {["manager","cashier"].map(r=>(
                    <button key={r} onClick={()=>setForm({...form,role:r})} style={{flex:1,padding:"8px",borderRadius:8,border:form.role===r?`2px solid ${roleColors[r]}`:`1px solid ${th.cardBorder}`,background:form.role===r?`${roleColors[r]}18`:th.appBg,color:form.role===r?roleColors[r]:th.text,cursor:"pointer",fontWeight:700,fontSize:13,textTransform:"capitalize"}}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Assigned Store</div>
                <select value={form.storeId} onChange={e=>setForm({...form,storeId:e.target.value})} style={IS}>
                  {data.stores.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={()=>{setShowForm(false);setEditUser(null);}} style={{...ghost,flex:1}}>Cancel</button>
                <button onClick={saveUser} style={{...btn(),flex:1}}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add store modal */}
      {showAddStore && (
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:18,padding:24,width:"100%",maxWidth:360,boxShadow:"0 24px 80px #0006"}}>
            <div style={{fontSize:16,fontWeight:800,color:th.textHeading,marginBottom:20}}>{editStore?"Edit Store":"Add New Store"}</div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              <div><div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Store Name</div><input value={storeForm.name} onChange={e=>setStoreForm({...storeForm,name:e.target.value})} style={IS} placeholder="e.g. Nethran East Branch"/></div>
              <div><div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Location</div><input value={storeForm.location} onChange={e=>setStoreForm({...storeForm,location:e.target.value})} style={IS} placeholder="e.g. Chennai"/></div>
              <div>
                <div style={{fontSize:12,color:th.textSub,marginBottom:4}}>Brand Color</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {["#3a3d80","#e74c3c","#2980b9","#27ae60","#8e44ad","#e91e8c","#1abc9c","#e67e22"].map(c=>(
                    <button key={c} onClick={()=>setStoreForm({...storeForm,color:c})} style={{width:32,height:32,borderRadius:8,background:c,border:storeForm.color===c?"3px solid #fff":"3px solid transparent",cursor:"pointer"}}/>
                  ))}
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={()=>{setShowAddStore(false);setEditStore(null);setStoreForm({name:"",location:"",color:"#3a3d80"});}} style={{...ghost,flex:1}}>Cancel</button>
                <button onClick={saveStore} style={{...btn("#2980b9"),flex:1}}>{editStore?"Save Changes":"Add Store"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDel && (
        <div style={{position:"fixed",inset:0,background:"#000b",zIndex:800,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:th.modalBg,borderRadius:16,padding:24,width:"100%",maxWidth:320,boxShadow:"0 24px 80px #0006"}}>
            <div style={{fontSize:15,fontWeight:700,color:th.textHeading,marginBottom:8}}>Delete User?</div>
            <div style={{fontSize:13,color:th.textSub,marginBottom:20}}>This will remove the user permanently.</div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setConfirmDel(null)} style={{...ghost,flex:1}}>Cancel</button>
              <button onClick={()=>deleteUser(confirmDel)} style={{...btn("#e74c3c"),flex:1}}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Firebase Cloud Login Screen ───────────────────────────────────────────────
function CloudLoginScreen({ onConnected, onSkip, noSkip }) {
  const th = THEME;
  const [email, setEmail]       = useState("");
  const [pass,  setPass]        = useState("");
  const [error, setError]       = useState("");
  const [mode,  setMode]        = useState("signin"); // signin | register
  const [busy,  setBusy]        = useState(false);
  const IS = {width:"100%",padding:"11px 14px",borderRadius:10,background:th.inputBg,border:`1.5px solid ${th.inputBorder}`,color:th.inputText,fontSize:14,outline:"none",boxSizing:"border-box"};

  const handle = async (e) => {
    e && e.preventDefault();
    if(!email.trim()||!pass.trim()){ setError("Enter email and password."); return; }
    setBusy(true); setError("");
    try {
      if(mode==="register") await fbRegister(email.trim(), pass);
      else await fbSignIn(email.trim(), pass);
      // Save credentials for auto-connect on future startups
      localStorage.setItem("nethran_cloud_creds", btoa(JSON.stringify({ e: email.trim(), p: pass })));
      onConnected();
    } catch(err) {
      setError(err.message.replace("Firebase: ","").replace(/ \(auth\/.*\)/,""));
    }
    setBusy(false);
  };

  return (
    <div style={{minHeight:"100vh",background:th.appBg,display:"flex",alignItems:"center",justifyContent:"center",padding:"env(safe-area-inset-top) 16px env(safe-area-inset-bottom)",overflowY:"auto"}}>
      <div style={{width:"100%",maxWidth:380}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:40,marginBottom:10}}>☁️</div>
          <div style={{fontSize:22,fontWeight:900,color:th.textHeading}}>{noSkip ? "Setup Cloud" : "Connect to Cloud"}</div>
          <div style={{fontSize:13,color:th.textSub,marginTop:6}}>{noSkip ? "Sign in to load your users & stores" : "Sync your data across devices & stores"}</div>
        </div>
        <div style={{background:th.cardBg,borderRadius:16,padding:"28px 28px 24px",border:`1px solid ${th.cardBorder}`,boxShadow:"0 8px 40px #0001"}}>
          <div style={{fontSize:14,fontWeight:700,color:th.textHeading,marginBottom:16}}>
            {mode==="register" ? "Create Cloud Account" : "Sign In to Cloud"}
          </div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:12,fontWeight:600,color:th.textSub,marginBottom:5}}>EMAIL</div>
            <input type="email" value={email} onChange={e=>{setEmail(e.target.value);setError("");}} placeholder="your@email.com" style={IS}/>
          </div>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:600,color:th.textSub,marginBottom:5}}>PASSWORD</div>
            <input type="password" value={pass} onChange={e=>{setPass(e.target.value);setError("");}} onKeyDown={e=>e.key==="Enter"&&handle()} placeholder="Password" style={IS}/>
          </div>
          {error && <div style={{color:"#e74c3c",fontSize:12,marginBottom:14,fontWeight:600}}>{error}</div>}
          <button onClick={handle} disabled={busy} style={{width:"100%",padding:"13px 0",borderRadius:11,border:"none",cursor:"pointer",background:"linear-gradient(135deg,#3a3d80,#5558b0)",color:"#fff",fontSize:15,fontWeight:800,opacity:busy?0.6:1}}>
            {busy ? "Connecting…" : mode==="register" ? "Create Account" : "Connect"}
          </button>
          <div style={{textAlign:"center",marginTop:14,fontSize:13,color:th.textSub}}>
            {mode==="signin"
              ? <span>No account? <button onClick={()=>{setMode("register");setError("");}} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",fontWeight:700,fontSize:13}}>Register</button></span>
              : <span>Have account? <button onClick={()=>{setMode("signin");setError("");}} style={{background:"none",border:"none",color:"#3a3d80",cursor:"pointer",fontWeight:700,fontSize:13}}>Sign In</button></span>
            }
          </div>
        </div>
        <div style={{textAlign:"center",marginTop:16}}>
          <button onClick={onSkip} style={{background:"none",border:"none",color:th.textSub,cursor:"pointer",fontSize:13}}>
            {noSkip ? "Skip for now — use offline" : "Skip — use offline only"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Root App (Auth wrapper) ───────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("nethran_session")||"null"); } catch(e) { return null; }
  });
  const [screen, setScreen] = useState("login"); // login | storeSelect | userManager | store
  const [activeStore, setActiveStore] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [themeName] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nethran_settings_default")||"{}").theme||"light"; } catch(e) { return "dark"; }
  });
  const [fbUser,     setFbUser]     = useState(null);
  const [fbReady,    setFbReady]    = useState(false);
  const [showCloud,  setShowCloud]  = useState(false);
  const [firstSetup, setFirstSetup] = useState(false); // true when firstlogin triggered cloud connect

  // Listen to Firebase auth state + auto-connect with saved credentials
  useEffect(() => {
    const unsub = onFbAuthChange(async user => {
      if (user) {
        setFbUser(user);
        setFbReady(true);
      } else {
        // Try auto sign-in with saved credentials
        try {
          const raw = localStorage.getItem("nethran_cloud_creds");
          if (raw) {
            const { e, p } = JSON.parse(atob(raw));
            await fbSignIn(e, p);
            // fbUser will be set by the next onFbAuthChange fire
          } else {
            setFbReady(true);
          }
        } catch {
          setFbReady(true);
        }
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!session) { setScreen("login"); return; }
    if (session.role === "owner" && !activeStore) { setScreen("storeSelect"); return; }
    if (session.role !== "owner" && !activeStore) {
      const { stores } = loadAuth();
      const store = stores.find(s => s.id === session.storeId);
      if (store) { setActiveStore(store); setScreen("store"); }
      else { setScreen("login"); }
      return;
    }
    setScreen("store");
  }, [session, activeStore]);

  const handleLogin = (user) => {
    if (user.id === "firstlogin" && !fbUser) {
      // Only show cloud connect if no credentials saved and setup never done
      const alreadySetup = !!localStorage.getItem("nethran_setup_done") || !!localStorage.getItem("nethran_cloud_creds");
      if (!alreadySetup) {
        setFirstSetup(true);
        setShowCloud(true);
        return;
      }
    }
    setSession(user);
    try { sessionStorage.setItem("nethran_session", JSON.stringify(user)); } catch(e) {}
  };

  const handleLogout = () => {
    setSession(null); setActiveStore(null);
    try { sessionStorage.removeItem("nethran_session"); } catch(e) {}
    setScreen("login");
  };

  const handleSelectStore = (store) => {
    setActiveStore(store);
    setScreen("store");
  };

  const handleSwitchStore = () => {
    setActiveStore(null);
    setScreen("storeSelect");
  };

  if(!fbReady) return null; // wait for Firebase auth state

  // Cloud connect overlay
  if(showCloud) return <CloudLoginScreen
    noSkip={firstSetup}
    onConnected={async ()=>{
      if (firstSetup) {
        // First-time setup: pull cloud users/stores → save locally → mark setup done → auto-logout
        const [fbUsers, fbStores] = await Promise.all([loadUsers(), loadStores()]);
        if (fbUsers && fbUsers.length) saveAuth(fbStores || [], fbUsers);
        else if (fbStores && fbStores.length) saveAuth(fbStores, []);
        localStorage.setItem("nethran_setup_done", "1");
        setFirstSetup(false);
        setShowCloud(false);
        setSession(null);
        try { sessionStorage.removeItem("nethran_session"); } catch(e) {}
        setScreen("login");
      } else {
        const { stores, users } = loadAuth();
        stores.forEach(s => syncStore(s));
        users.forEach(u => syncUser(u));
        setShowCloud(false);
      }
    }}
    onSkip={()=>{ if(firstSetup){ setFirstSetup(false); localStorage.setItem("nethran_setup_done","1"); } setShowCloud(false); setScreen("login"); }}
  />;

  if (screen === "login") return <LoginScreen onLogin={handleLogin}/>;
  if (screen === "storeSelect") return <StoreSelector currentUser={session} onSelectStore={handleSelectStore} onManageUsers={() => setScreen("userManager")} onLogout={handleLogout} onConnectCloud={()=>setShowCloud(true)} fbUser={fbUser}/>;
  if (screen === "userManager") return <UserManager onBack={() => setScreen("storeSelect")}/>;
  if (screen === "store" && activeStore && session) {
    return <NethranApp
      storeId={activeStore.id}
      storeName={activeStore.name}
      currentUser={session}
      onLogout={handleLogout}
      onSwitchStore={handleSwitchStore}
      onManageUsers={()=>setScreen("userManager")}
      allStores={loadAuth().stores}
      fbUser={fbUser}
      onConnectCloud={()=>setShowCloud(true)}
    />;
  }
  return <LoginScreen onLogin={handleLogin}/>;
}